import React, { useState, useEffect, useMemo, useRef } from 'react';
import mermaid from 'mermaid';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  FileText, 
  Download, 
  Upload, 
  Printer, 
  Users, 
  Phone, 
  MapPin, 
  AlertTriangle, 
  MessageSquare, 
  Heart, 
  Home, 
  PawPrint, 
  Folder, 
  DollarSign, 
  GraduationCap,
  Briefcase,
  User,
  Trophy,
  TrendingUp,
  CheckCircle,
  Upload as UploadIcon,
  Sparkles,
  Plus,
  X,
  Edit,
  ArrowRight,
  ArrowLeft,
  Wand2,
  ChevronRight,
  ChevronUp,
  ChevronDown,
  Bell,
  Calendar,
  Car,
  Clock,
  ListChecks,
  Map,
  PenTool,
  Type,
  Undo2,
  Trash2,
  ImageIcon,
  Save
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import ErrorBoundary from '../components/ErrorBoundary';
import ToolLayout from '../components/ToolLayout';
import { generateSectionPrintContent } from '../lib/binderSections/emergencyPlan';
import { MapContainer, TileLayer, Marker, Polyline, useMapEvents, useMap, Popup } from 'react-leaflet';
import L from 'leaflet';

// Fix for default marker icons in Leaflet with webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const STORAGE_KEY = 'emergency_plan_data';

// Comprehensive map of all 45 emergency types from Location Risk Assessment
const EMERGENCY_TYPES_MAP = {
  // Natural Disasters - Weather
  tornado: { label: 'Tornado', icon: '🌪️', description: 'Tornadoes and tornado outbreaks', category: 'natural_weather' },
  hurricane: { label: 'Hurricane', icon: '🌀', description: 'Hurricanes and tropical storms', category: 'natural_weather' },
  flood: { label: 'Flood', icon: '🌊', description: 'Flooding from rain, storm surge, or river overflow', category: 'natural_weather' },
  flash_flood: { label: 'Flash Flood', icon: '💧', description: 'Rapid-onset flooding', category: 'natural_weather' },
  severe_thunderstorm: { label: 'Severe Thunderstorm', icon: '⛈️', description: 'Severe thunderstorms with hail, wind, lightning', category: 'natural_weather' },
  hail: { label: 'Hail', icon: '🧊', description: 'Large hail events', category: 'natural_weather' },
  winter_storm: { label: 'Winter Storm', icon: '❄️', description: 'Winter storms, blizzards, ice storms', category: 'natural_weather' },
  ice_storm: { label: 'Ice Storm', icon: '🧊', description: 'Ice storms and freezing rain', category: 'natural_weather' },
  extreme_heat: { label: 'Extreme Heat', icon: '☀️', description: 'Extreme heat waves', category: 'natural_weather' },
  extreme_cold: { label: 'Extreme Cold', icon: '🥶', description: 'Extreme cold and frost', category: 'natural_weather' },
  drought: { label: 'Drought', icon: '🌵', description: 'Drought conditions', category: 'natural_weather' },
  severe_weather: { label: 'Severe Weather', icon: '🌩️', description: 'General severe weather', category: 'natural_weather' },
  
  // Natural Disasters - Geological
  earthquake: { label: 'Earthquake', icon: '🌍', description: 'Earthquakes and seismic activity', category: 'natural_geological' },
  tsunami: { label: 'Tsunami', icon: '🌊', description: 'Tsunamis', category: 'natural_geological' },
  volcano: { label: 'Volcano', icon: '🌋', description: 'Volcanic eruptions', category: 'natural_geological' },
  landslide: { label: 'Landslide', icon: '⛰️', description: 'Landslides and mudslides', category: 'natural_geological' },
  
  // Natural Disasters - Fire
  fire: { label: 'Fire', icon: '🔥', description: 'House fire, wildfire evacuation', category: 'fire' }, // Legacy key for backward compatibility
  wildfire: { label: 'Wildfire', icon: '🔥', description: 'Wildfires and forest fires', category: 'natural_fire' },
  home_fire: { label: 'Home Fire', icon: '🏠', description: 'Residential structure fires', category: 'fire' },
  structure_fire: { label: 'Structure Fire', icon: '🔥', description: 'Structure fires (commercial, residential)', category: 'fire' },
  
  // Human-Caused - Crime
  home_invasion: { label: 'Home Invasion', icon: '🚪', description: 'Home invasions and burglaries', category: 'crime' },
  violent_crime: { label: 'Violent Crime', icon: '⚠️', description: 'Violent crimes (assault, robbery, etc.)', category: 'crime' },
  property_crime: { label: 'Property Crime', icon: '🏚️', description: 'Property crimes (theft, vandalism, etc.)', category: 'crime' },
  civil_unrest: { label: 'Civil Unrest', icon: '🚨', description: 'Civil unrest, riots, protests', category: 'crime' },
  
  // Human-Caused - Social/Economic
  economic_decline: { label: 'Economic Decline', icon: '📉', description: 'Economic decline, population loss', category: 'economic' },
  social_instability: { label: 'Social Instability', icon: '⚖️', description: 'Social instability, low community resilience', category: 'social' },
  
  // Human-Caused - Industrial
  industrial_disaster: { label: 'Industrial Disaster', icon: '🏭', description: 'Industrial accidents and disasters', category: 'industrial' },
  chemical_spill: { label: 'Chemical Spill', icon: '☢️', description: 'Chemical spills and hazardous material releases', category: 'industrial' },
  nuclear_incident: { label: 'Nuclear Incident', icon: '☢️', description: 'Nuclear incidents and radiation releases', category: 'industrial' },
  
  // Infrastructure
  power_outage: { label: 'Power Outage', icon: '⚡', description: 'Power outages and electrical failures', category: 'infrastructure' },
  water_contamination: { label: 'Water Contamination', icon: '💧', description: 'Water contamination and supply issues', category: 'infrastructure' },
  gas_leak: { label: 'Gas Leak', icon: '💨', description: 'Natural gas leaks and explosions', category: 'infrastructure' },
  communication_failure: { label: 'Communication Failure', icon: '📡', description: 'Communication system failures', category: 'infrastructure' },
  transportation_accident: { label: 'Transportation Accident', icon: '🚗', description: 'Major transportation accidents', category: 'transportation' },
  building_collapse: { label: 'Building Collapse', icon: '🏢', description: 'Building collapses and structural failures', category: 'infrastructure' },
  
  // Medical/Health
  pandemic: { label: 'Pandemic', icon: '🦠', description: 'Pandemics and disease outbreaks', category: 'health' },
  medical_emergency: { label: 'Medical Emergency', icon: '🏥', description: 'Medical emergencies', category: 'health' },
  
  // Environmental Health
  radon_exposure: { label: 'Radon Exposure', icon: '☢️', description: 'Radon gas exposure risks', category: 'health' },
  air_quality: { label: 'Air Quality', icon: '🌫️', description: 'Poor air quality and pollution events', category: 'health' },
  
  // Security
  terrorism: { label: 'Terrorism', icon: '🚨', description: 'Terrorist attacks and threats', category: 'security' },
  active_shooter: { label: 'Active Shooter', icon: '🔫', description: 'Active shooter incidents', category: 'security' },
  cyber_attack: { label: 'Cyber Attack', icon: '💻', description: 'Cyber attacks and data breaches', category: 'security' },
  
  // Astronomical
  solar_flare: { label: 'Solar Flare', icon: '☀️', description: 'Solar flares and space weather events', category: 'astronomical' },
  geomagnetic_storm: { label: 'Geomagnetic Storm', icon: '🌌', description: 'Geomagnetic storms affecting power grids', category: 'astronomical' },
  meteor_impact: { label: 'Meteor Impact', icon: '☄️', description: 'Meteor and asteroid impacts', category: 'astronomical' },
  
  // Other
  other: { label: 'Other', icon: '❓', description: 'Other or unspecified emergencies', category: 'other' }
};

// Initial plan data structure
const initialPlanData = {
  version: '1.0',
  created_at: new Date().toISOString(),
  updated_at: new Date().toISOString(),
  family_info: {
    members: [] // Each member will have: name, age, role, special_needs, allergies: [], medications: [], doctors: [], conditions: [], insurance: {}
  },
  emergency_contacts: {
    contacts: []
  },
  evacuation_plans: {
    plans: [] // Each plan: { id, name, primary_route, secondary_route, meeting_points: { primary, secondary, tertiary }, alternative_locations: [], notes, map_image: null }
  },
  disaster_plans: {
    // Start with common ones, but allow dynamic addition from all 45 emergency types
    fire: { completed: false, steps: [] },
    flood: { completed: false, steps: [] },
    tornado: { completed: false, steps: [] },
    earthquake: { completed: false, steps: [] },
    hurricane: { completed: false, steps: [] },
    winter_storm: { completed: false, steps: [] },
    power_outage: { completed: false, steps: [] }
    // Users can add any of the 45 emergency types via the "Add Disaster Plan" button
  },
  communication_plan: {
    out_of_area_contacts: [
      {
        id: 'primary',
        name: '',
        phone: '',
        email: '',
        address: '',
        relationship: '',
        notes: '',
        is_primary: true
      }
    ],
    check_in_procedures: '',
    communication_tree: [],
    communication_methods: {
      methods: [], // Array of { id, platform, endpoint, protocol, priority }
      radio_channels: [] // Keep radio channels separate as they're more detailed
    },
    text_templates: [
      {
        id: 'safe',
        name: 'Safe Check-In',
        message: 'I am safe. Location: [LOCATION]. Status: [STATUS]'
      },
      {
        id: 'need_help',
        name: 'Need Help',
        message: 'I need help. Location: [LOCATION]. Situation: [SITUATION]'
      },
      {
        id: 'evacuating',
        name: 'Evacuating',
        message: 'Evacuating to [LOCATION]. ETA: [TIME]. Will update when safe.'
      }
    ],
    neighbor_network: [],
    emergency_notifications: {
      enable_amber_alerts: true,
      enable_weather_alerts: true,
      enable_local_alerts: true,
      notification_preferences: [] // ['phone', 'text', 'email', 'app']
    },
    communication_protocols: {
      during_power_outage: '',
      during_internet_outage: '',
      during_phone_outage: '',
      meeting_point_procedures: '',
      school_work_procedures: ''
    },
    social_media_checkin: {
      platform: '',
      hashtag: '',
      group_name: '',
      procedures: ''
    },
    status_codes: [], // User-defined status codes: { id, code, meaning, color }
    rally_points: [
      { id: 'home', name: 'Home', location: '', coordinates: '', notes: '' },
      { id: 'neighborhood', name: 'Neighborhood Safe Spot', location: '', coordinates: '', notes: '' },
      { id: 'out_of_area', name: 'Out-of-Area Meeting Point', location: '', coordinates: '', notes: '' }
    ],
    check_in_schedule: {
      normal: { interval: '24', unit: 'hours' },
      emergency: { interval: '2', unit: 'hours' },
      extreme: { interval: '30', unit: 'minutes' }
    }
  },
  medical_info: {
    allergies: [],
    medications: [],
    doctors: [],
    conditions: [],
    insurance: {
      provider: '',
      policy_number: '',
      group_number: '',
      dental_provider: '',
      dental_policy_number: '',
      dental_group_number: '',
      vision_provider: '',
      vision_policy_number: '',
      vision_group_number: ''
    },
    advance_directive: { has: false, location: '' },
    medical_power_of_attorney: { has: false, location: '' },
    immunization_records_location: '',
    significant_medical_history: ''
  },
  home_info: {
    address: {
      street: '',
      city: '',
      state: '',
      zip: ''
    },
    utility_shutoffs: {
      gas: '',
      water: '',
      electricity: ''
    },
    utility_photos: {
      gas: { imageData: null, caption: '' },
      water: { imageData: null, caption: '' },
      electricity: { imageData: null, caption: '' }
    },
    insurance: {
      provider: '',
      policy_number: '',
      agent_contact: ''
    },
    layout_notes: '',
    home_warranty: { company: '', contract_number: '', expiry: '' }
  },
  pet_plans: {
    pets: [],
    evacuation_procedures: '',
    vet_contacts: []
  },
  documents: {
    checklist: [],
    storage_locations: {}
  },
  grab_and_go: [], // Array of { id, name, priority, checked, kit_id? }
  vehicle_loading_plan: { vehicles: [] }, // vehicles: [{ id, name, type: 'vehicle'|'trailer', notes?, zones: [{ id, name, items: [{ id, name, notes }] }] }]
  go_no_go_criteria: { intro: '', criteria: [] }, // criteria: [{ id, trigger, when_met, notes?, reevaluate_period?, also_reassess? }]
  first_hour_protocol: { steps: [] }, // steps: [{ id, text }] — first 60 minutes in an emergency
  kits: {
    imported: false,
    kits: [] // Array of { id, name, location, notes, contents: [{ id, name, category, quantity, unit, expiration_date, notes }] }
  },
  financial_info: {
    bank_accounts: [],
    insurance_policies: [],
    important_accounts: []
  },
  /** Binder-safe financial summary (no account numbers). For printed binder only. */
  financial_summary: {
    emergency_fund_target: '',
    emergency_fund_current: '',
    timeline_years: '',
    insurance: [], // [{ type: 'Home', company: 'Acme' }]
    debt_accounts_count: '',
    payoff_strategy: ''
  },
  valuables: {
    items: [] // { id, name, category, estimated_value, location_note }
  },
  school_work_plans: {
    school_pickup: {},
    work_contacts: [],
    reunification_plans: []
  },
  gamification: {
    points: 0,
    level: 'beginner',
    streak_days: 0,
    last_review_date: null,
    achievements: [],
    completion_stats: {}
  },
  import_sources: {
    emergency_contacts: null,
    inventory: null,
    budget: null,
    debt: null
  }
};

// Mermaid Morse Code Tree Component
function MorseCodeTreeDiagram() {
  const mermaidRef = useRef(null);
  const [mermaidInitialized, setMermaidInitialized] = useState(false);
  const diagramIdRef = useRef(`morse-tree-${Date.now()}`);

  useEffect(() => {
    if (!mermaidInitialized) {
      mermaid.initialize({ 
        startOnLoad: false,
        theme: 'dark',
        flowchart: {
          useMaxWidth: true,
          htmlLabels: true,
          curve: 'basis',
          nodeSpacing: 80,
          rankSpacing: 100
        },
        securityLevel: 'loose'
      });
      setMermaidInitialized(true);
    }
  }, [mermaidInitialized]);

  useEffect(() => {
    if (mermaidInitialized && mermaidRef.current) {
      const mermaidDiagram = `
flowchart TD
    START["START"]
    
    START -->|"DAH (-)"| T["T"]
    START -->|"DIT (•)"| E["E"]
    
    T -->|"DAH (-)"| M["M"]
    T -->|"DIT (•)"| N["N"]
    E -->|"DAH (-)"| A["A"]
    E -->|"DIT (•)"| I["I"]
    
    M -->|"DAH (-)"| O["O"]
    M -->|"DIT (•)"| G["G"]
    N -->|"DAH (-)"| K["K"]
    N -->|"DIT (•)"| D["D"]
    A -->|"DAH (-)"| W["W"]
    A -->|"DIT (•)"| R["R"]
    I -->|"DAH (-)"| U["U"]
    I -->|"DIT (•)"| S["S"]
    
    O -->|"DAH (-)"| ZERO["0"]
    O -->|"DIT (•)"| NINE["9"]
    M -->|"DAH (-)"| Z["Z"]
    G -->|"DIT (•)"| Q["Q"]
    K -->|"DAH (-)"| Y["Y"]
    K -->|"DIT (•)"| C["C"]
    D -->|"DAH (-)"| X["X"]
    D -->|"DIT (•)"| B["B"]
    W -->|"DAH (-)"| J["J"]
    W -->|"DIT (•)"| P["P"]
    R -->|"DIT (•)"| L["L"]
    U -->|"DAH (-)"| F["F"]
    S -->|"DAH (-)"| V["V"]
    S -->|"DIT (•)"| H["H"]
    
    B -->|"DAH (-)"| SIX["6"]
    B -->|"DIT (•)"| ONE["1"]
    L -->|"DIT (•)"| TWO["2"]
    F -->|"DIT (•)"| THREE["3"]
    V -->|"DIT (•)"| FOUR["4"]
    H -->|"DIT (•)"| FIVE["5"]
    Q -->|"DIT (•)"| SEVEN["7"]
    Z -->|"DIT (•)"| EIGHT["8"]
    
    style START fill:#6d28d9,stroke:#7c3aed,stroke-width:3px,color:#fff,font-size:20px
    style T fill:#374151,stroke:#4b5563,stroke-width:2px,color:#fff,font-size:18px
    style E fill:#374151,stroke:#4b5563,stroke-width:2px,color:#fff,font-size:18px
    style M fill:#1e3a5f,stroke:#2563eb,stroke-width:2px,color:#fff,font-size:18px
    style N fill:#1e3a5f,stroke:#2563eb,stroke-width:2px,color:#fff,font-size:18px
    style A fill:#1e3a5f,stroke:#2563eb,stroke-width:2px,color:#fff,font-size:18px
    style I fill:#1e3a5f,stroke:#2563eb,stroke-width:2px,color:#fff,font-size:18px
    style O fill:#064e3b,stroke:#047857,stroke-width:2px,color:#fff,font-size:18px
    style G fill:#064e3b,stroke:#047857,stroke-width:2px,color:#fff,font-size:18px
    style K fill:#064e3b,stroke:#047857,stroke-width:2px,color:#fff,font-size:18px
    style D fill:#064e3b,stroke:#047857,stroke-width:2px,color:#fff,font-size:18px
    style W fill:#064e3b,stroke:#047857,stroke-width:2px,color:#fff,font-size:18px
    style R fill:#064e3b,stroke:#047857,stroke-width:2px,color:#fff,font-size:18px
    style U fill:#064e3b,stroke:#047857,stroke-width:2px,color:#fff,font-size:18px
    style S fill:#064e3b,stroke:#047857,stroke-width:2px,color:#fff,font-size:18px
    style Z fill:#374151,stroke:#4b5563,stroke-width:2px,color:#fff,font-size:18px
    style ZERO fill:#064e3b,stroke:#047857,stroke-width:2px,color:#fff,font-size:18px
    style NINE fill:#064e3b,stroke:#047857,stroke-width:2px,color:#fff,font-size:18px
    style Q fill:#064e3b,stroke:#047857,stroke-width:2px,color:#fff,font-size:18px
    style Y fill:#064e3b,stroke:#047857,stroke-width:2px,color:#fff,font-size:18px
    style C fill:#064e3b,stroke:#047857,stroke-width:2px,color:#fff,font-size:18px
    style X fill:#064e3b,stroke:#047857,stroke-width:2px,color:#fff,font-size:18px
    style B fill:#064e3b,stroke:#047857,stroke-width:2px,color:#fff,font-size:18px
    style J fill:#064e3b,stroke:#047857,stroke-width:2px,color:#fff,font-size:18px
    style P fill:#064e3b,stroke:#047857,stroke-width:2px,color:#fff,font-size:18px
    style L fill:#064e3b,stroke:#047857,stroke-width:2px,color:#fff,font-size:18px
    style F fill:#064e3b,stroke:#047857,stroke-width:2px,color:#fff,font-size:18px
    style V fill:#064e3b,stroke:#047857,stroke-width:2px,color:#fff,font-size:18px
    style H fill:#064e3b,stroke:#047857,stroke-width:2px,color:#fff,font-size:18px
    style SIX fill:#064e3b,stroke:#047857,stroke-width:2px,color:#fff,font-size:18px
    style ONE fill:#064e3b,stroke:#047857,stroke-width:2px,color:#fff,font-size:18px
    style TWO fill:#064e3b,stroke:#047857,stroke-width:2px,color:#fff,font-size:18px
    style THREE fill:#064e3b,stroke:#047857,stroke-width:2px,color:#fff,font-size:18px
    style FOUR fill:#064e3b,stroke:#047857,stroke-width:2px,color:#fff,font-size:18px
    style FIVE fill:#064e3b,stroke:#047857,stroke-width:2px,color:#fff,font-size:18px
    style SEVEN fill:#064e3b,stroke:#047857,stroke-width:2px,color:#fff,font-size:18px
    style EIGHT fill:#064e3b,stroke:#047857,stroke-width:2px,color:#fff,font-size:18px
      `;

      mermaidRef.current.innerHTML = '';
      const id = diagramIdRef.current;
      
      mermaid.render(id, mermaidDiagram).then((result) => {
        mermaidRef.current.innerHTML = result.svg;
        const svgElement = mermaidRef.current.querySelector('svg');
        if (svgElement) {
          // Force diagram background to match dark card
          svgElement.style.backgroundColor = 'transparent';
          // All text: light color and readable size (edge labels are often tiny)
          const textElements = svgElement.querySelectorAll('text');
          const labelColor = '#e5e7eb';
          textElements.forEach(text => {
            const currentSize = parseFloat(text.getAttribute('font-size') || '12');
            const isEdgeLabel = text.closest('.edgeLabel') || (currentSize < 16);
            const size = isEdgeLabel ? 14 : Math.max(currentSize, 18);
            text.setAttribute('font-size', size);
            text.setAttribute('fill', labelColor);
            text.style.fontSize = `${size}px`;
            text.style.fontWeight = '600';
            text.style.fill = labelColor;
          });
          // Edge paths and lines: thick, bright stroke so connectors are visible on dark
          const connectorStroke = '#94a3b8';
          const strokeWidth = 2.5;
          ['path', 'line'].forEach(tag => {
            svgElement.querySelectorAll(tag).forEach(el => {
              el.style.stroke = connectorStroke;
              el.setAttribute('stroke', connectorStroke);
              el.style.strokeWidth = `${strokeWidth}px`;
              el.setAttribute('stroke-width', String(strokeWidth));
            });
          });
          // Target edge paths specifically (Mermaid wraps connectors in .edgePath)
          svgElement.querySelectorAll('.edgePath path, .edgePaths path, .marker path').forEach(el => {
            el.style.stroke = '#cbd5e1';
            el.setAttribute('stroke', '#cbd5e1');
            el.style.strokeWidth = '3px';
            el.setAttribute('stroke-width', '3');
          });
        }
      }).catch((error) => {
        console.error('Mermaid rendering error:', error);
        mermaidRef.current.innerHTML = '<p className="text-destructive">Error rendering diagram. Please refresh the page.</p>';
      });
    }
  }, [mermaidInitialized]);

  return (
    <div className="mermaid-container" style={{ minHeight: '600px', width: '100%', overflow: 'auto', padding: '20px' }}>
      <div ref={mermaidRef} className="mermaid"></div>
    </div>
  );
}

function EmergencyPlanTool() {
  const [planData, setPlanData] = useState(initialPlanData);
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [previousLevel, setPreviousLevel] = useState(null);
  const [printRef, setPrintRef] = useState(null);
  const [showCustomPlanDialog, setShowCustomPlanDialog] = useState(false);
  const [customPlanForm, setCustomPlanForm] = useState({ name: '', icon: '⚠️', description: '' });
  const [showImportModal, setShowImportModal] = useState(false);
  const [showMapEditor, setShowMapEditor] = useState(false);
  const [editingPlanId, setEditingPlanId] = useState(null);

  // Ensure evacuation_plans structure is always initialized
  useEffect(() => {
    if (!planData.evacuation_plans || !planData.evacuation_plans.plans) {
      setPlanData(prev => ({
        ...prev,
        evacuation_plans: {
          ...prev.evacuation_plans,
          plans: prev.evacuation_plans?.plans || []
        }
      }));
    }
  }, []);

  // Ensure communication_plan structure is always initialized (run once on mount)
  useEffect(() => {
    if (!planData.communication_plan) {
      setPlanData(prev => ({
        ...prev,
        communication_plan: {
          ...initialPlanData.communication_plan,
          ...(prev.communication_plan || {})
        }
      }));
    } else if (!planData.communication_plan.out_of_area_contacts || 
               !Array.isArray(planData.communication_plan.communication_tree) ||
               planData.communication_plan.check_in_procedures === undefined) {
      setPlanData(prev => ({
        ...prev,
        communication_plan: {
          ...initialPlanData.communication_plan,
          ...(prev.communication_plan || {}),
          out_of_area_contacts: prev.communication_plan?.out_of_area_contacts ?? initialPlanData.communication_plan.out_of_area_contacts,
          communication_tree: Array.isArray(prev.communication_plan?.communication_tree) ? prev.communication_plan.communication_tree : [],
          check_in_procedures: prev.communication_plan?.check_in_procedures ?? ''
        }
      }));
    }
  }, []); // Only run once on mount - intentionally empty deps

  // Load data from localStorage on mount
  useEffect(() => {
    loadPlanData();
  }, []);

  // Auto-save to localStorage when planData changes (debounced)
  useEffect(() => {
    const timer = setTimeout(() => {
      savePlanData();
    }, 500);

    return () => clearTimeout(timer);
  }, [planData]);

  // Calculate and award points based on section completions
  useEffect(() => {
    const calculatePoints = () => {
      let newPoints = 0;
      const newAchievements = [...(planData.gamification?.achievements || [])];
      const previousPoints = planData.gamification?.points || 0;

      // Calculate completion stats inline to avoid dependency issues
      const sections = [
        { key: 'family_info', check: () => (planData.family_info?.members?.length ?? 0) > 0 },
        { key: 'evacuation_plans', check: () => (planData.evacuation_plans?.plans?.length ?? 0) > 0 },
        { key: 'disaster_plans', check: () => Object.values(planData.disaster_plans || {}).some(p => p?.completed) },
        { key: 'communication_plan', check: () => (planData.communication_plan?.out_of_area_contacts?.some(c => c?.name) || false) },
        { key: 'medical_info', check: () => (planData.medical_info?.allergies?.length ?? 0) > 0 || (planData.medical_info?.medications?.length ?? 0) > 0 },
        { key: 'home_info', check: () => !!planData.home_info?.address?.street },
        { key: 'pet_plans', check: () => (planData.pet_plans?.pets?.length ?? 0) > 0 },
        { key: 'grab_and_go', check: () => (planData.grab_and_go?.length ?? 0) > 0 },
        { key: 'school_work_plans', check: () => Object.keys(planData.school_work_plans?.school_pickup || {}).length > 0 }
      ];
      const completedSections = sections.filter(s => s.check()).length;
      const totalSections = sections.length;
      const completionPercentage = Math.round((completedSections / totalSections) * 100);

      // Points for section completions
      if ((planData.family_info?.members?.length ?? 0) > 0) newPoints += 10;
      if ((planData.evacuation_plans?.plans?.length ?? 0) > 0) newPoints += 20;
      if (Object.values(planData.disaster_plans || {}).some(p => p?.completed)) newPoints += 25;
      if (planData.communication_plan?.out_of_area_contacts?.some(c => c?.name)) newPoints += 15;
      if ((planData.family_info?.members ?? []).some(m => ((m?.allergies?.length ?? 0) > 0) || ((m?.medications?.length ?? 0) > 0))) newPoints += 15;
      if (planData.home_info?.address?.street) newPoints += 10;
      if ((planData.pet_plans?.pets?.length ?? 0) > 0) newPoints += 10;
      if ((planData.grab_and_go?.length ?? 0) > 0) newPoints += 10;
      if (Object.keys(planData.school_work_plans?.school_pickup || {}).length > 0) newPoints += 10;

      // Bonus points for multiple disaster plans
      const completedDisasters = Object.values(planData.disaster_plans || {}).filter(p => p?.completed).length;
      if (completedDisasters >= 3) newPoints += 10;
      if (completedDisasters >= 5) newPoints += 15;
      if (completedDisasters >= 7) newPoints += 20;

      // Achievement checks
      const achievementChecks = [
        { id: 'home_base', name: 'Home Base', icon: '🏠', condition: () => (planData.family_info?.members?.length ?? 0) > 0 },
        { id: 'navigator', name: 'Navigator', icon: '🗺️', condition: () => (planData.evacuation_plans?.plans?.length ?? 0) > 0 && (planData.evacuation_plans?.plans ?? []).some(p => p?.primary_route && p?.meeting_points?.primary) },
        { id: 'fire_fighter', name: 'Fire Fighter', icon: '🔥', condition: () => planData.disaster_plans?.fire?.completed },
        { id: 'storm_chaser', name: 'Storm Chaser', icon: '💨', condition: () => 
          planData.disaster_plans?.tornado?.completed || planData.disaster_plans?.hurricane?.completed || planData.disaster_plans?.flood?.completed },
        { id: 'pet_protector', name: 'Pet Protector', icon: '🐾', condition: () => (planData.pet_plans?.pets?.length ?? 0) > 0 },
        { id: 'plan_master', name: 'Plan Master', icon: '⭐', condition: () => completionPercentage >= 100 }
      ];

      achievementChecks.forEach(achievement => {
        if (achievement.condition() && !newAchievements.find(a => a.id === achievement.id)) {
          newAchievements.push({
            id: achievement.id,
            name: achievement.name,
            icon: achievement.icon,
            earned_at: new Date().toISOString()
          });
          toast.success(`Achievement Unlocked: ${achievement.icon} ${achievement.name}!`);
        }
      });

      // Calculate streak (only update on review, not automatically)
      const currentStreak = planData.gamification?.streak_days || 0;

      // Update gamification data if points or achievements changed
      if (newPoints !== previousPoints || newAchievements.length !== (planData.gamification?.achievements?.length || 0)) {
        setPlanData(prev => ({
          ...prev,
          gamification: {
            ...(prev.gamification || {}),
            points: newPoints,
            achievements: newAchievements,
            completion_stats: {
              sections_completed: completedSections,
              total_sections: totalSections,
              percentage: completionPercentage
            }
          }
        }));
      }
    };

    calculatePoints();
  },   [
    planData.family_info?.members?.length ?? 0,
    planData.evacuation_plans?.plans?.length ?? 0,
    JSON.stringify(planData.disaster_plans || {}),
    planData.communication_plan?.out_of_area_contacts?.some(c => c?.name) ? 'yes' : 'no',
    (planData.family_info?.members ?? []).reduce((sum, m) => sum + ((m?.allergies?.length ?? 0)), 0),
    (planData.family_info?.members ?? []).reduce((sum, m) => sum + ((m?.medications?.length ?? 0)), 0),
    planData.home_info?.address?.street || '',
    planData.pet_plans?.pets?.length ?? 0,
    planData.grab_and_go?.length ?? 0,
    Object.keys(planData.school_work_plans?.school_pickup || {}).length
  ]);

  const loadPlanData = () => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        
        // Migrate old evacuation_plans structure to new plans array structure
        if (parsed.evacuation_plans && !parsed.evacuation_plans.plans) {
          // Old structure detected - migrate it
          const oldPlan = parsed.evacuation_plans;
          if (oldPlan.primary_route || oldPlan.secondary_route || oldPlan.meeting_points?.home || oldPlan.alternative_locations?.length > 0) {
            parsed.evacuation_plans = {
              plans: [{
                id: `evac_plan_${Date.now()}`,
                name: 'Evacuation Plan',
                primary_route: oldPlan.primary_route || '',
                secondary_route: oldPlan.secondary_route || '',
                meeting_points: oldPlan.meeting_points ? {
                  primary: oldPlan.meeting_points.home || '',
                  secondary: oldPlan.meeting_points.neighborhood || '',
                  tertiary: oldPlan.meeting_points.out_of_area || ''
                } : { primary: '', secondary: '', tertiary: '' },
                alternative_locations: oldPlan.alternative_locations || [],
                notes: '',
                map_image: null
              }]
            };
          } else {
            parsed.evacuation_plans = { plans: [] };
          }
        }
        
        // Migrate existing plans with old meeting_points structure (home/neighborhood/out_of_area) to new structure
        if (parsed.evacuation_plans?.plans) {
          parsed.evacuation_plans.plans = parsed.evacuation_plans.plans.map(plan => {
            if (plan.meeting_points && (plan.meeting_points.home !== undefined || plan.meeting_points.neighborhood !== undefined || plan.meeting_points.out_of_area !== undefined)) {
              // Old structure detected - migrate it
              return {
                ...plan,
                meeting_points: {
                  primary: plan.meeting_points.home || '',
                  secondary: plan.meeting_points.neighborhood || '',
                  tertiary: plan.meeting_points.out_of_area || ''
                }
              };
            }
            // Ensure meeting_points structure exists even if not migrating
            if (!plan.meeting_points) {
              return {
                ...plan,
                meeting_points: { primary: '', secondary: '', tertiary: '' },
                map_image: plan.map_image || null
              };
            }
            // Ensure map_image field exists
            return {
              ...plan,
              map_image: plan.map_image || null
            };
          });
        }
        
        // Ensure evacuation_plans structure is correct
        if (!parsed.evacuation_plans || !parsed.evacuation_plans.plans) {
          parsed.evacuation_plans = { plans: [] };
        }
        
        // Migrate grab_and_go from documents to top level
        if (parsed.documents?.grab_and_go && !parsed.grab_and_go) {
          parsed.grab_and_go = parsed.documents.grab_and_go;
          delete parsed.documents.grab_and_go;
        }
        // Ensure grab_and_go exists at top level
        if (!parsed.grab_and_go) {
          parsed.grab_and_go = [];
        }
        // Ensure vehicle_loading_plan exists; migrate old zones-only to single vehicle
        if (!parsed.vehicle_loading_plan) {
          parsed.vehicle_loading_plan = { vehicles: [] };
        }
        if (!Array.isArray(parsed.vehicle_loading_plan.vehicles)) {
          const legacyZones = parsed.vehicle_loading_plan.zones ?? [];
          parsed.vehicle_loading_plan = {
            vehicles: legacyZones.length > 0
              ? [{ id: `vehicle_${Date.now()}`, name: 'Primary vehicle', type: 'vehicle', zones: legacyZones }]
              : []
          };
        }
        if (!parsed.go_no_go_criteria || !Array.isArray(parsed.go_no_go_criteria.criteria)) {
          parsed.go_no_go_criteria = { intro: parsed.go_no_go_criteria?.intro ?? '', criteria: parsed.go_no_go_criteria?.criteria ?? [] };
        }
        parsed.go_no_go_criteria.criteria = (parsed.go_no_go_criteria.criteria || []).map(c => ({
          ...c,
          reevaluate_period: c.reevaluate_period === undefined ? '' : c.reevaluate_period,
          severity: c.severity === undefined ? '' : c.severity,
          cascading_considerations: c.cascading_considerations === undefined ? '' : c.cascading_considerations,
          also_reassess: c.also_reassess === undefined ? '' : c.also_reassess
        }));
        // Ensure all grab_and_go items have checked field
        if (parsed.grab_and_go && Array.isArray(parsed.grab_and_go)) {
          parsed.grab_and_go = parsed.grab_and_go.map(item => ({
            ...item,
            checked: item.checked !== undefined ? item.checked : false
          }));
        }
        if (!parsed.first_hour_protocol || !Array.isArray(parsed.first_hour_protocol.steps)) {
          parsed.first_hour_protocol = { steps: parsed.first_hour_protocol?.steps ?? [] };
        }
        parsed.first_hour_protocol.steps = (parsed.first_hour_protocol.steps || []).map(s => (s && typeof s === 'object' && s.id ? s : { id: `step_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`, text: typeof s === 'string' ? s : (s?.text ?? '') }));

        // Migrate old supplies structure to new kits structure
        if (parsed.supplies && !parsed.kits) {
          // Convert supplies.items to kits structure
          const oldItems = parsed.supplies.items || [];
          if (oldItems.length > 0) {
            // Group items by location if available, otherwise create a single "Emergency Supplies" kit
            const itemsByLocation = {};
            oldItems.forEach(item => {
              const location = item.location || 'General';
              if (!itemsByLocation[location]) {
                itemsByLocation[location] = [];
              }
              itemsByLocation[location].push({
                id: item.id || `item_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                name: item.name || '',
                category: item.category || '',
                quantity: item.quantity || '',
                unit: item.unit || '',
                expiration_date: item.expiration_date || '',
                notes: item.notes || ''
              });
            });
            
            // Create kits from grouped items
            const migratedKits = Object.entries(itemsByLocation).map(([location, contents]) => ({
              id: `kit_migrated_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
              name: location === 'General' ? 'Emergency Supplies' : `${location} Kit`,
              location: location === 'General' ? '' : location,
              notes: 'Migrated from supplies',
              contents: contents,
              imported: parsed.supplies.imported || false
            }));
            
            parsed.kits = {
              imported: parsed.supplies.imported || false,
              kits: migratedKits
            };
          } else {
            parsed.kits = {
              imported: false,
              kits: []
            };
          }
          // Remove old supplies structure
          delete parsed.supplies;
        }
        // Ensure kits structure exists
        if (!parsed.kits) {
          parsed.kits = {
            imported: false,
            kits: []
          };
        }

        // Migrate old communication_plan structure to new structure
        if (parsed.communication_plan) {
          // Migrate out_of_area_contact (singular) to out_of_area_contacts (array)
          if (parsed.communication_plan.out_of_area_contact && !parsed.communication_plan.out_of_area_contacts) {
            parsed.communication_plan.out_of_area_contacts = [{
              id: 'primary',
              name: parsed.communication_plan.out_of_area_contact.name || '',
              phone: parsed.communication_plan.out_of_area_contact.phone || '',
              email: parsed.communication_plan.out_of_area_contact.email || '',
              address: '',
              relationship: '',
              notes: '',
              is_primary: true
            }];
            delete parsed.communication_plan.out_of_area_contact;
          }
          
          // Ensure new fields exist with defaults
          if (!parsed.communication_plan.out_of_area_contacts) {
            parsed.communication_plan.out_of_area_contacts = [{
              id: 'primary',
              name: '',
              phone: '',
              email: '',
              address: '',
              relationship: '',
              notes: '',
              is_primary: true
            }];
          }
          if (!parsed.communication_plan.communication_methods) {
            parsed.communication_plan.communication_methods = {
              preferred_methods: [],
              backup_methods: [],
              text_enabled: true,
              social_media_platforms: [],
              radio_channels: []
            };
          }
          if (!parsed.communication_plan.text_templates) {
            parsed.communication_plan.text_templates = [
              {
                id: 'safe',
                name: 'Safe Check-In',
                message: 'I am safe. Location: [LOCATION]. Status: [STATUS]'
              },
              {
                id: 'need_help',
                name: 'Need Help',
                message: 'I need help. Location: [LOCATION]. Situation: [SITUATION]'
              },
              {
                id: 'evacuating',
                name: 'Evacuating',
                message: 'Evacuating to [LOCATION]. ETA: [TIME]. Will update when safe.'
              }
            ];
          }
          if (!parsed.communication_plan.neighbor_network) {
            parsed.communication_plan.neighbor_network = [];
          }
          if (!parsed.communication_plan.emergency_notifications) {
            parsed.communication_plan.emergency_notifications = {
              enable_amber_alerts: true,
              enable_weather_alerts: true,
              enable_local_alerts: true,
              notification_preferences: []
            };
          }
          if (!parsed.communication_plan.communication_protocols) {
            parsed.communication_plan.communication_protocols = {
              during_power_outage: '',
              during_internet_outage: '',
              during_phone_outage: '',
              meeting_point_procedures: '',
              school_work_procedures: ''
            };
          }
          // Ensure status_codes exist and have IDs
          if (!parsed.communication_plan.status_codes) {
            parsed.communication_plan.status_codes = [];
          } else {
            // Add IDs to status codes that don't have them
            parsed.communication_plan.status_codes = parsed.communication_plan.status_codes.map((code, index) => {
              if (!code.id) {
                return { ...code, id: `status_${Date.now()}_${index}` };
              }
              return code;
            });
          }
          if (!parsed.communication_plan.social_media_checkin) {
            parsed.communication_plan.social_media_checkin = {
              platform: '',
              hashtag: '',
              group_name: '',
              procedures: ''
            };
          }
        }

        // Migrate pets from DocumentsTool if this plan has no pets yet
        if ((!parsed.pet_plans?.pets || parsed.pet_plans.pets.length === 0)) {
          try {
            const docPetsRaw = localStorage.getItem('document_audit_pets');
            if (docPetsRaw) {
              const docPets = JSON.parse(docPetsRaw);
              if (Array.isArray(docPets) && docPets.length > 0) {
                const migrated = docPets.map(p => ({
                  id: p.id || `pet_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
                  name: p.name || '',
                  type: '',
                  breed: '',
                  age: '',
                  special_needs: '',
                  microchip_id: p.microchip || '',
                  imageData: p.imageData || null,
                  vetName: p.vetName || '',
                  vetPhone: p.vetPhone || ''
                }));
                if (!parsed.pet_plans) parsed.pet_plans = { pets: [], evacuation_procedures: '', vet_contacts: [] };
                parsed.pet_plans.pets = migrated;
              }
            }
          } catch (_) { /* ignore migration errors */ }
        }

        // Merge with initial structure to ensure all fields exist
        setPlanData({
          ...initialPlanData,
          ...parsed,
          evacuation_plans: {
            ...initialPlanData.evacuation_plans,
            ...parsed.evacuation_plans,
            plans: parsed.evacuation_plans.plans || []
          },
          updated_at: new Date().toISOString()
        });
      }
    } catch (error) {
      console.error('Error loading plan data:', error);
      toast.error('Failed to load plan data');
    } finally {
      setLoading(false);
    }
  };

  const savePlanData = () => {
    try {
      const dataToSave = {
        ...planData,
        updated_at: new Date().toISOString()
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(dataToSave));
    } catch (error) {
      console.error('Error saving plan data:', error);
      toast.error('Failed to save plan data');
    }
  };


  // Handle custom plan creation
  const handleCreateCustomPlan = () => {
    if (!customPlanForm.name.trim()) {
      toast.error('Please enter a plan name');
      return;
    }
    
    const customKey = `custom_${Date.now()}`;
    setPlanData(prev => ({
      ...prev,
      disaster_plans: {
        ...prev.disaster_plans,
        [customKey]: {
          completed: false,
          steps: [],
          custom: true,
          label: customPlanForm.name.trim(),
          icon: customPlanForm.icon.trim() || '⚠️',
          description: customPlanForm.description.trim() || 'Custom emergency plan'
        }
      }
    }));
    toast.success(`Created custom plan: ${customPlanForm.name.trim()}`);
    setShowCustomPlanDialog(false);
    setCustomPlanForm({ name: '', icon: '⚠️', description: '' });
  };

  // Calculate completion percentage
  const completionStats = useMemo(() => {
    const sections = [
      { key: 'family_info', name: 'Family Information', check: () => (planData.family_info?.members?.length ?? 0) > 0 },
      { key: 'evacuation_plans', name: 'Evacuation Plans', check: () => planData.evacuation_plans?.plans?.length > 0 },
      { key: 'disaster_plans', name: 'Disaster Plans', check: () => Object.values(planData.disaster_plans || {}).some(p => p?.completed) },
      { key: 'communication_plan', name: 'Communication Plan', check: () => (planData.communication_plan?.out_of_area_contacts?.some(c => c.name) || false) },
      { key: 'home_info', name: 'Home Information', check: () => !!planData.home_info?.address?.street },
      { key: 'pet_plans', name: 'Pet Plans', check: () => (planData.pet_plans?.pets?.length ?? 0) > 0 },
      { key: 'vehicle_loading_plan', name: 'Vehicle Loading Plan', check: () => (planData.vehicle_loading_plan?.vehicles?.length ?? 0) > 0 },
      { key: 'go_no_go_criteria', name: 'Go/No-Go Criteria', check: () => (planData.go_no_go_criteria?.criteria?.length ?? 0) > 0 },
      { key: 'first_hour_protocol', name: 'First Hour Protocol', check: () => (planData.first_hour_protocol?.steps?.length ?? 0) > 0 },
      { key: 'school_work_plans', name: 'School/Work Plans', check: () => Object.keys(planData.school_work_plans?.school_pickup || {}).length > 0 }
    ];

    const completed = sections.filter(s => s.check()).length;
    const total = sections.length;
    const percentage = Math.round((completed / total) * 100);

    return {
      completed,
      total,
      percentage,
      sections: sections.map(s => ({
        ...s,
        isComplete: s.check()
      }))
    };
  }, [planData]);

  // Calculate gamification level
  const gamificationLevel = useMemo(() => {
    const points = planData.gamification?.points || 0;
    if (points >= 1500) return { level: 'master', label: 'Master', color: 'purple', next: null, progress: 100 };
    if (points >= 501) return { level: 'expert', label: 'Expert', color: 'blue', next: 1500, progress: Math.round(((points - 501) / (1500 - 501)) * 100) };
    if (points >= 101) return { level: 'prepared', label: 'Prepared', color: 'green', next: 501, progress: Math.round(((points - 101) / (501 - 101)) * 100) };
    return { level: 'beginner', label: 'Beginner', color: 'gray', next: 101, progress: Math.round((points / 101) * 100) };
  }, [planData.gamification.points]);

  // Calculate review reminder status
  const reviewReminder = useMemo(() => {
    const REVIEW_INTERVAL_DAYS = 90; // Recommended review every 90 days
    const WARNING_DAYS = 14; // Show warning 14 days before due
    
    // Check if reminder was dismissed today
    const dismissed = JSON.parse(localStorage.getItem('review_reminder_dismissed') || '{}');
    const today = new Date().toISOString().split('T')[0];
    if (dismissed[today]) {
      return null; // Don't show if dismissed today
    }
    
    const lastReview = planData.gamification.last_review_date;
    const createdDate = planData.created_at;
    
    if (!lastReview && !createdDate) {
      return null; // No data yet
    }

    const referenceDate = lastReview || createdDate;
    const daysSinceReview = Math.floor((new Date().getTime() - new Date(referenceDate).getTime()) / (1000 * 60 * 60 * 24));
    const daysUntilDue = REVIEW_INTERVAL_DAYS - daysSinceReview;
    
    if (daysSinceReview >= REVIEW_INTERVAL_DAYS) {
      return {
        status: 'overdue',
        days: daysSinceReview - REVIEW_INTERVAL_DAYS,
        message: `Your plan is ${daysSinceReview - REVIEW_INTERVAL_DAYS} days overdue for review`,
        urgency: 'high',
        color: 'red'
      };
    } else if (daysUntilDue <= WARNING_DAYS) {
      return {
        status: 'due_soon',
        days: daysUntilDue,
        message: `Your plan is due for review in ${daysUntilDue} days`,
        urgency: 'medium',
        color: 'orange'
      };
    } else {
      return {
        status: 'current',
        days: daysUntilDue,
        message: `Next review due in ${daysUntilDue} days`,
        urgency: 'low',
        color: 'green'
      };
    }
  }, [planData.gamification.last_review_date, planData.created_at]);

  // Track level changes for level-up notifications
  useEffect(() => {
    if (previousLevel !== null && previousLevel !== gamificationLevel.level) {
      toast.success(`Level Up! You're now ${gamificationLevel.label}! 🎉`);
    }
    if (previousLevel === null) {
      setPreviousLevel(gamificationLevel.level);
    } else if (previousLevel !== gamificationLevel.level) {
      setPreviousLevel(gamificationLevel.level);
    }
  }, [gamificationLevel.level, previousLevel]);

  const handleExport = () => {
    try {
      const exportData = {
        ...planData,
        export_date: new Date().toISOString()
      };
      const dataStr = JSON.stringify(exportData, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `emergency-plan-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success('Plan exported successfully!');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export plan');
    }
  };

  const handleImport = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const importData = JSON.parse(e.target.result);
        
        // Validate structure
        if (!importData.version) {
          throw new Error('Invalid plan file format');
        }

        // Merge with existing data, preserving gamification
        setPlanData({
          ...initialPlanData,
          ...importData,
          gamification: {
            ...initialPlanData.gamification,
            ...importData.gamification
          },
          updated_at: new Date().toISOString()
        });

        toast.success('Plan imported successfully!');
      } catch (error) {
        console.error('Import error:', error);
        toast.error(`Import failed: ${error.message}`);
      }
    };

    reader.onerror = () => {
      toast.error('Failed to read file');
    };

    reader.readAsText(file);
  };


  // Import from EmergencyContactApp
  const handleImportEmergencyContacts = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const importData = JSON.parse(e.target.result);
        
        // Check if this is an EmergencyContactApp export
        if (!importData.contacts && !importData.selected_contacts && !importData.contact_data) {
          throw new Error('Invalid EmergencyContactApp export format');
        }

        const importedContacts = [];
        
        // Process selected contacts with contact_data
        if (importData.selected_contacts && importData.contact_data) {
          importData.selected_contacts.forEach((contactKey) => {
            const contactInfo = importData.contact_data[contactKey];
            if (contactInfo) {
              importedContacts.push({
                id: `imported_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                name: contactInfo.name || contactInfo.contact_name || '',
                phone: contactInfo.phone || contactInfo.phone_number || '',
                email: contactInfo.email || '',
                address: contactInfo.address || '',
                category: contactInfo.category || 'Imported',
                notes: contactInfo.notes || '',
                imported: true,
                imported_from: 'EmergencyContactApp',
                imported_at: new Date().toISOString()
              });
            }
          });
        }

        // Process custom contacts
        if (importData.custom_contacts && Array.isArray(importData.custom_contacts)) {
          importData.custom_contacts.forEach((contact) => {
            importedContacts.push({
              id: `imported_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
              name: contact.name || '',
              phone: contact.phone || '',
              email: contact.email || '',
              address: contact.address || '',
              category: contact.category || 'Imported',
              notes: contact.notes || '',
              imported: true,
              imported_from: 'EmergencyContactApp',
              imported_at: new Date().toISOString()
            });
          });
        }

        // Process contacts array if present
        if (importData.contacts && Array.isArray(importData.contacts)) {
          importData.contacts.forEach((contact) => {
            if (!importedContacts.find(c => c.name === contact.name && c.phone === contact.phone)) {
              importedContacts.push({
                id: `imported_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                name: contact.name || contact.contact_name || '',
                phone: contact.phone || contact.phone_number || '',
                email: contact.email || '',
                address: contact.address || '',
                category: contact.category || 'Imported',
                notes: contact.notes || '',
                imported: true,
                imported_from: 'EmergencyContactApp',
                imported_at: new Date().toISOString()
              });
            }
          });
        }

        // Update plan data
        setPlanData(prev => ({
          ...prev,
          emergency_contacts: {
            imported: true,
            contacts: [...prev.emergency_contacts.contacts, ...importedContacts],
            import_source: 'EmergencyContactApp',
            imported_at: new Date().toISOString()
          },
          import_sources: {
            ...prev.import_sources,
            emergency_contacts: {
              imported_at: new Date().toISOString(),
              count: importedContacts.length
            }
          }
        }));

        toast.success(`Successfully imported ${importedContacts.length} emergency contacts!`);
      } catch (error) {
        console.error('Import error:', error);
        toast.error(`Failed to import contacts: ${error.message}`);
      }
    };

    reader.onerror = () => {
      toast.error('Failed to read file');
    };

    reader.readAsText(file);
  };

  // Import from InventoryTool
  const handleImportInventory = (event) => {
    const file = event.target.files[0];
    if (!file) {
      console.log('No file selected');
      return;
    }

    console.log('Importing file:', file.name, file.type);

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const fileContent = e.target.result;
        console.log('File content length:', fileContent.length);
        
        const importData = JSON.parse(fileContent);
        console.log('Parsed import data:', importData);
        
        // Check if this is an InventoryTool export
        let kits = [];
        if (importData.data && importData.data.kits) {
          kits = importData.data.kits;
          console.log('Found kits in importData.data.kits:', kits.length);
        } else if (Array.isArray(importData)) {
          kits = importData;
          console.log('Import data is array, using directly:', kits.length);
        } else if (importData.kits) {
          kits = importData.kits;
          console.log('Found kits in importData.kits:', kits.length);
        } else {
          console.error('Invalid format. Import data keys:', Object.keys(importData));
          throw new Error('Invalid InventoryTool export format. Expected array of kits or object with kits/data.kits property.');
        }

        if (!Array.isArray(kits) || kits.length === 0) {
          console.warn('No kits found in import data');
          toast.warning('No kits found in the import file');
          return;
        }

        const importedKits = [];
        let totalItems = 0;
        
        // Convert kits from inventory tool format to emergency plan format
        kits.forEach((kit, index) => {
          console.log(`Processing kit ${index + 1}:`, kit.name || 'Unnamed');
          
          const importedKit = {
            id: kit.id || `kit_imported_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            name: kit.name || 'Imported Kit',
            location: kit.location || '',
            notes: kit.description || kit.notes || '',
            contents: [],
            imported: true,
            imported_from: 'InventoryTool',
            imported_at: new Date().toISOString()
          };
          
          // Always initialize contents array, even if empty
          if (kit.items && Array.isArray(kit.items) && kit.items.length > 0) {
            importedKit.contents = kit.items.map((item) => ({
              id: item.id || `item_imported_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
              name: item.name || '',
              category: item.category || '',
              quantity: item.quantity || '',
              unit: item.unit || '',
              expiration_date: item.expiration_date || '',
              notes: item.notes || ''
            }));
            totalItems += importedKit.contents.length;
            console.log(`  - Added ${importedKit.contents.length} items to kit`);
          } else {
            // Ensure contents is always an array, even if empty
            importedKit.contents = [];
            console.log(`  - Kit has no items (empty kit)`);
          }
          
          importedKits.push(importedKit);
        });

        console.log(`Total kits to import: ${importedKits.length}, Total items: ${totalItems}`);

        // Update plan data
        setPlanData(prev => {
          const updatedKits = [...prev.kits.kits, ...importedKits];
          console.log('Updated kits array length:', updatedKits.length);
          
          return {
            ...prev,
            kits: {
              imported: true,
              kits: updatedKits,
              import_source: 'InventoryTool',
              imported_at: new Date().toISOString()
            },
            import_sources: {
              ...prev.import_sources,
              inventory: {
                imported_at: new Date().toISOString(),
                count: importedKits.length
              }
            }
          };
        });

        toast.success(`Successfully imported ${importedKits.length} kit(s) with ${totalItems} items from InventoryTool!`);
        
        // Reset file input to allow re-importing the same file
        event.target.value = '';
      } catch (error) {
        console.error('Import error:', error);
        console.error('Error stack:', error.stack);
        toast.error(`Failed to import inventory: ${error.message}`);
      }
    };

    reader.onerror = (error) => {
      console.error('FileReader error:', error);
      toast.error('Failed to read file. Please try again.');
    };

    reader.readAsText(file);
  };

  const handlePrintPetIdCard = (pet) => {
    const owner = planData.family_info?.members?.[0];
    const ownerName = owner?.name || '';
    const ownerPhone = owner?.phone || '';
    const html = `<!DOCTYPE html>
<html><head><meta charset="utf-8"><title>Pet ID Card - ${(pet.name || 'Pet').replace(/</g, '&lt;')}</title>
<style>
  @page { size: 3.375in 2.125in landscape; margin: 0; }
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body { width: 3.375in; height: 2.125in; font-family: Arial, Helvetica, sans-serif; overflow: hidden; }
  .card { width: 3.375in; height: 2.125in; display: flex; flex-direction: row; padding: 0.1in; border: 1.5px solid #333; border-radius: 6px; background: #fff; }
  .photo-area { width: 0.95in; min-width: 0.95in; display: flex; align-items: center; justify-content: center; margin-right: 0.1in; }
  .photo-area img { max-width: 0.9in; max-height: 1.1in; object-fit: cover; border-radius: 4px; border: 1px solid #ccc; }
  .photo-placeholder { width: 0.9in; height: 1.1in; border: 1px dashed #aaa; border-radius: 4px; display: flex; align-items: center; justify-content: center; font-size: 7px; color: #999; }
  .info { flex: 1; display: flex; flex-direction: column; justify-content: space-between; font-size: 7.5px; line-height: 1.35; overflow: hidden; }
  .pet-name { font-size: 11px; font-weight: bold; color: #1a1a1a; margin-bottom: 1px; }
  .pet-type { font-size: 8px; color: #555; margin-bottom: 3px; }
  .row { display: flex; justify-content: space-between; margin-bottom: 1.5px; }
  .label { font-weight: bold; color: #333; }
  .value { color: #444; text-align: right; max-width: 60%; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .special { background: #fff8e1; border-left: 2px solid #f9a825; padding: 2px 4px; font-size: 7px; margin-top: 2px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .footer { text-align: right; font-size: 6px; color: #999; margin-top: auto; padding-top: 2px; }
  @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } .card { border: 1.5px solid #333; } }
</style></head><body>
<div class="card">
  <div class="photo-area">
    ${pet.imageData ? `<img src="${pet.imageData.replace(/"/g, '&quot;')}" alt="Pet" />` : '<div class="photo-placeholder">No Photo</div>'}
  </div>
  <div class="info">
    <div>
      <div class="pet-name">${(pet.name || 'Pet').replace(/</g, '&lt;')}</div>
      <div class="pet-type">${(pet.type || '').replace(/</g, '&lt;')}${pet.breed ? ' / ' + pet.breed.replace(/</g, '&lt;') : ''}</div>
      ${pet.microchip_id ? `<div class="row"><span class="label">Microchip:</span><span class="value">${pet.microchip_id.replace(/</g, '&lt;')}</span></div>` : ''}
      ${pet.vetName || pet.vetPhone ? `<div class="row"><span class="label">Vet:</span><span class="value">${(pet.vetName || '').replace(/</g, '&lt;')}${pet.vetPhone ? ' ' + pet.vetPhone.replace(/</g, '&lt;') : ''}</span></div>` : ''}
      ${ownerName || ownerPhone ? `<div class="row"><span class="label">Owner:</span><span class="value">${ownerName.replace(/</g, '&lt;')}${ownerPhone ? ' ' + ownerPhone.replace(/</g, '&lt;') : ''}</span></div>` : ''}
      ${pet.special_needs ? `<div class="special">\u26A0 ${pet.special_needs.replace(/</g, '&lt;')}</div>` : ''}
    </div>
    <div class="footer">PET ID CARD</div>
  </div>
</div>
</body></html>`;
    const printWindow = window.open('', '_blank');
    if (!printWindow) { toast.error('Pop-up blocked. Please allow pop-ups and try again.'); return; }
    printWindow.document.write(html);
    printWindow.document.close();
    setTimeout(() => { printWindow.print(); }, 400);
  };

  // Print functionality
  const _handlePrintSectionRemoved = (sectionName) => {
    const sectionContent = generateSectionPrintContent(planData, sectionName);
    const htmlDocument = generateSectionHTMLDocument(sectionName, sectionContent);
    
    // Create a blob and download it
    const blob = new Blob([htmlDocument], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Emergency_Plan_${sectionName.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.html`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    toast.success(`HTML document for "${sectionName}" generated! Open it in your browser to view or print.`);
  };

  const generateSectionHTMLDocument = (sectionName, sectionContent) => {
    return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Emergency Plan - ${sectionName}</title>
  <style>
            @page {
              size: 8.5in 11in;
              margin: 0.75in;
            }
            * {
              box-sizing: border-box;
            }
            body {
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;
              font-size: 11pt;
              line-height: 1.7;
              color: #1f2937;
              background: #ffffff;
            }
            .print-header {
              background: linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%);
              color: white;
              padding: 25px 30px;
              margin: -0.75in -0.75in 30px -0.75in;
              border-radius: 0;
              box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
            }
            .print-title {
              font-size: 24pt;
              font-weight: 700;
              margin-bottom: 6px;
              letter-spacing: -0.5px;
            }
            .print-date {
              font-size: 10pt;
              color: rgba(255, 255, 255, 0.9);
              font-weight: 500;
            }
            .section-content {
              margin-bottom: 25px;
            }
            .section-title {
              font-size: 16pt;
              font-weight: 700;
              margin-top: 25px;
              margin-bottom: 12px;
              color: #7c3aed;
              padding-bottom: 8px;
              border-bottom: 2px solid #7c3aed;
            }
            .subsection-title {
              font-size: 13pt;
              font-weight: 600;
              margin-top: 20px;
              margin-bottom: 10px;
              color: #4b5563;
              padding-left: 12px;
              border-left: 3px solid #7c3aed;
            }
            .item-row {
              margin-bottom: 10px;
              padding: 10px 12px;
              background: #f9fafb;
              border-radius: 6px;
              border-left: 3px solid #e5e7eb;
            }
            .label {
              font-weight: 600;
              display: inline-block;
              width: 180px;
              color: #374151;
              margin-right: 10px;
            }
            .value {
              color: #1f2937;
            }
            .highlight-box {
              background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
              border: 2px solid #f59e0b;
              border-radius: 8px;
              padding: 15px;
              margin: 20px 0;
            }
            .info-card {
              background: white;
              border: 1px solid #e5e7eb;
              border-radius: 8px;
              padding: 20px;
              margin: 15px 0;
              box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
            }
            .badge {
              display: inline-block;
              padding: 4px 12px;
              border-radius: 12px;
              font-size: 10pt;
              font-weight: 600;
              margin: 2px;
            }
            .badge-primary {
              background: #ede9fe;
              color: #7c3aed;
            }
            .badge-success {
              background: #d1fae5;
              color: #059669;
            }
            .badge-warning {
              background: #fef3c7;
              color: #d97706;
            }
            @media print {
              .no-print {
                display: none;
              }
              .page-break {
                page-break-after: always;
              }
              .print-header {
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
              }
              .item-row {
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
                page-break-inside: avoid;
              }
              .highlight-box {
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
                page-break-inside: avoid;
              }
              .info-card {
                page-break-inside: avoid;
              }
            }
          </style>
        </head>
        <body>
          ${sectionContent}
        </body>
      </html>`;
  };

  const generateBabysitterReference = () => {
    const addr = planData.home_info?.address || {};
    const fullAddress = `${addr.street || ''} ${addr.city || ''}, ${addr.state || ''} ${addr.zip || ''}`.trim();
    
    let content = `
      <div class="reference-card">
        <div class="header">
          <h1>🚨 EMERGENCY INFORMATION 🚨</h1>
          <p style="font-size: 14pt; margin-top: 10px;">Babysitter Quick Reference Card</p>
        </div>

        <div class="emergency-numbers">
          <div class="section-title">EMERGENCY NUMBERS</div>
          <div class="info-row">
            <div class="info-label">911 (Emergency):</div>
            <div class="info-value"><strong>911</strong></div>
          </div>
          <div class="info-row">
            <div class="info-label">Poison Control:</div>
            <div class="info-value"><strong>1-800-222-1222</strong></div>
          </div>
          <div class="info-row">
            <div class="info-label">Non-Emergency Police:</div>
            <div class="info-value">${planData.emergency_contacts?.contacts?.find(c => c.category === 'Police')?.phone || 'Check local directory'}</div>
          </div>
          <div class="info-row">
            <div class="info-label">Fire Department:</div>
            <div class="info-value">${planData.emergency_contacts?.contacts?.find(c => c.category === 'Fire')?.phone || 'Check local directory'}</div>
          </div>
        </div>

        <div class="section">
          <div class="section-title">HOME ADDRESS</div>
          <div class="info-row">
            <div class="info-value"><strong>${fullAddress || 'Not specified'}</strong></div>
          </div>
        </div>

        <div class="section">
          <div class="section-title">PARENT/GUARDIAN CONTACTS</div>
    `;
    
    // Add parent contacts from family members
    const parents = (planData.family_info?.members || []).filter(m => 
      m.role && ['parent', 'guardian', 'head_of_household'].some(r => m.role.toLowerCase().includes(r))
    );
    
    if (parents.length > 0) {
      parents.forEach((parent) => {
        const contact = planData.emergency_contacts?.contacts?.find(c => 
          c.name?.toLowerCase().includes(parent.name?.toLowerCase() || '')
        );
        content += `
          <div class="info-row">
            <div class="info-label">${parent.name || 'Parent'}:</div>
            <div class="info-value">${contact?.phone || 'Not provided'}</div>
          </div>
        `;
      });
    } else {
      // Fallback to emergency contacts
      const primaryContacts = (planData.emergency_contacts?.contacts || []).slice(0, 2);
      primaryContacts.forEach((contact) => {
        content += `
          <div class="info-row">
            <div class="info-label">${contact.name || 'Contact'}:</div>
            <div class="info-value">${contact.phone || 'Not provided'}</div>
          </div>
        `;
      });
    }
    
    // Add out-of-area contact
    const outOfAreaContact = planData.communication_plan?.out_of_area_contacts?.find(c => c.is_primary);
    if (outOfAreaContact) {
      content += `
        <div class="info-row">
          <div class="info-label">Out-of-Area Contact:</div>
          <div class="info-value">${outOfAreaContact.name || ''} - ${outOfAreaContact.phone || ''}</div>
        </div>
      `;
    }
    
    content += `</div>`;

    // Family Members with Medical Info
    if (planData.family_info?.members && planData.family_info.members.length > 0) {
      content += `
        <div class="section">
          <div class="section-title">FAMILY MEMBERS</div>
      `;
      
      planData.family_info.members.forEach((member) => {
        let ageDisplay = '';
        if (member.date_of_birth) {
          const birthDate = new Date(member.date_of_birth);
          const today = new Date();
          let age = today.getFullYear() - birthDate.getFullYear();
          const monthDiff = today.getMonth() - birthDate.getMonth();
          if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
            age--;
          }
          ageDisplay = ` (Age: ${age})`;
        }
        
        content += `
          <div class="family-member">
            <div class="info-row">
              <div class="info-label"><strong>${member.name || 'Family Member'}</strong>${ageDisplay}</div>
            </div>
        `;
        
        // Allergies
        if (member.allergies && member.allergies.length > 0) {
          member.allergies.forEach((allergy) => {
            content += `
              <div class="allergy-warning">
                <strong>⚠️ ALLERGY:</strong> ${allergy.allergen || 'Unknown'} 
                ${allergy.severity ? `(${allergy.severity})` : ''}
                ${allergy.notes ? ` - ${allergy.notes}` : ''}
              </div>
            `;
          });
        }
        
        // Medications
        if (member.medications && member.medications.length > 0) {
          member.medications.forEach((med) => {
            content += `
              <div class="medication-info">
                <strong>💊 Medication:</strong> ${med.medication || 'Unknown'}
                ${med.dosage ? ` - ${med.dosage}` : ''}
                ${med.frequency ? ` (${med.frequency})` : ''}
              </div>
            `;
          });
        }
        
        content += `</div>`;
      });
      
      content += `</div>`;
    }

    // Important Notes
    if (planData.home_info?.layout_notes) {
      content += `
        <div class="section">
          <div class="section-title">IMPORTANT NOTES</div>
          <div class="info-value">${planData.home_info.layout_notes}</div>
        </div>
      `;
    }

    // School Pickup Information
    const schools = planData.school_work_plans?.school_pickup ? Object.values(planData.school_work_plans.school_pickup) : [];
    if (schools.length > 0) {
      content += `
        <div class="section">
          <div class="section-title">SCHOOL PICKUP INFORMATION</div>
      `;
      schools.forEach((school) => {
        content += `
          <div class="family-member">
            <div class="info-row">
              <div class="info-label"><strong>${school.name || 'School'}</strong></div>
            </div>
            ${school.address ? `
              <div class="info-row">
                <div class="info-label">Address:</div>
                <div class="info-value">${school.address}</div>
              </div>
            ` : ''}
            ${school.phone ? `
              <div class="info-row">
                <div class="info-label">Phone:</div>
                <div class="info-value">${school.phone}</div>
              </div>
            ` : ''}
            ${school.pickup_procedures ? `
              <div class="info-row">
                <div class="info-label">Pickup Procedures:</div>
                <div class="info-value">${school.pickup_procedures}</div>
              </div>
            ` : ''}
          </div>
        `;
      });
      content += `</div>`;
    }

    // Pet Information
    if (planData.pet_plans?.pets && planData.pet_plans.pets.length > 0) {
      content += `
        <div class="section">
          <div class="section-title">PET INFORMATION</div>
      `;
      planData.pet_plans.pets.forEach((pet) => {
        content += `
          <div class="family-member">
            ${pet.imageData ? `
              <div style="margin-bottom: 0.5rem;">
                <img src="${(pet.imageData || '').replace(/"/g, '&quot;')}" alt="${(pet.name || 'Pet').replace(/"/g, '&quot;')}" style="max-width: 200px; max-height: 200px; border-radius: 8px; border: 1px solid #e5e7eb;" />
              </div>
            ` : ''}
            <div class="info-row">
              <div class="info-label"><strong>${pet.name || 'Pet'}</strong> - ${pet.type || 'Unknown'} (${pet.breed || 'Unknown breed'})</div>
            </div>
            ${pet.age ? `
              <div class="info-row">
                <div class="info-label">Age:</div>
                <div class="info-value">${pet.age}</div>
              </div>
            ` : ''}
            ${pet.microchip_id ? `
              <div class="info-row">
                <div class="info-label">Microchip ID:</div>
                <div class="info-value">${pet.microchip_id}</div>
              </div>
            ` : ''}
            ${(pet.vetName || pet.vetPhone) ? `
              <div class="info-row">
                <div class="info-label">Vet:</div>
                <div class="info-value">${pet.vetName || '—'}${pet.vetPhone ? ' ' + pet.vetPhone : ''}</div>
              </div>
            ` : ''}
            ${pet.special_needs ? `
              <div class="allergy-warning">
                <strong>⚠️ Special Needs:</strong> ${pet.special_needs}
              </div>
            ` : ''}
          </div>
        `;
      });
      if (planData.pet_plans.vet_contacts && planData.pet_plans.vet_contacts.length > 0) {
        content += `<div class="info-row" style="margin-top: 10px;"><div class="info-label"><strong>Veterinarian:</strong></div></div>`;
        planData.pet_plans.vet_contacts.forEach((vet) => {
          content += `
            <div class="info-row">
              <div class="info-label">${vet.name || 'Vet'}:</div>
              <div class="info-value">${vet.phone || ''} ${vet.address ? ` - ${vet.address}` : ''}</div>
            </div>
          `;
        });
      }
      content += `</div>`;
    }

    // Doctor Information
    const doctors = [];
    (planData.family_info?.members || []).forEach((member) => {
      if (member.medications && member.medications.length > 0) {
        member.medications.forEach((med) => {
          if (med.prescribing_doctor && !doctors.find(d => d.name === med.prescribing_doctor)) {
            doctors.push({ name: med.prescribing_doctor, member: member.name });
          }
        });
      }
    });
    if (planData.medical_info?.doctors && planData.medical_info.doctors.length > 0) {
      planData.medical_info.doctors.forEach((doc) => {
        if (!doctors.find(d => d.name === doc.name)) {
          doctors.push({ name: doc.name, phone: doc.phone, specialty: doc.specialty });
        }
      });
    }
    if (doctors.length > 0) {
      content += `
        <div class="section">
          <div class="section-title">DOCTOR CONTACTS</div>
      `;
      doctors.forEach((doc) => {
        content += `
          <div class="info-row">
            <div class="info-label">${doc.name || 'Doctor'}:</div>
            <div class="info-value">${doc.phone || 'Not provided'} ${doc.specialty ? `(${doc.specialty})` : ''} ${doc.member ? `- For ${doc.member}` : ''}</div>
          </div>
        `;
      });
      content += `</div>`;
    }

    // Neighbor Contacts
    if (planData.communication_plan?.neighbor_network && planData.communication_plan.neighbor_network.length > 0) {
      content += `
        <div class="section">
          <div class="section-title">NEIGHBOR CONTACTS</div>
      `;
      planData.communication_plan.neighbor_network.forEach((neighbor) => {
        content += `
          <div class="info-row">
            <div class="info-label">${neighbor.name || 'Neighbor'}:</div>
            <div class="info-value">${neighbor.phone || ''} ${neighbor.address ? ` - ${neighbor.address}` : ''}</div>
          </div>
        `;
      });
      content += `</div>`;
    }

    // Additional Emergency Contacts
    const additionalContacts = (planData.emergency_contacts?.contacts || []).filter(c => 
      c.category && !['Police', 'Fire'].includes(c.category)
    ).slice(0, 5);
    if (additionalContacts.length > 0) {
      content += `
        <div class="section">
          <div class="section-title">ADDITIONAL EMERGENCY CONTACTS</div>
      `;
      additionalContacts.forEach((contact) => {
        content += `
          <div class="info-row">
            <div class="info-label">${contact.name || 'Contact'}:</div>
            <div class="info-value">${contact.phone || ''} ${contact.category ? `(${contact.category})` : ''}</div>
          </div>
        `;
      });
      content += `</div>`;
    }

    // Utility Shutoff Locations
    const shutoffs = planData.home_info?.utility_shutoffs || {};
    if (shutoffs.gas || shutoffs.water || shutoffs.electricity) {
      content += `
        <div class="section">
          <div class="section-title">UTILITY SHUTOFF LOCATIONS</div>
      `;
      if (shutoffs.gas) {
        content += `
          <div class="info-row">
            <div class="info-label">Gas:</div>
            <div class="info-value">${shutoffs.gas}</div>
          </div>
        `;
      }
      if (shutoffs.water) {
        content += `
          <div class="info-row">
            <div class="info-label">Water:</div>
            <div class="info-value">${shutoffs.water}</div>
          </div>
        `;
      }
      if (shutoffs.electricity) {
        content += `
          <div class="info-row">
            <div class="info-label">Electricity:</div>
            <div class="info-value">${shutoffs.electricity}</div>
          </div>
        `;
      }
      content += `</div>`;
    }

    // Evacuation Info
    const primaryEvacPlan = planData.evacuation_plans?.plans?.[0];
    if (primaryEvacPlan) {
      content += `
        <div class="section">
          <div class="section-title">EVACUATION INFORMATION</div>
      `;
      if (primaryEvacPlan.meeting_points?.primary) {
        content += `
          <div class="info-row">
            <div class="info-label">Meeting Point:</div>
            <div class="info-value">${primaryEvacPlan.meeting_points.primary}</div>
          </div>
        `;
      }
      if (primaryEvacPlan.primary_route) {
        content += `
          <div class="info-row">
            <div class="info-label">Primary Route:</div>
            <div class="info-value">${primaryEvacPlan.primary_route}</div>
          </div>
        `;
      }
      content += `</div>`;
    }

    content += `
        <div style="margin-top: 30px; padding-top: 15px; border-top: 2px solid #000; text-align: center; font-size: 10pt; color: #666;">
          <p><strong>Keep this card in a visible location</strong></p>
          <p>Last Updated: ${new Date().toLocaleDateString()}</p>
        </div>
      </div>
    `;
    
    return content;
  };

  const generateBabysitterReferencePreview = () => {
    const addr = planData.home_info?.address || {};
    const fullAddress = `${addr.street || ''} ${addr.city || ''}, ${addr.state || ''} ${addr.zip || ''}`.trim();
    
    return (
      <div className="space-y-4">
        <div className="text-center border-b-2 border-border pb-3 mb-4">
          <h2 className="text-2xl font-bold text-destructive">🚨 EMERGENCY INFORMATION 🚨</h2>
          <p className="text-lg mt-2">Babysitter Quick Reference Card</p>
        </div>

        <div className="bg-destructive/10 border-2 border-destructive p-4 rounded">
          <h3 className="font-bold text-lg mb-3 bg-destructive text-destructive-foreground p-2 -m-4 mb-2">EMERGENCY NUMBERS</h3>
          <div className="space-y-2">
            <div className="flex">
              <span className="font-bold w-40">911 (Emergency):</span>
              <span className="font-bold">911</span>
            </div>
            <div className="flex">
              <span className="font-bold w-40">Poison Control:</span>
              <span className="font-bold">1-800-222-1222</span>
            </div>
            <div className="flex">
              <span className="font-bold w-40">Non-Emergency Police:</span>
              <span>{planData.emergency_contacts?.contacts?.find(c => c.category === 'Police')?.phone || 'Check local directory'}</span>
            </div>
            <div className="flex">
              <span className="font-bold w-40">Fire Department:</span>
              <span>{planData.emergency_contacts?.contacts?.find(c => c.category === 'Fire')?.phone || 'Check local directory'}</span>
            </div>
          </div>
        </div>

        <div>
          <h3 className="font-bold text-lg mb-2 bg-surface-muted p-2 border-l-4 border-primary text-text-primary">HOME ADDRESS</h3>
          <p className="font-bold">{fullAddress || 'Not specified'}</p>
        </div>

        <div>
          <h3 className="font-bold text-lg mb-2 bg-surface-muted p-2 border-l-4 border-primary text-text-primary">PARENT/GUARDIAN CONTACTS</h3>
          <div className="space-y-2">
            {(planData.family_info?.members || []).filter(m => 
              m.role && ['parent', 'guardian', 'head_of_household'].some(r => m.role.toLowerCase().includes(r))
            ).slice(0, 2).map((parent, idx) => {
              const contact = planData.emergency_contacts?.contacts?.find(c => 
                c.name?.toLowerCase().includes(parent.name?.toLowerCase() || '')
              );
              return (
                <div key={idx} className="flex">
                  <span className="font-bold w-40">{parent.name || 'Parent'}:</span>
                  <span>{contact?.phone || 'Not provided'}</span>
                </div>
              );
            })}
            {planData.communication_plan?.out_of_area_contacts?.find(c => c.is_primary) && (
              <div className="flex">
                <span className="font-bold w-40">Out-of-Area Contact:</span>
                <span>{planData.communication_plan.out_of_area_contacts.find(c => c.is_primary).name} - {planData.communication_plan.out_of_area_contacts.find(c => c.is_primary).phone}</span>
              </div>
            )}
          </div>
        </div>

        {(planData.family_info?.members || []).length > 0 && (
          <div>
            <h3 className="font-bold text-lg mb-2 bg-surface-muted p-2 border-l-4 border-primary text-text-primary">FAMILY MEMBERS</h3>
            <div className="space-y-3">
              {planData.family_info.members.map((member, idx) => {
                let ageDisplay = '';
                if (member.date_of_birth) {
                  const birthDate = new Date(member.date_of_birth);
                  const today = new Date();
                  let age = today.getFullYear() - birthDate.getFullYear();
                  const monthDiff = today.getMonth() - birthDate.getMonth();
                  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
                    age--;
                  }
                  ageDisplay = ` (Age: ${age})`;
                }
                
                return (
                  <div key={idx} className="border-b border-border pb-2">
                    <p className="font-bold">{member.name || 'Family Member'}{ageDisplay}</p>
                    {member.allergies && member.allergies.length > 0 && (
                      <div className="mt-1">
                        {member.allergies.map((allergy, aIdx) => (
                          <div key={aIdx} className="bg-primary/20 border-l-4 border-primary p-2 my-1 text-sm text-foreground/90">
                            <strong>⚠️ ALLERGY:</strong> {allergy.allergen || 'Unknown'} 
                            {allergy.severity && ` (${allergy.severity})`}
                            {allergy.notes && ` - ${allergy.notes}`}
                          </div>
                        ))}
                      </div>
                    )}
                    {member.medications && member.medications.length > 0 && (
                      <div className="mt-1">
                        {member.medications.map((med, mIdx) => (
                          <div key={mIdx} className="bg-primary/10 border-l-4 border-primary p-2 my-1 text-sm text-foreground/90">
                            <strong>💊 Medication:</strong> {med.medication || 'Unknown'}
                            {med.dosage && ` - ${med.dosage}`}
                            {med.frequency && ` (${med.frequency})`}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {planData.home_info?.layout_notes && (
          <div>
            <h3 className="font-bold text-lg mb-2 bg-surface-muted p-2 border-l-4 border-primary text-text-primary">IMPORTANT NOTES</h3>
            <p>{planData.home_info.layout_notes}</p>
          </div>
        )}

        {planData.school_work_plans?.school_pickup && Object.values(planData.school_work_plans.school_pickup).length > 0 && (
          <div>
            <h3 className="font-bold text-lg mb-2 bg-surface-muted p-2 border-l-4 border-primary text-text-primary">SCHOOL PICKUP INFORMATION</h3>
            <div className="space-y-3">
              {Object.values(planData.school_work_plans.school_pickup).map((school, idx) => (
                <div key={idx} className="border-b border-border pb-2">
                  <p className="font-bold">{school.name || 'School'}</p>
                  {school.address && (
                    <div className="flex text-sm">
                      <span className="font-bold w-32">Address:</span>
                      <span>{school.address}</span>
                    </div>
                  )}
                  {school.phone && (
                    <div className="flex text-sm">
                      <span className="font-bold w-32">Phone:</span>
                      <span>{school.phone}</span>
                    </div>
                  )}
                  {school.pickup_procedures && (
                    <div className="mt-2 text-sm">
                      <span className="font-bold">Pickup Procedures:</span>
                      <p className="mt-1">{school.pickup_procedures}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {planData.pet_plans?.pets && planData.pet_plans.pets.length > 0 && (
          <div>
            <h3 className="font-bold text-lg mb-2 bg-surface-muted p-2 border-l-4 border-primary text-text-primary">PET INFORMATION</h3>
            <div className="space-y-3">
              {planData.pet_plans.pets.map((pet, idx) => (
                <div key={idx} className="border-b border-border pb-2">
                  {pet.imageData && (
                    <img src={pet.imageData} alt={pet.name || 'Pet'} className="w-24 h-24 object-cover rounded-md border mb-2" />
                  )}
                  <p className="font-bold">{pet.name || 'Pet'} - {pet.type || 'Unknown'} ({pet.breed || 'Unknown breed'})</p>
                  {pet.age && (
                    <div className="flex text-sm">
                      <span className="font-bold w-32">Age:</span>
                      <span>{pet.age}</span>
                    </div>
                  )}
                  {pet.microchip_id && (
                    <div className="flex text-sm">
                      <span className="font-bold w-32">Microchip ID:</span>
                      <span>{pet.microchip_id}</span>
                    </div>
                  )}
                  {(pet.vetName || pet.vetPhone) && (
                    <div className="flex text-sm">
                      <span className="font-bold w-32">Vet:</span>
                      <span>{pet.vetName || '—'}{pet.vetPhone ? ` ${pet.vetPhone}` : ''}</span>
                    </div>
                  )}
                  {pet.special_needs && (
                    <div className="bg-primary/20 border-l-4 border-primary p-2 my-1 text-sm text-foreground/90 mt-2">
                      <strong>⚠️ Special Needs:</strong> {pet.special_needs}
                    </div>
                  )}
                </div>
              ))}
              {planData.pet_plans.vet_contacts && planData.pet_plans.vet_contacts.length > 0 && (
                <div className="mt-3">
                  <p className="font-bold mb-2">Veterinarian:</p>
                  {planData.pet_plans.vet_contacts.map((vet, idx) => (
                    <div key={idx} className="flex text-sm mb-1">
                      <span className="font-bold w-32">{vet.name || 'Vet'}:</span>
                      <span>{vet.phone || ''} {vet.address ? ` - ${vet.address}` : ''}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {(() => {
          const doctors = [];
          (planData.family_info?.members || []).forEach((member) => {
            if (member.medications && member.medications.length > 0) {
              member.medications.forEach((med) => {
                if (med.prescribing_doctor && !doctors.find(d => d.name === med.prescribing_doctor)) {
                  doctors.push({ name: med.prescribing_doctor, member: member.name });
                }
              });
            }
          });
          if (planData.medical_info?.doctors && planData.medical_info.doctors.length > 0) {
            planData.medical_info.doctors.forEach((doc) => {
              if (!doctors.find(d => d.name === doc.name)) {
                doctors.push({ name: doc.name, phone: doc.phone, specialty: doc.specialty });
              }
            });
          }
          return doctors.length > 0 ? (
            <div>
              <h3 className="font-bold text-lg mb-2 bg-surface-muted p-2 border-l-4 border-primary text-text-primary">DOCTOR CONTACTS</h3>
              <div className="space-y-2">
                {doctors.map((doc, idx) => (
                  <div key={idx} className="flex">
                    <span className="font-bold w-40">{doc.name || 'Doctor'}:</span>
                    <span>{doc.phone || 'Not provided'} {doc.specialty ? `(${doc.specialty})` : ''} {doc.member ? `- For ${doc.member}` : ''}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : null;
        })()}

        {planData.communication_plan?.neighbor_network && planData.communication_plan.neighbor_network.length > 0 && (
          <div>
            <h3 className="font-bold text-lg mb-2 bg-surface-muted p-2 border-l-4 border-primary text-text-primary">NEIGHBOR CONTACTS</h3>
            <div className="space-y-2">
              {planData.communication_plan.neighbor_network.map((neighbor, idx) => (
                <div key={idx} className="flex">
                  <span className="font-bold w-40">{neighbor.name || 'Neighbor'}:</span>
                  <span>{neighbor.phone || ''} {neighbor.address ? ` - ${neighbor.address}` : ''}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {(() => {
          const additionalContacts = (planData.emergency_contacts?.contacts || []).filter(c => 
            c.category && !['Police', 'Fire'].includes(c.category)
          ).slice(0, 5);
          return additionalContacts.length > 0 ? (
            <div>
              <h3 className="font-bold text-lg mb-2 bg-surface-muted p-2 border-l-4 border-primary text-text-primary">ADDITIONAL EMERGENCY CONTACTS</h3>
              <div className="space-y-2">
                {additionalContacts.map((contact, idx) => (
                  <div key={idx} className="flex">
                    <span className="font-bold w-40">{contact.name || 'Contact'}:</span>
                    <span>{contact.phone || ''} {contact.category ? `(${contact.category})` : ''}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : null;
        })()}

        {(() => {
          const shutoffs = planData.home_info?.utility_shutoffs || {};
          return (shutoffs.gas || shutoffs.water || shutoffs.electricity) ? (
            <div>
              <h3 className="font-bold text-lg mb-2 bg-surface-muted p-2 border-l-4 border-primary text-text-primary">UTILITY SHUTOFF LOCATIONS</h3>
              <div className="space-y-2">
                {shutoffs.gas && (
                  <div className="flex">
                    <span className="font-bold w-40">Gas:</span>
                    <span>{shutoffs.gas}</span>
                  </div>
                )}
                {shutoffs.water && (
                  <div className="flex">
                    <span className="font-bold w-40">Water:</span>
                    <span>{shutoffs.water}</span>
                  </div>
                )}
                {shutoffs.electricity && (
                  <div className="flex">
                    <span className="font-bold w-40">Electricity:</span>
                    <span>{shutoffs.electricity}</span>
                  </div>
                )}
              </div>
            </div>
          ) : null;
        })()}

        {planData.evacuation_plans?.plans?.[0] && (
          <div>
            <h3 className="font-bold text-lg mb-2 bg-surface-muted p-2 border-l-4 border-primary text-text-primary">EVACUATION INFORMATION</h3>
            <div className="space-y-2">
              {planData.evacuation_plans.plans[0].meeting_points?.primary && (
                <div className="flex">
                  <span className="font-bold w-40">Meeting Point:</span>
                  <span>{planData.evacuation_plans.plans[0].meeting_points.primary}</span>
                </div>
              )}
              {planData.evacuation_plans.plans[0].primary_route && (
                <div className="flex">
                  <span className="font-bold w-40">Primary Route:</span>
                  <span>{planData.evacuation_plans.plans[0].primary_route}</span>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="mt-6 pt-4 border-t-2 border-border text-center text-sm text-foreground/90">
          <p><strong>Keep this card in a visible location</strong></p>
          <p>Last Updated: {new Date().toLocaleDateString()}</p>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-page flex items-center justify-center">
        <div className="text-center animate-fade-in">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground animate-pulse">Loading your emergency plan...</p>
        </div>
      </div>
    );
  }

  // Safety check: ensure planData is valid
  if (!planData) {
    console.error('planData is null or undefined');
    return (
      <div className="min-h-screen bg-page flex items-center justify-center">
        <div className="text-center">
          <p className="text-destructive mb-4">Error: Plan data is missing. Please refresh the page.</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-primary text-primary-foreground rounded hover:opacity-90"
          >
            Refresh Page
          </button>
        </div>
      </div>
    );
  }

  // Ensure communication_plan exists before rendering
  if (!planData.communication_plan) {
    console.warn('communication_plan is missing, initializing...');
    setPlanData(prev => ({
      ...prev,
      communication_plan: initialPlanData.communication_plan
    }));
    return (
      <div className="min-h-screen bg-page flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground animate-pulse">Initializing plan data...</p>
        </div>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <ToolLayout
        title="Emergency Plan Creator"
        description="Create a comprehensive emergency preparedness plan for your family"
        icon={FileText}
        guide={{
          intro: 'Build a complete emergency plan covering your family, home, and go-bags.',
          steps: [
            'Work through each tab — Family, Evacuation, Disasters, Communication, Medical, Home, Pets, Grab-and-Go, School/Work.',
            'Fill in details for each section.',
            'Pet tab includes photo upload and vet info.',
            'Export to include in your binder.',
          ],
        }}
      >
        <><div className="max-w-7xl mx-auto animate-fade-in">
              <div className="flex flex-wrap items-center justify-end gap-2 mb-6">
                <Button
                  onClick={() => document.getElementById('import-file').click()}
                  className="flex items-center gap-2 bg-primary text-primary-foreground rounded-lg hover:opacity-90"
                >
                  <UploadIcon className="h-4 w-4" />
                  Import Plan
                </Button>
                <input
                  id="import-file"
                  type="file"
                  accept=".json"
                  onChange={handleImport}
                  className="hidden"
                />
                <Button
                  onClick={handleExport}
                  className="flex items-center gap-2 bg-primary text-primary-foreground rounded-lg hover:opacity-90"
                >
                  <Download className="h-4 w-4" />
                  Export
                </Button>
              </div>

          {/* Progress & Achievements */}
          <Card className="mb-3 transition-all duration-300 hover:shadow-md">
            <CardHeader className="pb-2">
              <div className="flex justify-between items-center">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <TrendingUp className="h-4 w-4 text-primary" />
                  Progress & Achievements
                </CardTitle>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className={`text-sm px-3 py-0.5 ${
                    gamificationLevel.color === 'purple' ? 'bg-primary/10 text-primary border-border' :
                    gamificationLevel.color === 'blue' ? 'bg-primary/10 text-primary border-border' :
                    gamificationLevel.color === 'green' ? 'bg-primary/20 text-primary border-primary/50' :
                    'bg-surface-muted text-foreground/90 border-border'
                  }`}>
                    {gamificationLevel.label}
                  </Badge>
                  {gamificationLevel.next && (
                    <div className="text-xs text-foreground/90">
                      {planData.gamification.points || 0} / {gamificationLevel.next}
                    </div>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="space-y-3">
                {/* Progress Section */}
                <div>
                  <div className="flex justify-between mb-1">
                    <span className="text-xs font-medium text-foreground/90">Overall Completion</span>
                    <span className="text-xs font-bold text-primary">{completionStats.percentage}%</span>
                  </div>
                  <div className="w-full bg-surface-muted rounded-full h-2 overflow-hidden">
                    <div
                      className="bg-primary h-2 rounded-full transition-all duration-500 ease-out shadow-sm"
                      style={{ width: `${completionStats.percentage}%` }}
                    ></div>
                  </div>
                </div>
                <div className="grid grid-cols-4 gap-2">
                  <div className="text-center p-2 rounded-lg bg-primary/10 transition-all duration-300 hover:bg-primary/20">
                    <div className="text-lg font-bold text-primary">{completionStats.completed}</div>
                    <div className="text-xs text-foreground/90">Sections</div>
                  </div>
                  <div className="text-center p-2 rounded-lg bg-primary/10 transition-all duration-300 hover:bg-primary/20">
                    <div className="text-lg font-bold text-primary">{planData.gamification.points || 0}</div>
                    <div className="text-xs text-foreground/90">Points</div>
                  </div>
                  <div className="text-center p-2 rounded-lg bg-primary/10 transition-all duration-300 hover:bg-primary/20">
                    <div className="text-lg font-bold text-primary">{planData.gamification.streak_days || 0}</div>
                    <div className="text-xs text-foreground/90">Streak</div>
                  </div>
                  <div className="text-center p-2 rounded-lg bg-primary/10 transition-all duration-300 hover:bg-primary/20">
                    <div className="text-lg font-bold text-primary">{planData.gamification.achievements?.length || 0}</div>
                    <div className="text-xs text-foreground/90">Achieved</div>
                  </div>
                </div>
                
                {/* Achievements Section */}
                <div className="pt-2 border-t border-border">
                  <div className="flex items-center gap-2 mb-2">
                    <Trophy className="h-3 w-3 text-primary" />
                    <span className="text-sm font-semibold text-text-primary">Achievements</span>
                  </div>
                  <div className="overflow-x-auto pb-2">
                    <div className="flex gap-2 min-w-max">
                  {(() => {
                    // Define all possible achievements with descriptions
                    const allAchievements = [
                      { 
                        id: 'home_base', 
                        name: 'Home Base', 
                        icon: '🏠', 
                        condition: () => (planData.family_info?.members?.length ?? 0) > 0,
                        description: 'Add at least one family member to your plan'
                      },
                      { 
                        id: 'emergency_ready', 
                        name: 'Emergency Ready', 
                        icon: '🚨', 
                        condition: () => (planData.emergency_contacts?.contacts?.length ?? 0) >= 5,
                        description: 'Add at least 5 emergency contacts'
                      },
                      { 
                        id: 'navigator', 
                        name: 'Navigator', 
                        icon: '🗺️', 
                        condition: () => (planData.evacuation_plans?.plans?.length ?? 0) > 0 && (planData.evacuation_plans?.plans ?? []).some(p => p?.primary_route && p?.meeting_points?.primary),
                        description: 'Create an evacuation plan with a primary route and meeting point'
                      },
                      { 
                        id: 'fire_fighter', 
                        name: 'Fire Fighter', 
                        icon: '🔥', 
                        condition: () => planData.disaster_plans?.fire?.completed,
                        description: 'Complete your fire disaster plan'
                      },
                      { 
                        id: 'storm_chaser', 
                        name: 'Storm Chaser', 
                        icon: '💨', 
                        condition: () => 
                          planData.disaster_plans?.tornado?.completed || planData.disaster_plans?.hurricane?.completed || planData.disaster_plans?.flood?.completed,
                        description: 'Complete a tornado, hurricane, or flood disaster plan'
                      },
                      { 
                        id: 'pet_protector', 
                        name: 'Pet Protector', 
                        icon: '🐾', 
                        condition: () => (planData.pet_plans?.pets?.length ?? 0) > 0,
                        description: 'Add at least one pet to your pet plans'
                      },
                      { 
                        id: 'plan_master', 
                        name: 'Plan Master', 
                        icon: '⭐', 
                        condition: () => completionStats.percentage >= 100,
                        description: 'Complete 100% of your emergency preparedness plan'
                      }
                    ];
                    
                    // Get earned achievement IDs
                    const earnedAchievementIds = new Set((planData.gamification?.achievements || []).map(a => a.id));
                    
                    // Calculate max width needed for all achievement names
                    const maxNameLength = Math.max(...allAchievements.map(a => a.name.length));
                    const tileWidth = Math.max(100, Math.min(160, maxNameLength * 7 + 30));
                    const tileHeight = 110;
                    
                    return allAchievements.map((achievement, index) => {
                      const isEarned = earnedAchievementIds.has(achievement.id);
                      const earnedAchievement = isEarned ? planData.gamification.achievements.find(a => a.id === achievement.id) : null;
                      
                      return (
                        <div
                          key={achievement.id}
                          className={`flex flex-col items-center justify-center p-2 rounded-lg border-2 transition-all duration-300 transform cursor-help flex-shrink-0 ${
                            isEarned
                              ? 'bg-primary/10 border-primary/50 hover:shadow-lg hover:scale-105 hover:border-primary'
                              : 'bg-surface-muted border-border opacity-80'
                          }`}
                          style={{ animationDelay: `${index * 50}ms`, width: `${tileWidth}px`, height: `${tileHeight}px` }}
                          title={achievement.description}
                        >
                          <div className={`text-2xl mb-1 transition-transform duration-300 ${isEarned ? 'hover:scale-125' : ''}`}>
                            {achievement.icon}
                          </div>
                          <div className={`text-sm font-semibold text-center leading-tight ${isEarned ? 'text-text-primary' : 'text-foreground/90'}`}>
                            {achievement.name}
                          </div>
                          {isEarned && earnedAchievement?.earned_at && (
                            <div className="text-xs text-foreground/90 mt-0.5 text-center">
                              {new Date(earnedAchievement.earned_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                            </div>
                          )}
                          {!isEarned && (
                            <div className="text-sm text-foreground/75 mt-1 text-center font-medium">
                              Locked
                            </div>
                          )}
                        </div>
                      );
                    });
                  })()}
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Streak Display */}
          {planData.gamification.streak_days > 0 && (
            <Card className="mb-6 border-primary/30 bg-primary/10">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-text-primary">
                  <TrendingUp className="h-5 w-5 text-primary" />
                  Review Streak
                </CardTitle>
                <CardDescription className="text-foreground/90">
                  Keep your plan updated! Review your plan regularly to maintain your streak.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-4">
                  <div className="text-4xl font-bold text-primary">{planData.gamification.streak_days}</div>
                  <div>
                    <div className="font-semibold text-text-primary">Day Streak</div>
                    <div className="text-sm text-primary">
                      {planData.gamification.streak_days >= 7 && '🔥 '}
                      {planData.gamification.streak_days >= 30 && '🔥🔥 '}
                      Keep it up!
                    </div>
                  </div>
                  <div className="ml-auto">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setPlanData(prev => ({
                          ...prev,
                          gamification: {
                            ...prev.gamification,
                            last_review_date: new Date().toISOString()
                          }
                        }));
                        toast.success('Plan review recorded! Streak maintained.');
                      }}
                      className="bg-primary text-primary-foreground hover:opacity-90"
                    >
                      Mark as Reviewed
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Review Reminder Banner */}
          {reviewReminder && (reviewReminder.status === 'overdue' || reviewReminder.status === 'due_soon') && (
            <Card className={`mb-6 ${
              reviewReminder.color === 'red' ? 'border-destructive/50 bg-destructive/10' :
              reviewReminder.color === 'orange' ? 'border-primary/50 bg-primary/10' :
              'border-primary/50 bg-primary/10'
            }`}>
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  <div className={`p-3 rounded-lg flex-shrink-0 ${
                    reviewReminder.color === 'red' ? 'bg-destructive/20' :
                    reviewReminder.color === 'orange' ? 'bg-primary/20' :
                    'bg-primary/20'
                  }`}>
                    <Bell className={`h-6 w-6 ${
                      reviewReminder.color === 'red' ? 'text-destructive' :
                      reviewReminder.color === 'orange' ? 'text-primary' :
                      'text-primary'
                    }`} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <CardTitle className={`flex items-center gap-2 ${
                        reviewReminder.color === 'red' ? 'text-destructive' :
                        reviewReminder.color === 'orange' ? 'text-text-primary' :
                        'text-text-primary'
                      }`}>
                        <Calendar className="h-5 w-5" />
                        {reviewReminder.status === 'overdue' ? 'Plan Review Overdue' : 'Plan Review Due Soon'}
                      </CardTitle>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          // Store dismissed reminder preference
                          const dismissed = JSON.parse(localStorage.getItem('review_reminder_dismissed') || '{}');
                          dismissed[new Date().toISOString().split('T')[0]] = true;
                          localStorage.setItem('review_reminder_dismissed', JSON.stringify(dismissed));
                          // Force re-render by updating a dummy state
                          setPlanData(prev => ({ ...prev }));
                        }}
                        className="text-foreground/90"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                    <CardDescription className={`mb-4 ${
                      reviewReminder.color === 'red' ? 'text-destructive/90' :
                      reviewReminder.color === 'orange' ? 'text-foreground/90' :
                      'text-foreground/90'
                    }`}>
                      {reviewReminder.message}. Emergency plans should be reviewed every 90 days to ensure information is current.
                    </CardDescription>
                    <div className="flex items-center gap-3">
                      <Button
                        onClick={() => {
                          setPlanData(prev => ({
                            ...prev,
                            gamification: {
                              ...prev.gamification,
                              last_review_date: new Date().toISOString()
                            }
                          }));
                          toast.success('Plan review recorded! Your plan is now up to date.');
                        }}
                        className={`flex items-center gap-2 ${
                          reviewReminder.color === 'red' ? 'bg-destructive text-destructive-foreground hover:opacity-90' :
                          reviewReminder.color === 'orange' ? 'bg-primary text-primary-foreground hover:opacity-90' :
                          'bg-primary text-primary-foreground hover:opacity-90'
                        }`}
                      >
                        <CheckCircle className="h-4 w-4" />
                        Mark as Reviewed
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => {
                          // Navigate to overview to review plan
                          setActiveTab('overview');
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }}
                        className="flex items-center gap-2"
                      >
                        <FileText className="h-4 w-4" />
                        Review Plan Now
                      </Button>
                    </div>
                    {reviewReminder.status === 'overdue' && (
                      <div className="mt-4 p-3 bg-card rounded-lg border border-border">
                        <p className="text-sm text-foreground/90">
                          <strong>What to review:</strong> Check that all contact information is current, verify evacuation routes are still valid, 
                          update any changes in family members or medical information, and ensure supplies are still available.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        {/* Main Content */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <div className="mb-6">
            <TabsList className="flex w-full h-auto flex-wrap gap-2 p-2 rounded-xl bg-surface-muted/80 border border-border">
                <TabsTrigger
                  value="overview"
                  className="rounded-lg px-4 py-2 text-sm font-medium transition-all duration-200 data-[state=inactive]:text-foreground/80 data-[state=inactive]:hover:text-foreground data-[state=inactive]:hover:bg-primary/10 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-md"
                >
                  Overview
                </TabsTrigger>
                <TabsTrigger
                  value="home"
                  className="rounded-lg px-4 py-2 text-sm font-medium transition-all duration-200 data-[state=inactive]:text-foreground/80 data-[state=inactive]:hover:text-foreground data-[state=inactive]:hover:bg-primary/10 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-md"
                >
                  Home
                </TabsTrigger>
                <TabsTrigger
                  value="family"
                  className="rounded-lg px-4 py-2 text-sm font-medium transition-all duration-200 data-[state=inactive]:text-foreground/80 data-[state=inactive]:hover:text-foreground data-[state=inactive]:hover:bg-primary/10 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-md"
                >
                  Family
                </TabsTrigger>
                <TabsTrigger
                  value="pets"
                  className="rounded-lg px-4 py-2 text-sm font-medium transition-all duration-200 data-[state=inactive]:text-foreground/80 data-[state=inactive]:hover:text-foreground data-[state=inactive]:hover:bg-primary/10 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-md"
                >
                  Pets
                </TabsTrigger>
                <TabsTrigger
                  value="school-work"
                  className="rounded-lg px-4 py-2 text-sm font-medium transition-all duration-200 data-[state=inactive]:text-foreground/80 data-[state=inactive]:hover:text-foreground data-[state=inactive]:hover:bg-primary/10 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-md"
                >
                  School/Work
                </TabsTrigger>
                <TabsTrigger
                  value="evacuation"
                  className="rounded-lg px-4 py-2 text-sm font-medium transition-all duration-200 data-[state=inactive]:text-foreground/80 data-[state=inactive]:hover:text-foreground data-[state=inactive]:hover:bg-primary/10 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-md"
                >
                  Evacuation
                </TabsTrigger>
                <TabsTrigger
                  value="disasters"
                  className="rounded-lg px-4 py-2 text-sm font-medium transition-all duration-200 data-[state=inactive]:text-foreground/80 data-[state=inactive]:hover:text-foreground data-[state=inactive]:hover:bg-primary/10 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-md"
                >
                  Disasters
                </TabsTrigger>
                <TabsTrigger
                  value="communication"
                  className="rounded-lg px-4 py-2 text-sm font-medium transition-all duration-200 data-[state=inactive]:text-foreground/80 data-[state=inactive]:hover:text-foreground data-[state=inactive]:hover:bg-primary/10 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-md"
                >
                  Communication
                </TabsTrigger>
                <TabsTrigger
                  value="grab-and-go"
                  className="rounded-lg px-4 py-2 text-sm font-medium transition-all duration-200 data-[state=inactive]:text-foreground/80 data-[state=inactive]:hover:text-foreground data-[state=inactive]:hover:bg-primary/10 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-md"
                >
                  Grab-and-Go
                </TabsTrigger>
                <TabsTrigger
                  value="vehicle"
                  className="rounded-lg px-4 py-2 text-sm font-medium transition-all duration-200 data-[state=inactive]:text-foreground/80 data-[state=inactive]:hover:text-foreground data-[state=inactive]:hover:bg-primary/10 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-md"
                >
                  Vehicle
                </TabsTrigger>
                <TabsTrigger
                  value="go-no-go"
                  className="rounded-lg px-4 py-2 text-sm font-medium transition-all duration-200 data-[state=inactive]:text-foreground/80 data-[state=inactive]:hover:text-foreground data-[state=inactive]:hover:bg-primary/10 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-md"
                >
                  Go/No-Go
                </TabsTrigger>
                <TabsTrigger
                  value="first-hour"
                  className="rounded-lg px-4 py-2 text-sm font-medium transition-all duration-200 data-[state=inactive]:text-foreground/80 data-[state=inactive]:hover:text-foreground data-[state=inactive]:hover:bg-primary/10 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-md"
                >
                  First Hour
                </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="overview" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Welcome to Your Emergency Plan</CardTitle>
                <CardDescription>
                  This tool will help you create a comprehensive emergency preparedness plan. 
                  Import data from other tools to save time!
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {!(planData.import_sources?.emergency_contacts && planData.import_sources?.inventory) && (
                    <Card className="border-primary/30 bg-primary/10">
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-text-primary">
                          <Sparkles className="h-5 w-5 text-primary" />
                          Quick Start: Import from Other Tools
                        </CardTitle>
                        <CardDescription className="text-foreground/90">
                          Have you completed other preparedness tools? Import your data to auto-populate sections!
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <Button 
                          onClick={() => setShowImportModal(true)}
                          className="w-full bg-primary hover:opacity-90 text-primary-foreground"
                        >
                          <Upload className="h-4 w-4 mr-2" />
                          Import from Other Tools
                        </Button>
                      </CardContent>
                    </Card>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {completionStats.sections.map((section, index) => {
                      // Map section keys to tab values and icons
                      const sectionConfig = {
                        family_info: { tab: 'family', icon: Users, description: 'Add family members and their roles' },
                        evacuation_plans: { tab: 'evacuation', icon: MapPin, description: 'Plan evacuation routes and meeting points' },
                        disaster_plans: { tab: 'disasters', icon: AlertTriangle, description: 'Create disaster-specific action plans' },
                        communication_plan: { tab: 'communication', icon: MessageSquare, description: 'Set up communication procedures' },
                        home_info: { tab: 'home', icon: Home, description: 'Document home details and utility locations' },
                        pet_plans: { tab: 'pets', icon: PawPrint, description: 'Plan for pet safety and evacuation' },
                        grab_and_go: { tab: 'grab-and-go', icon: CheckCircle, description: 'Departure countdown (30/15/5 min) and grab list' },
                        vehicle_loading_plan: { tab: 'vehicle', icon: Car, description: 'Per vehicle or trailer: zones and what to pack where' },
                        go_no_go_criteria: { tab: 'go-no-go', icon: ListChecks, description: 'Triggers and criteria to decide leave vs stay' },
                        first_hour_protocol: { tab: 'first-hour', icon: Clock, description: 'First 60 minutes: steps to follow in an emergency' },
                        school_work_plans: { tab: 'school-work', icon: GraduationCap, description: 'Plan for school and work emergencies' }
                      };
                      
                      const config = sectionConfig[section.key] || { tab: 'overview', icon: FileText, description: 'Section details' };
                      const IconComponent = config.icon;
                      
                      return (
                        <Card 
                          key={section.key} 
                          className={`cursor-pointer transition-all duration-300 ease-in-out hover:shadow-xl hover:scale-[1.02] transform group ${
                            section.isComplete ? 'border-primary/50 bg-primary/10 hover:border-primary' : 'border-border hover:border-primary/50 hover:bg-primary/5'
                          } animate-fade-in-up`}
                          style={{ animationDelay: `${index * 50}ms` }}
                          onClick={() => {
                            setActiveTab(config.tab);
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                          }}
                        >
                          <CardContent className="pt-6">
                            <div className="flex items-start gap-3">
                              <div className={`p-2 rounded-lg transition-all duration-300 ${
                                section.isComplete ? 'bg-primary/20 text-primary group-hover:bg-primary/30 group-hover:scale-110' : 'bg-surface-muted text-text-muted group-hover:bg-primary/10 group-hover:text-primary group-hover:scale-110'
                              }`}>
                                <IconComponent className="h-5 w-5 transition-transform duration-300" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between mb-1">
                                  <h3 className="font-semibold text-text-primary">{section.name}</h3>
                                  {section.isComplete ? (
                                    <CheckCircle className="h-5 w-5 text-primary flex-shrink-0" />
                                  ) : (
                                    <div className="h-5 w-5 rounded-full border-2 border-border flex-shrink-0"></div>
                                  )}
                                </div>
                                <p className="text-sm text-foreground/90 line-clamp-2">{config.description}</p>
                              </div>
                            </div>
                            <div className="mt-3 pt-3 border-t border-border">
                              <span className="text-xs text-primary font-medium flex items-center gap-1 group-hover:gap-2 transition-all">
                                Click to edit 
                                <ArrowRight className="h-3 w-3 inline transition-transform group-hover:translate-x-1" />
                              </span>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="family">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Users className="h-5 w-5 text-primary" />
                      Family Information
                    </CardTitle>
                    <CardDescription>
                      Add family members and their roles in emergency situations
                    </CardDescription>
                  </div>
                  <Button
                    onClick={() => {
                        const newMember = {
                          id: `member_${Date.now()}`,
                          name: '',
                          date_of_birth: '',
                          blood_type: '',
                          role: '',
                          special_needs: '',
                          emergency_responsibilities: [],
                          allergies: [],
                          medications: [],
                          doctors: [],
                          conditions: [],
                          insurance: {
                            provider: '',
                            policy_number: '',
                            group_number: '',
                            dental_provider: '',
                            dental_policy_number: '',
                            dental_group_number: '',
                            vision_provider: '',
                            vision_policy_number: '',
                            vision_group_number: ''
                          }
                        };
                      setPlanData(prev => ({
                        ...prev,
                        family_info: {
                          ...prev.family_info,
                          members: [...prev.family_info.members, newMember]
                        }
                      }));
                    }}
                    className="flex items-center gap-2"
                  >
                    <Plus className="h-4 w-4" />
                    Add Member
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {planData.family_info.members.length === 0 ? (
                  <div className="text-center py-12 text-text-muted">
                    <Users className="h-12 w-12 mx-auto mb-4 text-text-muted" />
                    <p className="mb-4">No family members added yet</p>
                    <Button
                      onClick={() => {
                        const newMember = {
                          id: `member_${Date.now()}`,
                          name: '',
                          age: '',
                          date_of_birth: '',
                          blood_type: '',
                          role: '',
                          special_needs: '',
                          emergency_responsibilities: [],
                          allergies: [],
                          medications: [],
                          doctors: [],
                          conditions: [],
                          insurance: { provider: '', policy_number: '', group_number: '', dental_provider: '', dental_policy_number: '', dental_group_number: '', vision_provider: '', vision_policy_number: '', vision_group_number: '' }
                        };
                        setPlanData(prev => ({
                          ...prev,
                          family_info: {
                            ...prev.family_info,
                            members: [...prev.family_info.members, newMember]
                          }
                        }));
                      }}
                      className="flex items-center gap-2"
                    >
                      <Plus className="h-4 w-4" />
                      Add First Family Member
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {planData.family_info.members.map((member, index) => (
                      <Card key={member.id} className="border-2">
                        <CardHeader>
                          <div className="flex justify-between items-center">
                            <CardTitle className="text-lg">
                              {member.name || `Family Member ${index + 1}`}
                            </CardTitle>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setPlanData(prev => ({
                                  ...prev,
                                  family_info: {
                                    ...prev.family_info,
                                    members: prev.family_info.members.filter(m => m.id !== member.id)
                                  }
                                }));
                              }}
                              className="text-destructive hover:opacity-90"
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <Label htmlFor={`name-${member.id}`}>Full Name *</Label>
                              <Input
                                id={`name-${member.id}`}
                                value={member.name}
                                onChange={(e) => {
                                  const updated = planData.family_info.members.map(m =>
                                    m.id === member.id ? { ...m, name: e.target.value } : m
                                  );
                                  setPlanData(prev => ({
                                    ...prev,
                                    family_info: { ...prev.family_info, members: updated }
                                  }));
                                }}
                                placeholder="John Doe"
                              />
                            </div>
                            <div>
                              <Label htmlFor={`date-of-birth-${member.id}`}>Date of Birth *</Label>
                              <Input
                                id={`date-of-birth-${member.id}`}
                                type="date"
                                value={member.date_of_birth || ''}
                                onChange={(e) => {
                                  const updated = planData.family_info.members.map(m =>
                                    m.id === member.id ? { ...m, date_of_birth: e.target.value } : m
                                  );
                                  setPlanData(prev => ({
                                    ...prev,
                                    family_info: { ...prev.family_info, members: updated }
                                  }));
                                }}
                                max={new Date().toISOString().split('T')[0]}
                              />
                            </div>
                          </div>
                          <div>
                            <Label htmlFor={`blood-type-${member.id}`}>Blood type (for Medical Snapshot in binder)</Label>
                            <Select
                              value={member.blood_type || 'unknown'}
                              onValueChange={(value) => {
                                const updated = planData.family_info.members.map(m =>
                                  m.id === member.id ? { ...m, blood_type: value === 'unknown' ? '' : value } : m
                                );
                                setPlanData(prev => ({
                                  ...prev,
                                  family_info: { ...prev.family_info, members: updated }
                                }));
                              }}
                            >
                              <SelectTrigger id={`blood-type-${member.id}`}>
                                <SelectValue placeholder="Optional" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="unknown">Not specified</SelectItem>
                                <SelectItem value="A+">A+</SelectItem>
                                <SelectItem value="A-">A-</SelectItem>
                                <SelectItem value="B+">B+</SelectItem>
                                <SelectItem value="B-">B-</SelectItem>
                                <SelectItem value="AB+">AB+</SelectItem>
                                <SelectItem value="AB-">AB-</SelectItem>
                                <SelectItem value="O+">O+</SelectItem>
                                <SelectItem value="O-">O-</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label htmlFor={`role-${member.id}`}>Emergency Role</Label>
                            <Select
                              value={(() => {
                                const predefinedRoles = ['evacuation_leader', 'communication_coordinator', 'first_aid_specialist', 'pet_care_coordinator', 'supply_manager', 'document_keeper', 'none'];
                                if (!member.role) return '';
                                return predefinedRoles.includes(member.role) ? member.role : 'custom';
                              })()}
                              onValueChange={(value) => {
                                if (value === 'custom') {
                                  // Set to empty string so user can type
                                  const updated = planData.family_info.members.map(m =>
                                    m.id === member.id ? { ...m, role: '' } : m
                                  );
                                  setPlanData(prev => ({
                                    ...prev,
                                    family_info: { ...prev.family_info, members: updated }
                                  }));
                                } else {
                                  const updated = planData.family_info.members.map(m =>
                                    m.id === member.id ? { ...m, role: value } : m
                                  );
                                  setPlanData(prev => ({
                                    ...prev,
                                    family_info: { ...prev.family_info, members: updated }
                                  }));
                                }
                              }}
                            >
                              <SelectTrigger id={`role-${member.id}`}>
                                <SelectValue placeholder="Select a role" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="evacuation_leader">Evacuation Leader</SelectItem>
                                <SelectItem value="communication_coordinator">Communication Coordinator</SelectItem>
                                <SelectItem value="first_aid_specialist">First Aid Specialist</SelectItem>
                                <SelectItem value="pet_care_coordinator">Pet Care Coordinator</SelectItem>
                                <SelectItem value="supply_manager">Supply Manager</SelectItem>
                                <SelectItem value="document_keeper">Document Keeper</SelectItem>
                                <SelectItem value="none">No Specific Role</SelectItem>
                                <SelectItem value="custom">Other (Custom Role)</SelectItem>
                              </SelectContent>
                            </Select>
                            {(() => {
                              const predefinedRoles = ['evacuation_leader', 'communication_coordinator', 'first_aid_specialist', 'pet_care_coordinator', 'supply_manager', 'document_keeper', 'none'];
                              // Show input if role is not in predefined list (including empty string when custom is selected)
                              const shouldShowCustomInput = !member.role || !predefinedRoles.includes(member.role);
                              
                              return shouldShowCustomInput ? (
                                <Input
                                  id={`custom-role-${member.id}`}
                                  className="mt-2"
                                  value={member.role || ''}
                                  onChange={(e) => {
                                    const updated = planData.family_info.members.map(m =>
                                      m.id === member.id ? { ...m, role: e.target.value } : m
                                    );
                                    setPlanData(prev => ({
                                      ...prev,
                                      family_info: { ...prev.family_info, members: updated }
                                    }));
                                  }}
                                  placeholder="Enter custom emergency role"
                                  autoFocus={!member.role}
                                />
                              ) : null;
                            })()}
                          </div>
                          <div>
                            <Label htmlFor={`special_needs-${member.id}`}>Special Needs or Considerations</Label>
                            <Textarea
                              id={`special_needs-${member.id}`}
                              value={member.special_needs || ''}
                              onChange={(e) => {
                                const updated = planData.family_info.members.map(m =>
                                  m.id === member.id ? { ...m, special_needs: e.target.value } : m
                                );
                                setPlanData(prev => ({
                                  ...prev,
                                  family_info: { ...prev.family_info, members: updated }
                                }));
                              }}
                              placeholder="Mobility assistance needed, medication requirements, etc."
                              rows={3}
                            />
                          </div>

                          {/* Medical Information Section */}
                          <div className="border-t pt-4 mt-4">
                            <div className="flex items-center gap-2 mb-4">
                              <Heart className="h-5 w-5 text-primary" />
                              <Label className="text-lg font-semibold">Medical Information</Label>
                            </div>

                            {/* Allergies */}
                            <div className="mb-4">
                              <div className="flex justify-between items-center mb-2">
                                <Label>Allergies</Label>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    const newAllergy = {
                                      id: `allergy_${Date.now()}_${member.id}`,
                                      allergen: '',
                                      severity: '',
                                      notes: ''
                                    };
                                    const updated = planData.family_info.members.map(m =>
                                      m.id === member.id ? { ...m, allergies: [...(m.allergies || []), newAllergy] } : m
                                    );
                                    setPlanData(prev => ({
                                      ...prev,
                                      family_info: { ...prev.family_info, members: updated }
                                    }));
                                  }}
                                  className="flex items-center gap-1"
                                >
                                  <Plus className="h-3 w-3" />
                                  Add Allergy
                                </Button>
                              </div>
                              {(!member.allergies || member.allergies.length === 0) ? (
                                <p className="text-sm text-text-muted italic">No allergies recorded</p>
                              ) : (
                                <div className="space-y-2">
                                  {member.allergies.map((allergy) => (
                                    <Card key={allergy.id} className="p-3">
                                      <div className="flex justify-end mb-2">
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          onClick={() => {
                                            const updated = planData.family_info.members.map(m =>
                                              m.id === member.id ? { ...m, allergies: (m.allergies || []).filter(a => a.id !== allergy.id) } : m
                                            );
                                            setPlanData(prev => ({
                                              ...prev,
                                              family_info: { ...prev.family_info, members: updated }
                                            }));
                                          }}
                                          className="h-6 w-6 p-0 text-destructive"
                                        >
                                          <X className="h-4 w-4" />
                                        </Button>
                                      </div>
                                      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                                        <Input
                                          value={allergy.allergen || ''}
                                          onChange={(e) => {
                                            const updated = planData.family_info.members.map(m =>
                                              m.id === member.id ? { ...m, allergies: (m.allergies || []).map(a => a.id === allergy.id ? { ...a, allergen: e.target.value } : a) } : m
                                            );
                                            setPlanData(prev => ({
                                              ...prev,
                                              family_info: { ...prev.family_info, members: updated }
                                            }));
                                          }}
                                          placeholder="Allergen (e.g., Peanuts, Penicillin)"
                                        />
                                        <Input
                                          value={allergy.severity || ''}
                                          onChange={(e) => {
                                            const updated = planData.family_info.members.map(m =>
                                              m.id === member.id ? { ...m, allergies: (m.allergies || []).map(a => a.id === allergy.id ? { ...a, severity: e.target.value } : a) } : m
                                            );
                                            setPlanData(prev => ({
                                              ...prev,
                                              family_info: { ...prev.family_info, members: updated }
                                            }));
                                          }}
                                          placeholder="Severity (Mild, Moderate, Severe)"
                                        />
                                        <Input
                                          value={allergy.notes || ''}
                                          onChange={(e) => {
                                            const updated = planData.family_info.members.map(m =>
                                              m.id === member.id ? { ...m, allergies: (m.allergies || []).map(a => a.id === allergy.id ? { ...a, notes: e.target.value } : a) } : m
                                            );
                                            setPlanData(prev => ({
                                              ...prev,
                                              family_info: { ...prev.family_info, members: updated }
                                            }));
                                          }}
                                          placeholder="Treatment notes"
                                        />
                                      </div>
                                    </Card>
                                  ))}
                                </div>
                              )}
                            </div>

                            {/* Medications */}
                            <div className="mb-4">
                              <div className="flex justify-between items-center mb-2">
                                <Label>Medications</Label>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    const newMed = {
                                      id: `med_${Date.now()}_${member.id}`,
                                      medication: '',
                                      dosage: '',
                                      frequency: '',
                                      prescribing_doctor: ''
                                    };
                                    const updated = planData.family_info.members.map(m =>
                                      m.id === member.id ? { ...m, medications: [...(m.medications || []), newMed] } : m
                                    );
                                    setPlanData(prev => ({
                                      ...prev,
                                      family_info: { ...prev.family_info, members: updated }
                                    }));
                                  }}
                                  className="flex items-center gap-1"
                                >
                                  <Plus className="h-3 w-3" />
                                  Add Medication
                                </Button>
                              </div>
                              {(!member.medications || member.medications.length === 0) ? (
                                <p className="text-sm text-text-muted italic">No medications recorded</p>
                              ) : (
                                <div className="space-y-2">
                                  {member.medications.map((med) => (
                                    <Card key={med.id} className="p-3">
                                      <div className="flex justify-end mb-2">
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          onClick={() => {
                                            const updated = planData.family_info.members.map(m =>
                                              m.id === member.id ? { ...m, medications: (m.medications || []).filter(m => m.id !== med.id) } : m
                                            );
                                            setPlanData(prev => ({
                                              ...prev,
                                              family_info: { ...prev.family_info, members: updated }
                                            }));
                                          }}
                                          className="h-6 w-6 p-0 text-destructive"
                                        >
                                          <X className="h-4 w-4" />
                                        </Button>
                                      </div>
                                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                        <Input
                                          value={med.medication || ''}
                                          onChange={(e) => {
                                            const updated = planData.family_info.members.map(m =>
                                              m.id === member.id ? { ...m, medications: (m.medications || []).map(m => m.id === med.id ? { ...m, medication: e.target.value } : m) } : m
                                            );
                                            setPlanData(prev => ({
                                              ...prev,
                                              family_info: { ...prev.family_info, members: updated }
                                            }));
                                          }}
                                          placeholder="Medication name"
                                        />
                                        <Input
                                          value={med.dosage || ''}
                                          onChange={(e) => {
                                            const updated = planData.family_info.members.map(m =>
                                              m.id === member.id ? { ...m, medications: (m.medications || []).map(m => m.id === med.id ? { ...m, dosage: e.target.value } : m) } : m
                                            );
                                            setPlanData(prev => ({
                                              ...prev,
                                              family_info: { ...prev.family_info, members: updated }
                                            }));
                                          }}
                                          placeholder="Dosage"
                                        />
                                        <Input
                                          value={med.frequency || ''}
                                          onChange={(e) => {
                                            const updated = planData.family_info.members.map(m =>
                                              m.id === member.id ? { ...m, medications: (m.medications || []).map(m => m.id === med.id ? { ...m, frequency: e.target.value } : m) } : m
                                            );
                                            setPlanData(prev => ({
                                              ...prev,
                                              family_info: { ...prev.family_info, members: updated }
                                            }));
                                          }}
                                          placeholder="Frequency (e.g., Twice daily)"
                                        />
                                        <Input
                                          value={med.prescribing_doctor || ''}
                                          onChange={(e) => {
                                            const updated = planData.family_info.members.map(m =>
                                              m.id === member.id ? { ...m, medications: (m.medications || []).map(m => m.id === med.id ? { ...m, prescribing_doctor: e.target.value } : m) } : m
                                            );
                                            setPlanData(prev => ({
                                              ...prev,
                                              family_info: { ...prev.family_info, members: updated }
                                            }));
                                          }}
                                          placeholder="Prescribing doctor"
                                        />
                                      </div>
                                    </Card>
                                  ))}
                                </div>
                              )}
                            </div>

                            {/* Insurance */}
                            <div className="mb-4">
                              <Label className="mb-2 block">Medical Insurance</Label>
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-4">
                                <Input
                                  value={member.insurance?.provider || ''}
                                  onChange={(e) => {
                                    const updated = planData.family_info.members.map(m =>
                                      m.id === member.id ? { ...m, insurance: { ...(m.insurance || {}), provider: e.target.value } } : m
                                    );
                                    setPlanData(prev => ({
                                      ...prev,
                                      family_info: { ...prev.family_info, members: updated }
                                    }));
                                  }}
                                  placeholder="Medical Insurance Provider"
                                />
                                <Input
                                  value={member.insurance?.policy_number || ''}
                                  onChange={(e) => {
                                    const updated = planData.family_info.members.map(m =>
                                      m.id === member.id ? { ...m, insurance: { ...(m.insurance || {}), policy_number: e.target.value } } : m
                                    );
                                    setPlanData(prev => ({
                                      ...prev,
                                      family_info: { ...prev.family_info, members: updated }
                                    }));
                                  }}
                                  placeholder="Medical Policy Number"
                                />
                                <Input
                                  value={member.insurance?.group_number || ''}
                                  onChange={(e) => {
                                    const updated = planData.family_info.members.map(m =>
                                      m.id === member.id ? { ...m, insurance: { ...(m.insurance || {}), group_number: e.target.value } } : m
                                    );
                                    setPlanData(prev => ({
                                      ...prev,
                                      family_info: { ...prev.family_info, members: updated }
                                    }));
                                  }}
                                  placeholder="Medical Group Number"
                                />
                              </div>

                              <Label className="mb-2 block">Dental Insurance</Label>
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-4">
                                <Input
                                  value={member.insurance?.dental_provider || ''}
                                  onChange={(e) => {
                                    const updated = planData.family_info.members.map(m =>
                                      m.id === member.id ? { ...m, insurance: { ...(m.insurance || {}), dental_provider: e.target.value } } : m
                                    );
                                    setPlanData(prev => ({
                                      ...prev,
                                      family_info: { ...prev.family_info, members: updated }
                                    }));
                                  }}
                                  placeholder="Dental Insurance Provider"
                                />
                                <Input
                                  value={member.insurance?.dental_policy_number || ''}
                                  onChange={(e) => {
                                    const updated = planData.family_info.members.map(m =>
                                      m.id === member.id ? { ...m, insurance: { ...(m.insurance || {}), dental_policy_number: e.target.value } } : m
                                    );
                                    setPlanData(prev => ({
                                      ...prev,
                                      family_info: { ...prev.family_info, members: updated }
                                    }));
                                  }}
                                  placeholder="Dental Policy Number"
                                />
                                <Input
                                  value={member.insurance?.dental_group_number || ''}
                                  onChange={(e) => {
                                    const updated = planData.family_info.members.map(m =>
                                      m.id === member.id ? { ...m, insurance: { ...(m.insurance || {}), dental_group_number: e.target.value } } : m
                                    );
                                    setPlanData(prev => ({
                                      ...prev,
                                      family_info: { ...prev.family_info, members: updated }
                                    }));
                                  }}
                                  placeholder="Dental Group Number"
                                />
                              </div>

                              <Label className="mb-2 block">Vision Insurance</Label>
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                                <Input
                                  value={member.insurance?.vision_provider || ''}
                                  onChange={(e) => {
                                    const updated = planData.family_info.members.map(m =>
                                      m.id === member.id ? { ...m, insurance: { ...(m.insurance || {}), vision_provider: e.target.value } } : m
                                    );
                                    setPlanData(prev => ({
                                      ...prev,
                                      family_info: { ...prev.family_info, members: updated }
                                    }));
                                  }}
                                  placeholder="Vision Insurance Provider"
                                />
                                <Input
                                  value={member.insurance?.vision_policy_number || ''}
                                  onChange={(e) => {
                                    const updated = planData.family_info.members.map(m =>
                                      m.id === member.id ? { ...m, insurance: { ...(m.insurance || {}), vision_policy_number: e.target.value } } : m
                                    );
                                    setPlanData(prev => ({
                                      ...prev,
                                      family_info: { ...prev.family_info, members: updated }
                                    }));
                                  }}
                                  placeholder="Vision Policy Number"
                                />
                                <Input
                                  value={member.insurance?.vision_group_number || ''}
                                  onChange={(e) => {
                                    const updated = planData.family_info.members.map(m =>
                                      m.id === member.id ? { ...m, insurance: { ...(m.insurance || {}), vision_group_number: e.target.value } } : m
                                    );
                                    setPlanData(prev => ({
                                      ...prev,
                                      family_info: { ...prev.family_info, members: updated }
                                    }));
                                  }}
                                  placeholder="Vision Group Number"
                                />
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="mt-6">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Heart className="h-5 w-5 text-primary" />
                  Household medical (for binder)
                </CardTitle>
                <CardDescription>
                  Advance directive, medical power of attorney, immunizations, and significant medical history. These appear in your printed binder.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Advance directive</Label>
                    <div className="flex gap-2 mt-1">
                      <Select
                        value={planData.medical_info?.advance_directive?.has ? 'yes' : 'no'}
                        onValueChange={(v) => setPlanData(prev => ({
                          ...prev,
                          medical_info: {
                            ...prev.medical_info,
                            advance_directive: { ...(prev.medical_info?.advance_directive || {}), has: v === 'yes' }
                          }
                        }))}
                      >
                        <SelectTrigger><SelectValue placeholder="On file?" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="no">No</SelectItem>
                          <SelectItem value="yes">Yes</SelectItem>
                        </SelectContent>
                      </Select>
                      <Input
                        value={planData.medical_info?.advance_directive?.location ?? ''}
                        onChange={(e) => setPlanData(prev => ({
                          ...prev,
                          medical_info: {
                            ...prev.medical_info,
                            advance_directive: { ...(prev.medical_info?.advance_directive || {}), location: e.target.value }
                          }
                        }))}
                        placeholder="Location (e.g. safe, attorney)"
                        className="flex-1"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Medical power of attorney</Label>
                    <div className="flex gap-2 mt-1">
                      <Select
                        value={planData.medical_info?.medical_power_of_attorney?.has ? 'yes' : 'no'}
                        onValueChange={(v) => setPlanData(prev => ({
                          ...prev,
                          medical_info: {
                            ...prev.medical_info,
                            medical_power_of_attorney: { ...(prev.medical_info?.medical_power_of_attorney || {}), has: v === 'yes' }
                          }
                        }))}
                      >
                        <SelectTrigger><SelectValue placeholder="On file?" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="no">No</SelectItem>
                          <SelectItem value="yes">Yes</SelectItem>
                        </SelectContent>
                      </Select>
                      <Input
                        value={planData.medical_info?.medical_power_of_attorney?.location ?? ''}
                        onChange={(e) => setPlanData(prev => ({
                          ...prev,
                          medical_info: {
                            ...prev.medical_info,
                            medical_power_of_attorney: { ...(prev.medical_info?.medical_power_of_attorney || {}), location: e.target.value }
                          }
                        }))}
                        placeholder="Location"
                        className="flex-1"
                      />
                    </div>
                  </div>
                </div>
                <div>
                  <Label>Immunization records (kept at)</Label>
                  <Input
                    value={planData.medical_info?.immunization_records_location ?? ''}
                    onChange={(e) => setPlanData(prev => ({
                      ...prev,
                      medical_info: {
                        ...prev.medical_info,
                        immunization_records_location: e.target.value
                      }
                    }))}
                    placeholder="e.g. doctor's office, filing cabinet"
                  />
                </div>
                <div>
                  <Label>Significant medical history</Label>
                  <Textarea
                    value={planData.medical_info?.significant_medical_history ?? ''}
                    onChange={(e) => setPlanData(prev => ({
                      ...prev,
                      medical_info: {
                        ...prev.medical_info,
                        significant_medical_history: e.target.value
                      }
                    }))}
                    placeholder="Brief summary of serious conditions, surgeries, or ongoing care (for binder only)"
                    rows={3}
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="evacuation">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MapPin className="h-5 w-5 text-primary" />
                  Evacuation Plans
                </CardTitle>
                <CardDescription>
                  Plan your evacuation routes and meeting points
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex justify-between items-center">
                  <Label>Evacuation Plans</Label>
                  <Button
                    variant="default"
                    size="sm"
                    onClick={() => {
                      const newPlan = {
                        id: `evac_plan_${Date.now()}`,
                        name: '',
                        primary_route: '',
                        secondary_route: '',
                        meeting_points: {
                          primary: '',
                          secondary: '',
                          tertiary: ''
                        },
                        alternative_locations: [],
                        notes: '',
                        map_image: null
                      };
                      setPlanData(prev => ({
                        ...prev,
                        evacuation_plans: {
                          ...prev.evacuation_plans,
                          plans: [...(prev.evacuation_plans?.plans || []), newPlan]
                        }
                      }));
                    }}
                    className="flex items-center gap-2 bg-primary hover:opacity-90 text-primary-foreground"
                  >
                    <Plus className="h-4 w-4" />
                    Add Evacuation Plan
                  </Button>
                </div>
                {(!planData.evacuation_plans?.plans || planData.evacuation_plans.plans.length === 0) ? (
                  <div className="text-center py-12 text-text-muted">
                    <MapPin className="h-12 w-12 mx-auto mb-4 text-text-muted" />
                    <p className="mb-4">No evacuation plans created yet</p>
                    <Button
                      variant="default"
                      onClick={() => {
                        const newPlan = {
                          id: `evac_plan_${Date.now()}`,
                          name: '',
                          primary_route: '',
                          secondary_route: '',
                          meeting_points: {
                            primary: '',
                            secondary: '',
                            tertiary: ''
                          },
                          alternative_locations: [],
                          notes: ''
                        };
                        setPlanData(prev => ({
                          ...prev,
                          evacuation_plans: {
                            ...prev.evacuation_plans,
                            plans: [...(prev.evacuation_plans?.plans || []), newPlan]
                          }
                        }));
                      }}
                      className="flex items-center gap-2 bg-primary hover:opacity-90 text-primary-foreground"
                    >
                      <Plus className="h-4 w-4" />
                      Create First Evacuation Plan
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {(planData.evacuation_plans?.plans || []).map((plan) => (
                      <Card key={plan.id} className="border-2">
                        <CardHeader>
                          <div className="flex justify-between items-center">
                            <div className="flex-1">
                              <Input
                                value={plan.name || ''}
                                onChange={(e) => {
                                  const updated = (planData.evacuation_plans?.plans || []).map(p =>
                                    p.id === plan.id ? { ...p, name: e.target.value } : p
                                  );
                                  setPlanData(prev => ({
                                    ...prev,
                                    evacuation_plans: { ...prev.evacuation_plans, plans: updated }
                                  }));
                                }}
                                placeholder="Plan Name (e.g., Home Evacuation, Work Evacuation)"
                                className="text-lg font-semibold border-0 border-b-2 border-border focus:border-primary rounded-none px-0"
                              />
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setPlanData(prev => ({
                                  ...prev,
                                  evacuation_plans: {
                                    ...prev.evacuation_plans,
                                    plans: (prev.evacuation_plans?.plans || []).filter(p => p.id !== plan.id)
                                  }
                                }));
                              }}
                              className="text-destructive hover:opacity-90"
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          <div>
                            <Label htmlFor={`primary-route-${plan.id}`}>Primary Evacuation Route</Label>
                            <Textarea
                              id={`primary-route-${plan.id}`}
                              value={plan.primary_route || ''}
                                onChange={(e) => {
                                  const updated = (planData.evacuation_plans?.plans || []).map(p =>
                                    p.id === plan.id ? { ...p, primary_route: e.target.value } : p
                                  );
                                setPlanData(prev => ({
                                  ...prev,
                                  evacuation_plans: { ...prev.evacuation_plans, plans: updated }
                                }));
                              }}
                              placeholder="Describe your primary evacuation route (e.g., Main St to Highway 101, then north to City Park)"
                              rows={3}
                            />
                          </div>
                          <div>
                            <Label htmlFor={`secondary-route-${plan.id}`}>Secondary Evacuation Route</Label>
                            <Textarea
                              id={`secondary-route-${plan.id}`}
                              value={plan.secondary_route || ''}
                                onChange={(e) => {
                                  const updated = (planData.evacuation_plans?.plans || []).map(p =>
                                    p.id === plan.id ? { ...p, secondary_route: e.target.value } : p
                                  );
                                setPlanData(prev => ({
                                  ...prev,
                                  evacuation_plans: { ...prev.evacuation_plans, plans: updated }
                                }));
                              }}
                              placeholder="Describe your backup evacuation route in case the primary route is blocked"
                              rows={3}
                            />
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                              <Label htmlFor={`meeting-primary-${plan.id}`}>Primary Meeting Point</Label>
                              <Input
                                id={`meeting-primary-${plan.id}`}
                                value={plan.meeting_points?.primary || ''}
                                onChange={(e) => {
                                  const updated = (planData.evacuation_plans?.plans || []).map(p =>
                                    p.id === plan.id ? { ...p, meeting_points: { ...(p.meeting_points || {}), primary: e.target.value } } : p
                                  );
                                  setPlanData(prev => ({
                                    ...prev,
                                    evacuation_plans: { ...prev.evacuation_plans, plans: updated }
                                  }));
                                }}
                                placeholder="First priority meeting location"
                              />
                            </div>
                            <div>
                              <Label htmlFor={`meeting-secondary-${plan.id}`}>Secondary Meeting Point</Label>
                              <Input
                                id={`meeting-secondary-${plan.id}`}
                                value={plan.meeting_points?.secondary || ''}
                                onChange={(e) => {
                                  const updated = (planData.evacuation_plans?.plans || []).map(p =>
                                    p.id === plan.id ? { ...p, meeting_points: { ...(p.meeting_points || {}), secondary: e.target.value } } : p
                                  );
                                  setPlanData(prev => ({
                                    ...prev,
                                    evacuation_plans: { ...prev.evacuation_plans, plans: updated }
                                  }));
                                }}
                                placeholder="Second priority meeting location"
                              />
                            </div>
                            <div>
                              <Label htmlFor={`meeting-tertiary-${plan.id}`}>Tertiary Meeting Point</Label>
                              <Input
                                id={`meeting-tertiary-${plan.id}`}
                                value={plan.meeting_points?.tertiary || ''}
                                onChange={(e) => {
                                  const updated = (planData.evacuation_plans?.plans || []).map(p =>
                                    p.id === plan.id ? { ...p, meeting_points: { ...(p.meeting_points || {}), tertiary: e.target.value } } : p
                                  );
                                  setPlanData(prev => ({
                                    ...prev,
                                    evacuation_plans: { ...prev.evacuation_plans, plans: updated }
                                  }));
                                }}
                                placeholder="Third priority meeting location"
                              />
                            </div>
                          </div>
                          <div>
                            <div className="flex justify-between items-center mb-2">
                              <Label>Alternative Locations</Label>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  const newLocation = {
                                    id: `alt_${Date.now()}_${plan.id}`,
                                    name: '',
                                    address: '',
                                    phone: '',
                                    notes: ''
                                  };
                                  const updated = (planData.evacuation_plans?.plans || []).map(p =>
                                    p.id === plan.id ? { ...p, alternative_locations: [...(p.alternative_locations || []), newLocation] } : p
                                  );
                                  setPlanData(prev => ({
                                    ...prev,
                                    evacuation_plans: { ...prev.evacuation_plans, plans: updated }
                                  }));
                                }}
                                className="flex items-center gap-1"
                              >
                                <Plus className="h-3 w-3" />
                                Add Location
                              </Button>
                            </div>
                            {(!plan.alternative_locations || plan.alternative_locations.length === 0) ? (
                              <p className="text-sm text-text-muted italic">No alternative locations added yet</p>
                            ) : (
                              <div className="space-y-3">
                                {plan.alternative_locations.map((location) => (
                                  <Card key={location.id} className="p-4">
                                    <div className="flex justify-end mb-2">
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => {
                                          const updated = (planData.evacuation_plans?.plans || []).map(p =>
                                            p.id === plan.id ? { ...p, alternative_locations: (p.alternative_locations || []).filter(l => l.id !== location.id) } : p
                                          );
                                          setPlanData(prev => ({
                                            ...prev,
                                            evacuation_plans: { ...prev.evacuation_plans, plans: updated }
                                          }));
                                        }}
                                        className="h-6 w-6 p-0 text-destructive"
                                      >
                                        <X className="h-4 w-4" />
                                      </Button>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                      <div>
                                        <Label htmlFor={`alt-name-${location.id}`}>Location Name</Label>
                                        <Input
                                          id={`alt-name-${location.id}`}
                                          value={location.name || ''}
                                          onChange={(e) => {
                                            const updated = (planData.evacuation_plans?.plans || []).map(p =>
                                              p.id === plan.id ? { ...p, alternative_locations: (p.alternative_locations || []).map(l => l.id === location.id ? { ...l, name: e.target.value } : l) } : p
                                            );
                                            setPlanData(prev => ({
                                              ...prev,
                                              evacuation_plans: { ...prev.evacuation_plans, plans: updated }
                                            }));
                                          }}
                                          placeholder="Friend's house, Hotel, etc."
                                        />
                                      </div>
                                      <div>
                                        <Label htmlFor={`alt-address-${location.id}`}>Address</Label>
                                        <Input
                                          id={`alt-address-${location.id}`}
                                          value={location.address || ''}
                                          onChange={(e) => {
                                            const updated = (planData.evacuation_plans?.plans || []).map(p =>
                                              p.id === plan.id ? { ...p, alternative_locations: (p.alternative_locations || []).map(l => l.id === location.id ? { ...l, address: e.target.value } : l) } : p
                                            );
                                            setPlanData(prev => ({
                                              ...prev,
                                              evacuation_plans: { ...prev.evacuation_plans, plans: updated }
                                            }));
                                          }}
                                          placeholder="Full address"
                                        />
                                      </div>
                                      <div>
                                        <Label htmlFor={`alt-phone-${location.id}`}>Phone Number</Label>
                                        <Input
                                          id={`alt-phone-${location.id}`}
                                          value={location.phone || ''}
                                          onChange={(e) => {
                                            const updated = (planData.evacuation_plans?.plans || []).map(p =>
                                              p.id === plan.id ? { ...p, alternative_locations: (p.alternative_locations || []).map(l => l.id === location.id ? { ...l, phone: e.target.value } : l) } : p
                                            );
                                            setPlanData(prev => ({
                                              ...prev,
                                              evacuation_plans: { ...prev.evacuation_plans, plans: updated }
                                            }));
                                          }}
                                          placeholder="(555) 123-4567"
                                        />
                                      </div>
                                      <div>
                                        <Label htmlFor={`alt-notes-${location.id}`}>Notes</Label>
                                        <Input
                                          id={`alt-notes-${location.id}`}
                                          value={location.notes || ''}
                                          onChange={(e) => {
                                            const updated = (planData.evacuation_plans?.plans || []).map(p =>
                                              p.id === plan.id ? { ...p, alternative_locations: (p.alternative_locations || []).map(l => l.id === location.id ? { ...l, notes: e.target.value } : l) } : p
                                            );
                                            setPlanData(prev => ({
                                              ...prev,
                                              evacuation_plans: { ...prev.evacuation_plans, plans: updated }
                                            }));
                                          }}
                                          placeholder="Special instructions"
                                        />
                                      </div>
                                    </div>
                                  </Card>
                                ))}
                              </div>
                            )}
                          </div>
                          <div>
                            <Label htmlFor={`plan-notes-${plan.id}`}>Additional Notes</Label>
                            <Textarea
                              id={`plan-notes-${plan.id}`}
                              value={plan.notes || ''}
                              onChange={(e) => {
                                const updated = (planData.evacuation_plans?.plans || []).map(p =>
                                  p.id === plan.id ? { ...p, notes: e.target.value } : p
                                );
                                setPlanData(prev => ({
                                  ...prev,
                                  evacuation_plans: { ...prev.evacuation_plans, plans: updated }
                                }));
                              }}
                              placeholder="Any additional notes or instructions for this evacuation plan"
                              rows={3}
                            />
                          </div>
                          <div className="pt-4 border-t">
                            <Button
                              variant="outline"
                              onClick={() => {
                                setEditingPlanId(plan.id);
                                setShowMapEditor(true);
                              }}
                              className="w-full flex items-center justify-center gap-2"
                            >
                              <Map className="h-4 w-4" />
                              {plan.map_image ? 'Edit Evacuation Map' : 'Create Evacuation Map'}
                            </Button>
                            {plan.map_image && (
                              <div className="mt-4">
                                <MapPreview mapData={plan.map_image} />
                              </div>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="disasters">
            <Card className="mb-4">
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <AlertTriangle className="h-5 w-5 text-primary" />
                      Disaster Plans
                    </CardTitle>
                    <CardDescription>
                      Create action plans for specific disasters. Add plans based on risks identified in your Location Risk Assessment.
                    </CardDescription>
                  </div>
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button className="flex items-center gap-2">
                        <Plus className="h-4 w-4" />
                        Add Disaster Plan
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto bg-white" style={{ backgroundColor: 'white' }}>
                      <DialogHeader className="bg-white">
                        <DialogTitle>Add Disaster Plan</DialogTitle>
                        <DialogDescription>
                          Select from all available emergency types or create a custom plan. You currently have {Object.keys(planData.disaster_plans).length} plan{Object.keys(planData.disaster_plans).length !== 1 ? 's' : ''}.
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-2 mt-4 bg-white">
                        {/* Create Custom Plan Button */}
                        <Button
                          variant="outline"
                          className="w-full justify-start h-auto p-3 bg-primary/10 border-primary/30 hover:bg-primary/20"
                          onClick={() => setShowCustomPlanDialog(true)}
                        >
                          <Plus className="h-4 w-4 mr-3" />
                          <div className="text-left flex-1">
                            <div className="font-semibold text-text-primary">Create Custom Disaster Plan</div>
                            <div className="text-xs text-foreground/90">Add a plan for a specific emergency not in the list</div>
                          </div>
                        </Button>
                        
                        {/* Custom Plan Dialog */}
                        <Dialog open={showCustomPlanDialog} onOpenChange={setShowCustomPlanDialog}>
                          <DialogContent className="max-w-md bg-white" style={{ backgroundColor: 'white' }}>
                            <DialogHeader className="bg-white">
                              <DialogTitle>Create Custom Disaster Plan</DialogTitle>
                              <DialogDescription>
                                Create a custom disaster plan with your own name, icon, and description.
                              </DialogDescription>
                            </DialogHeader>
                            <div className="space-y-4 mt-4 bg-white">
                              <div>
                                <Label htmlFor="custom-plan-name">Plan Name *</Label>
                                <Input
                                  id="custom-plan-name"
                                  placeholder="e.g., Local Chemical Plant Incident"
                                  value={customPlanForm.name}
                                  onChange={(e) => setCustomPlanForm(prev => ({ ...prev, name: e.target.value }))}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' && customPlanForm.name.trim()) {
                                      handleCreateCustomPlan();
                                    }
                                  }}
                                />
                              </div>
                              <div>
                                <Label htmlFor="custom-plan-icon">Icon (Emoji) *</Label>
                                <Input
                                  id="custom-plan-icon"
                                  placeholder="🔥"
                                  maxLength={2}
                                  value={customPlanForm.icon}
                                  onChange={(e) => setCustomPlanForm(prev => ({ ...prev, icon: e.target.value || '⚠️' }))}
                                />
                                <p className="text-xs text-text-muted mt-1">Enter an emoji (1-2 characters)</p>
                              </div>
                              <div>
                                <Label htmlFor="custom-plan-description">Description</Label>
                                <Textarea
                                  id="custom-plan-description"
                                  placeholder="Brief description of this emergency type"
                                  rows={2}
                                  value={customPlanForm.description}
                                  onChange={(e) => setCustomPlanForm(prev => ({ ...prev, description: e.target.value }))}
                                />
                              </div>
                              <div className="flex gap-2">
                                <Button
                                  variant="outline"
                                  className="flex-1"
                                  onClick={() => {
                                    setShowCustomPlanDialog(false);
                                    setCustomPlanForm({ name: '', icon: '⚠️', description: '' });
                                  }}
                                >
                                  Cancel
                                </Button>
                                <Button
                                  className="flex-1 bg-primary hover:opacity-90 text-primary-foreground"
                                  onClick={handleCreateCustomPlan}
                                >
                                  Create Plan
                                </Button>
                              </div>
                            </div>
                          </DialogContent>
                        </Dialog>
                        
                        <div className="border-t my-3"></div>
                        
                        {/* Predefined Emergency Types */}
                        <div className="text-sm font-semibold text-text-primary mb-2">Predefined Emergency Types:</div>
                        {Object.entries(EMERGENCY_TYPES_MAP)
                          .filter(([key]) => !planData.disaster_plans[key]) // Only show ones not already added
                          .map(([key, info]) => (
                            <Button
                              key={key}
                              variant="outline"
                              className="w-full justify-start h-auto p-3 bg-card hover:bg-surface-muted border-border"
                              style={{ backgroundColor: 'white' }}
                              onClick={() => {
                                setPlanData(prev => ({
                                  ...prev,
                                  disaster_plans: {
                                    ...prev.disaster_plans,
                                    [key]: { completed: false, steps: [] }
                                  }
                                }));
                                toast.success(`Added ${info.label} plan`);
                              }}
                            >
                              <span className="text-2xl mr-3 bg-transparent">{info.icon}</span>
                              <div className="text-left flex-1 bg-transparent">
                                <div className="font-semibold">{info.label}</div>
                                <div className="text-xs text-text-muted">{info.description}</div>
                              </div>
                            </Button>
                          ))}
                        {Object.keys(planData.disaster_plans).length >= Object.keys(EMERGENCY_TYPES_MAP).length && (
                          <p className="text-sm text-text-muted text-center py-4">All predefined disaster types have been added</p>
                        )}
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardHeader>
            </Card>
            <div className="space-y-4">
              {Object.entries(planData.disaster_plans).map(([key, plan]) => {
                const info = EMERGENCY_TYPES_MAP[key] || { label: key, icon: '❓', description: 'Emergency plan' };
                return (
                  <Card key={key} className={plan.completed ? 'border-primary/50 bg-primary/10' : ''}>
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <span className="text-2xl">{info.icon}</span>
                          <div>
                            <CardTitle className="text-lg">{info.label}</CardTitle>
                            <CardDescription>{info.description}</CardDescription>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant={plan.completed ? 'default' : 'outline'}>
                            {plan.completed ? 'Complete' : 'Incomplete'}
                          </Badge>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setPlanData(prev => ({
                                ...prev,
                                disaster_plans: {
                                  ...prev.disaster_plans,
                                  [key]: {
                                    ...prev.disaster_plans[key],
                                    completed: !prev.disaster_plans[key].completed
                                  }
                                }
                              }));
                            }}
                          >
                            {plan.completed ? 'Mark Incomplete' : 'Mark Complete'}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              const newDisasterPlans = { ...planData.disaster_plans };
                              delete newDisasterPlans[key];
                              setPlanData(prev => ({
                                ...prev,
                                disaster_plans: newDisasterPlans
                              }));
                              toast.success(`Removed ${info.label} plan`);
                            }}
                            className="text-destructive hover:opacity-90"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {plan.steps.length === 0 ? (
                          <div className="text-center py-4 text-text-muted">
                            <p className="mb-2">No action steps added yet</p>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                const newStep = {
                                  id: `step_${Date.now()}`,
                                  action: '',
                                  order: plan.steps.length + 1
                                };
                                setPlanData(prev => ({
                                  ...prev,
                                  disaster_plans: {
                                    ...prev.disaster_plans,
                                    [key]: {
                                      ...prev.disaster_plans[key],
                                      steps: [...prev.disaster_plans[key].steps, newStep]
                                    }
                                  }
                                }));
                              }}
                            >
                              <Plus className="h-3 w-3 mr-1" />
                              Add First Step
                            </Button>
                          </div>
                        ) : (
                          plan.steps.sort((a, b) => a.order - b.order).map((step, index) => (
                            <div key={step.id} className="flex items-start gap-2 p-3 bg-white rounded border">
                              <span className="font-bold text-primary mt-1">{index + 1}.</span>
                              <Input
                                value={step.action}
                                onChange={(e) => {
                                  const updated = plan.steps.map(s =>
                                    s.id === step.id ? { ...s, action: e.target.value } : s
                                  );
                                  setPlanData(prev => ({
                                    ...prev,
                                    disaster_plans: {
                                      ...prev.disaster_plans,
                                      [key]: {
                                        ...prev.disaster_plans[key],
                                        steps: updated
                                      }
                                    }
                                  }));
                                }}
                                placeholder={`Action step ${index + 1} (e.g., "Grab go-bag and exit through front door")`}
                                className="flex-1"
                              />
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  const updated = plan.steps.filter(s => s.id !== step.id);
                                  setPlanData(prev => ({
                                    ...prev,
                                    disaster_plans: {
                                      ...prev.disaster_plans,
                                      [key]: {
                                        ...prev.disaster_plans[key],
                                        steps: updated
                                      }
                                    }
                                  }));
                                }}
                                className="text-destructive"
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                          ))
                        )}
                        {plan.steps.length > 0 && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const newStep = {
                                id: `step_${Date.now()}`,
                                action: '',
                                order: plan.steps.length + 1
                              };
                              setPlanData(prev => ({
                                ...prev,
                                disaster_plans: {
                                  ...prev.disaster_plans,
                                  [key]: {
                                    ...prev.disaster_plans[key],
                                    steps: [...prev.disaster_plans[key].steps, newStep]
                                  }
                                }
                              }));
                            }}
                            className="w-full"
                          >
                            <Plus className="h-3 w-3 mr-1" />
                            Add Another Step
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </TabsContent>

          <TabsContent value="communication">
            <Card>
              <CardHeader className="space-y-3">
                <CardTitle className="flex items-center gap-2 text-text-primary">
                  <MessageSquare className="h-5 w-5 text-primary" />
                  Communication Plan
                </CardTitle>
                <CardDescription className="text-foreground/90">
                  Establish comprehensive communication procedures for emergencies.
                </CardDescription>
                <div className="rounded-lg border border-border bg-primary/5 p-3 text-sm text-foreground/90">
                  <span className="font-medium text-text-primary">Note:</span> This section focuses on communication strategy. For general emergency services (police, fire, doctors), see the &quot;Emergency Contacts&quot; tab above.
                </div>
              </CardHeader>
              <CardContent className="pt-2">
                <Tabs defaultValue="contacts" className="w-full">
                  <TabsList className="flex w-full h-auto flex-wrap gap-2 p-2 rounded-xl bg-surface-muted/80 border border-border mb-4">
                    <TabsTrigger
                      value="contacts"
                      className="rounded-lg px-4 py-2 text-sm font-medium transition-all duration-200 data-[state=inactive]:text-foreground/80 data-[state=inactive]:hover:text-foreground data-[state=inactive]:hover:bg-primary/10 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-md"
                    >
                      Out-of-Area
                    </TabsTrigger>
                    <TabsTrigger
                      value="methods"
                      className="rounded-lg px-4 py-2 text-sm font-medium transition-all duration-200 data-[state=inactive]:text-foreground/80 data-[state=inactive]:hover:text-foreground data-[state=inactive]:hover:bg-primary/10 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-md"
                    >
                      Methods
                    </TabsTrigger>
                    <TabsTrigger
                      value="protocols"
                      className="rounded-lg px-4 py-2 text-sm font-medium transition-all duration-200 data-[state=inactive]:text-foreground/80 data-[state=inactive]:hover:text-foreground data-[state=inactive]:hover:bg-primary/10 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-md"
                    >
                      Protocols
                    </TabsTrigger>
                    <TabsTrigger
                      value="network"
                      className="rounded-lg px-4 py-2 text-sm font-medium transition-all duration-200 data-[state=inactive]:text-foreground/80 data-[state=inactive]:hover:text-foreground data-[state=inactive]:hover:bg-primary/10 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-md"
                    >
                      Network
                    </TabsTrigger>
                    <TabsTrigger
                      value="signaling"
                      className="rounded-lg px-4 py-2 text-sm font-medium transition-all duration-200 data-[state=inactive]:text-foreground/80 data-[state=inactive]:hover:text-foreground data-[state=inactive]:hover:bg-primary/10 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-md"
                    >
                      Signaling Methods
                    </TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="contacts" className="space-y-6 mt-4">
                    {/* Out-of-Area Contacts */}
                    <div>
                      <div className="flex justify-between items-center mb-4">
                        <div>
                          <h3 className="font-semibold text-lg">Out-of-Area Contacts</h3>
                          <p className="text-sm text-foreground/90">
                            Designate people outside your immediate area who can serve as central points of contact
                          </p>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            const newContact = {
                              id: `contact_${Date.now()}`,
                              name: '',
                              phone: '',
                              email: '',
                              address: '',
                              relationship: '',
                              notes: '',
                              is_primary: (planData.communication_plan?.out_of_area_contacts?.length ?? 0) === 0
                            };
                            setPlanData(prev => ({
                              ...prev,
                              communication_plan: {
                                ...(prev.communication_plan ?? {}),
                                out_of_area_contacts: [...(prev.communication_plan?.out_of_area_contacts ?? []), newContact]
                              }
                            }));
                          }}
                        >
                          <Plus className="h-4 w-4 mr-1" />
                          Add Contact
                        </Button>
                      </div>
                      {(planData.communication_plan?.out_of_area_contacts?.length ?? 0) === 0 ? (
                        <p className="text-sm text-text-muted italic">No out-of-area contacts added yet</p>
                      ) : (
                        <div className="space-y-4">
                          {(planData.communication_plan?.out_of_area_contacts ?? []).map((contact, index) => (
                            <Card key={contact.id} className="p-4">
                              <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center gap-2">
                                  <Badge 
                                    variant={contact.is_primary ? "default" : "outline"}
                                    className={contact.is_primary ? "bg-primary cursor-pointer hover:opacity-90 text-primary-foreground" : "cursor-pointer hover:bg-surface-muted"}
                                    onClick={() => {
                                      const updated = (planData.communication_plan?.out_of_area_contacts ?? []).map(c => ({
                                        ...c,
                                        is_primary: c.id === contact.id ? !contact.is_primary : false
                                      }));
                                      setPlanData(prev => ({
                                        ...prev,
                                        communication_plan: {
                                          ...(prev.communication_plan ?? {}),
                                          out_of_area_contacts: updated
                                        }
                                      }));
                                    }}
                                    title={contact.is_primary ? "Click to remove as primary contact" : "Click to set as primary contact"}
                                  >
                                    {contact.is_primary ? "Primary" : "Set as Primary"}
                                  </Badge>
                                  <span className="font-semibold">Contact {index + 1}</span>
                                </div>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    const updated = (planData.communication_plan?.out_of_area_contacts ?? []).filter(c => c.id !== contact.id);
                                    setPlanData(prev => ({
                                      ...prev,
                                      communication_plan: {
                                        ...(prev.communication_plan ?? {}),
                                        out_of_area_contacts: updated
                                      }
                                    }));
                                  }}
                                  className="text-destructive"
                                >
                                  <X className="h-4 w-4" />
                                </Button>
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                  <Label>Name *</Label>
                                  <Input
                                    value={contact.name}
                                    onChange={(e) => {
                                      const updated = (planData.communication_plan?.out_of_area_contacts ?? []).map(c =>
                                        c.id === contact.id ? { ...c, name: e.target.value } : c
                                      );
                                      setPlanData(prev => ({
                                        ...prev,
                                        communication_plan: {
                                          ...(prev.communication_plan ?? {}),
                                          out_of_area_contacts: updated
                                        }
                                      }));
                                    }}
                                    placeholder="Full name"
                                  />
                                </div>
                                <div>
                                  <Label>Phone Number *</Label>
                                  <Input
                                    value={contact.phone}
                                    onChange={(e) => {
                                      const updated = (planData.communication_plan?.out_of_area_contacts ?? []).map(c =>
                                        c.id === contact.id ? { ...c, phone: e.target.value } : c
                                      );
                                      setPlanData(prev => ({
                                        ...prev,
                                        communication_plan: {
                                          ...(prev.communication_plan ?? {}),
                                          out_of_area_contacts: updated
                                        }
                                      }));
                                    }}
                                    placeholder="(555) 123-4567"
                                  />
                                </div>
                                <div>
                                  <Label>Email</Label>
                                  <Input
                                    type="email"
                                    value={contact.email}
                                    onChange={(e) => {
                                      const updated = (planData.communication_plan?.out_of_area_contacts ?? []).map(c =>
                                        c.id === contact.id ? { ...c, email: e.target.value } : c
                                      );
                                      setPlanData(prev => ({
                                        ...prev,
                                        communication_plan: {
                                          ...(prev.communication_plan ?? {}),
                                          out_of_area_contacts: updated
                                        }
                                      }));
                                    }}
                                    placeholder="email@example.com"
                                  />
                                </div>
                                <div>
                                  <Label>Address</Label>
                                  <Input
                                    value={contact.address}
                                    onChange={(e) => {
                                      const updated = (planData.communication_plan?.out_of_area_contacts ?? []).map(c =>
                                        c.id === contact.id ? { ...c, address: e.target.value } : c
                                      );
                                      setPlanData(prev => ({
                                        ...prev,
                                        communication_plan: {
                                          ...(prev.communication_plan ?? {}),
                                          out_of_area_contacts: updated
                                        }
                                      }));
                                    }}
                                    placeholder="Full address"
                                  />
                                </div>
                                <div>
                                  <Label>Relationship</Label>
                                  <Input
                                    value={contact.relationship}
                                    onChange={(e) => {
                                      const updated = (planData.communication_plan?.out_of_area_contacts ?? []).map(c =>
                                        c.id === contact.id ? { ...c, relationship: e.target.value } : c
                                      );
                                      setPlanData(prev => ({
                                        ...prev,
                                        communication_plan: {
                                          ...(prev.communication_plan ?? {}),
                                          out_of_area_contacts: updated
                                        }
                                      }));
                                    }}
                                    placeholder="e.g., Aunt, Friend, etc."
                                  />
                                </div>
                                <div className="md:col-span-2">
                                  <Label>Notes</Label>
                                  <Textarea
                                    value={contact.notes}
                                    onChange={(e) => {
                                      const updated = (planData.communication_plan?.out_of_area_contacts ?? []).map(c =>
                                        c.id === contact.id ? { ...c, notes: e.target.value } : c
                                      );
                                      setPlanData(prev => ({
                                        ...prev,
                                        communication_plan: {
                                          ...(prev.communication_plan ?? {}),
                                          out_of_area_contacts: updated
                                        }
                                      }));
                                    }}
                                    placeholder="Additional notes about this contact"
                                    rows={2}
                                  />
                                </div>
                              </div>
                            </Card>
                          ))}
                        </div>
                      )}
                    </div>
                  </TabsContent>
                  
                  <TabsContent value="methods" className="space-y-6 mt-4">
                    {/* Communication Methods */}
                    <div>
                      <div className="flex justify-between items-center mb-4">
                        <div>
                          <h3 className="font-semibold text-lg mb-2">Communication Methods</h3>
                          <p className="text-sm text-foreground/90">
                            Configure each communication method with platform, endpoint, and protocol. Methods are used in priority order.
                          </p>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            const newMethod = {
                              id: `method_${Date.now()}`,
                              platform: 'phone',
                              endpoint: '',
                              protocol: '',
                              priority: (planData.communication_plan?.communication_methods?.methods?.length ?? 0) + 1
                            };
                            setPlanData(prev => ({
                              ...prev,
                              communication_plan: {
                                ...(prev.communication_plan ?? {}),
                                communication_methods: {
                                  ...(prev.communication_plan?.communication_methods ?? {}),
                                  methods: [...(prev.communication_plan?.communication_methods?.methods ?? []), newMethod]
                                }
                              }
                            }));
                          }}
                        >
                          <Plus className="h-4 w-4 mr-1" />
                          Add Method
                        </Button>
                      </div>

                      {(planData.communication_plan?.communication_methods?.methods?.length ?? 0) === 0 ? (
                        <p className="text-sm text-text-muted italic">No communication methods configured yet</p>
                      ) : (
                        <div className="space-y-4">
                          {(planData.communication_plan?.communication_methods?.methods ?? [])
                            .sort((a, b) => (a.priority ?? 999) - (b.priority ?? 999))
                            .map((method) => (
                            <Card key={method.id} className="p-4">
                              <div className="flex items-center justify-between mb-4">
                                <div className="flex items-center gap-3">
                                  <span className="font-bold text-primary w-8">#{method.priority || '?'}</span>
                                  <span className="text-sm text-foreground/90">Lower numbers = tried first</span>
                                </div>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    const updated = (planData.communication_plan?.communication_methods?.methods ?? []).filter(m => m.id !== method.id);
                                    setPlanData(prev => ({
                                      ...prev,
                                      communication_plan: {
                                        ...(prev.communication_plan ?? {}),
                                        communication_methods: {
                                          ...(prev.communication_plan?.communication_methods ?? {}),
                                          methods: updated
                                        }
                                      }
                                    }));
                                  }}
                                  className="text-destructive"
                                >
                                  <X className="h-4 w-4" />
                                </Button>
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                  <Label>Platform</Label>
                                  <Select
                                    value={method.platform || 'phone'}
                                    onValueChange={(value) => {
                                      const updated = (planData.communication_plan?.communication_methods?.methods ?? []).map(m =>
                                        m.id === method.id ? { ...m, platform: value, endpoint: '' } : m
                                      );
                                      setPlanData(prev => ({
                                        ...prev,
                                        communication_plan: {
                                          ...(prev.communication_plan ?? {}),
                                          communication_methods: {
                                            ...(prev.communication_plan?.communication_methods ?? {}),
                                            methods: updated
                                          }
                                        }
                                      }));
                                    }}
                                  >
                                    <SelectTrigger>
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="phone">Phone (Voice Call)</SelectItem>
                                      <SelectItem value="text">Text Message (SMS)</SelectItem>
                                      <SelectItem value="email">Email</SelectItem>
                                      <SelectItem value="radio">Radio (see Radio Channels below)</SelectItem>
                                      <SelectItem value="social_media">Social Media</SelectItem>
                                      <SelectItem value="satellite">Satellite Phone</SelectItem>
                                      <SelectItem value="ham_radio">Ham Radio</SelectItem>
                                      <SelectItem value="cb_radio">CB Radio</SelectItem>
                                      <SelectItem value="in_person">In Person</SelectItem>
                                      <SelectItem value="other">Other</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </div>
                                <div>
                                  <Label>
                                    {method.platform === 'phone' || method.platform === 'text' ? 'Phone Number' :
                                     method.platform === 'email' ? 'Email Address' :
                                     method.platform === 'social_media' ? 'Platform Handle/Group' :
                                     method.platform === 'radio' ? 'Radio Channel Name (see below)' :
                                     method.platform === 'satellite' ? 'Satellite Phone Number' :
                                     method.platform === 'in_person' ? 'Meeting Location' :
                                     'Endpoint'}
                                  </Label>
                                  <Input
                                    value={method.endpoint || ''}
                                    onChange={(e) => {
                                      const updated = (planData.communication_plan?.communication_methods?.methods ?? []).map(m =>
                                        m.id === method.id ? { ...m, endpoint: e.target.value } : m
                                      );
                                      setPlanData(prev => ({
                                        ...prev,
                                        communication_plan: {
                                          ...(prev.communication_plan ?? {}),
                                          communication_methods: {
                                            ...(prev.communication_plan?.communication_methods ?? {}),
                                            methods: updated
                                          }
                                        }
                                      }));
                                    }}
                                    placeholder={
                                      method.platform === 'phone' || method.platform === 'text' ? '(555) 123-4567' :
                                      method.platform === 'email' ? 'email@example.com' :
                                      method.platform === 'social_media' ? '@username or group name' :
                                      method.platform === 'radio' ? 'Reference radio channel name from below' :
                                      method.platform === 'in_person' ? 'Location address' :
                                      'Endpoint details'
                                    }
                                  />
                                </div>
                                <div>
                                  <Label>Priority (Order)</Label>
                                  <Input
                                    type="number"
                                    value={method.priority || ''}
                                    onChange={(e) => {
                                      const updated = (planData.communication_plan?.communication_methods?.methods ?? []).map(m =>
                                        m.id === method.id ? { ...m, priority: parseInt(e.target.value) || 0 } : m
                                      );
                                      setPlanData(prev => ({
                                        ...prev,
                                        communication_plan: {
                                          ...(prev.communication_plan ?? {}),
                                          communication_methods: {
                                            ...(prev.communication_plan?.communication_methods ?? {}),
                                            methods: updated
                                          }
                                        }
                                      }));
                                    }}
                                    placeholder="1, 2, 3..."
                                    min="1"
                                  />
                                </div>
                                <div className="md:col-span-2">
                                  <Label>Protocol (How to Use)</Label>
                                  <Textarea
                                    value={method.protocol || ''}
                                    onChange={(e) => {
                                      const updated = (planData.communication_plan?.communication_methods?.methods ?? []).map(m =>
                                        m.id === method.id ? { ...m, protocol: e.target.value } : m
                                      );
                                      setPlanData(prev => ({
                                        ...prev,
                                        communication_plan: {
                                          ...(prev.communication_plan ?? {}),
                                          communication_methods: {
                                            ...(prev.communication_plan?.communication_methods ?? {}),
                                            methods: updated
                                          }
                                        }
                                      }));
                                    }}
                                    placeholder="Describe how to use this method: When to use it, what format to use, call signs, procedures, etc. (e.g., 'Call every 2 hours during emergency. Use status codes: GREEN=safe, RED=need help. Leave voicemail if no answer.')"
                                    rows={3}
                                  />
                                </div>
                              </div>
                            </Card>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Radio Frequencies */}
                    <div>
                      <div className="flex justify-between items-center mb-3">
                        <div>
                          <h3 className="font-semibold text-lg">Radio Frequencies & Channels</h3>
                          <p className="text-sm text-foreground/90">
                            Store radio frequencies, channels, and call signs for emergency communication
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const commonFrequencies = [
                                { name: 'FRS Channel 1', frequency: '462.5625', channel: '1', type: 'FRS', notes: 'Family Radio Service - No license required' },
                                { name: 'FRS Channel 2', frequency: '462.5875', channel: '2', type: 'FRS', notes: 'Family Radio Service - No license required' },
                                { name: 'CB Channel 9', frequency: '27.065', channel: '9', type: 'CB', notes: 'Emergency channel - Monitor for emergencies' },
                                { name: 'Ham 2m Simplex', frequency: '146.520', channel: '', type: 'Ham', notes: 'Ham radio emergency frequency - License required' }
                              ];
                              const existing = planData.communication_plan?.communication_methods?.radio_channels ?? [];
                              const newChannels = commonFrequencies.map(freq => ({
                                id: `channel_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                                ...freq,
                                call_sign: ''
                              }));
                              setPlanData(prev => ({
                                ...prev,
                                communication_plan: {
                                  ...(prev.communication_plan ?? {}),
                                  communication_methods: {
                                    ...(prev.communication_plan?.communication_methods ?? {}),
                                    radio_channels: [...existing, ...newChannels]
                                  }
                                }
                              }));
                            }}
                          >
                            <Plus className="h-4 w-4 mr-1" />
                            Add Common Frequencies
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const newChannel = {
                                id: `channel_${Date.now()}`,
                                name: '',
                                frequency: '',
                                channel: '',
                                call_sign: '',
                                type: 'FRS', // FRS, GMRS, Ham, CB, etc.
                                notes: ''
                              };
                              setPlanData(prev => ({
                                ...prev,
                                communication_plan: {
                                  ...(prev.communication_plan ?? {}),
                                  communication_methods: {
                                    ...(prev.communication_plan?.communication_methods ?? {}),
                                    radio_channels: [...(prev.communication_plan?.communication_methods?.radio_channels ?? []), newChannel]
                                  }
                                }
                              }));
                            }}
                          >
                            <Plus className="h-4 w-4 mr-1" />
                            Add Custom Channel
                          </Button>
                        </div>
                      </div>
                      {(planData.communication_plan?.communication_methods?.radio_channels?.length ?? 0) === 0 ? (
                        <p className="text-sm text-text-muted italic">No radio channels configured yet</p>
                      ) : (
                        <div className="space-y-3">
                          {(planData.communication_plan?.communication_methods?.radio_channels ?? []).map((channel) => (
                            <Card key={channel.id} className="p-4">
                              <div className="flex items-center justify-between mb-3">
                                <span className="font-semibold">{channel.name || 'Unnamed Channel'}</span>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    const updated = (planData.communication_plan?.communication_methods?.radio_channels ?? []).filter(c => c.id !== channel.id);
                                    setPlanData(prev => ({
                                      ...prev,
                                      communication_plan: {
                                        ...(prev.communication_plan ?? {}),
                                        communication_methods: {
                                          ...(prev.communication_plan?.communication_methods ?? {}),
                                          radio_channels: updated
                                        }
                                      }
                                    }));
                                  }}
                                  className="text-destructive"
                                >
                                  <X className="h-4 w-4" />
                                </Button>
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div>
                                  <Label>Channel Name</Label>
                                  <Input
                                    value={channel.name || ''}
                                    onChange={(e) => {
                                      const updated = (planData.communication_plan?.communication_methods?.radio_channels ?? []).map(c =>
                                        c.id === channel.id ? { ...c, name: e.target.value } : c
                                      );
                                      setPlanData(prev => ({
                                        ...prev,
                                        communication_plan: {
                                          ...(prev.communication_plan ?? {}),
                                          communication_methods: {
                                            ...(prev.communication_plan?.communication_methods ?? {}),
                                            radio_channels: updated
                                          }
                                        }
                                      }));
                                    }}
                                    placeholder="e.g., Family Channel 1"
                                  />
                                </div>
                                <div>
                                  <Label>Frequency (MHz)</Label>
                                  <Input
                                    value={channel.frequency || ''}
                                    onChange={(e) => {
                                      const updated = (planData.communication_plan?.communication_methods?.radio_channels ?? []).map(c =>
                                        c.id === channel.id ? { ...c, frequency: e.target.value } : c
                                      );
                                      setPlanData(prev => ({
                                        ...prev,
                                        communication_plan: {
                                          ...(prev.communication_plan ?? {}),
                                          communication_methods: {
                                            ...(prev.communication_plan?.communication_methods ?? {}),
                                            radio_channels: updated
                                          }
                                        }
                                      }));
                                    }}
                                    placeholder="e.g., 462.5625"
                                  />
                                </div>
                                <div>
                                  <Label>Channel Number</Label>
                                  <Input
                                    value={channel.channel || ''}
                                    onChange={(e) => {
                                      const updated = (planData.communication_plan?.communication_methods?.radio_channels ?? []).map(c =>
                                        c.id === channel.id ? { ...c, channel: e.target.value } : c
                                      );
                                      setPlanData(prev => ({
                                        ...prev,
                                        communication_plan: {
                                          ...(prev.communication_plan ?? {}),
                                          communication_methods: {
                                            ...(prev.communication_plan?.communication_methods ?? {}),
                                            radio_channels: updated
                                          }
                                        }
                                      }));
                                    }}
                                    placeholder="e.g., Channel 1"
                                  />
                                </div>
                                <div>
                                  <Label>Radio Type</Label>
                                  <Select
                                    value={channel.type || 'FRS'}
                                    onValueChange={(value) => {
                                      const updated = (planData.communication_plan?.communication_methods?.radio_channels ?? []).map(c =>
                                        c.id === channel.id ? { ...c, type: value } : c
                                      );
                                      setPlanData(prev => ({
                                        ...prev,
                                        communication_plan: {
                                          ...(prev.communication_plan ?? {}),
                                          communication_methods: {
                                            ...(prev.communication_plan?.communication_methods ?? {}),
                                            radio_channels: updated
                                          }
                                        }
                                      }));
                                    }}
                                  >
                                    <SelectTrigger>
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="FRS">FRS (Family Radio Service)</SelectItem>
                                      <SelectItem value="GMRS">GMRS (General Mobile Radio Service)</SelectItem>
                                      <SelectItem value="Ham">Ham Radio</SelectItem>
                                      <SelectItem value="CB">CB Radio</SelectItem>
                                      <SelectItem value="Marine">Marine Radio</SelectItem>
                                      <SelectItem value="Other">Other</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </div>
                                <div>
                                  <Label>Call Sign</Label>
                                  <Input
                                    value={channel.call_sign || ''}
                                    onChange={(e) => {
                                      const updated = (planData.communication_plan?.communication_methods?.radio_channels ?? []).map(c =>
                                        c.id === channel.id ? { ...c, call_sign: e.target.value } : c
                                      );
                                      setPlanData(prev => ({
                                        ...prev,
                                        communication_plan: {
                                          ...(prev.communication_plan ?? {}),
                                          communication_methods: {
                                            ...(prev.communication_plan?.communication_methods ?? {}),
                                            radio_channels: updated
                                          }
                                        }
                                      }));
                                    }}
                                    placeholder="e.g., KB1ABC"
                                  />
                                </div>
                                <div className="md:col-span-2">
                                  <Label>Notes</Label>
                                  <Textarea
                                    value={channel.notes || ''}
                                    onChange={(e) => {
                                      const updated = (planData.communication_plan?.communication_methods?.radio_channels ?? []).map(c =>
                                        c.id === channel.id ? { ...c, notes: e.target.value } : c
                                      );
                                      setPlanData(prev => ({
                                        ...prev,
                                        communication_plan: {
                                          ...(prev.communication_plan ?? {}),
                                          communication_methods: {
                                            ...(prev.communication_plan?.communication_methods ?? {}),
                                            radio_channels: updated
                                          }
                                        }
                                      }));
                                    }}
                                    placeholder="Additional notes about this channel"
                                    rows={2}
                                  />
                                </div>
                              </div>
                            </Card>
                          ))}
                        </div>
                      )}
                    </div>


                    {/* Check-In Procedures */}
                    <div>
                      <Label htmlFor="check-in-procedures">Check-In Procedures</Label>
                      <Textarea
                        id="check-in-procedures"
                        value={planData.communication_plan?.check_in_procedures ?? ''}
                        onChange={(e) => {
                          setPlanData(prev => ({
                            ...prev,
                            communication_plan: {
                              ...(prev.communication_plan ?? {}),
                              check_in_procedures: e.target.value
                            }
                          }));
                        }}
                        placeholder="Describe how family members should check in during an emergency (e.g., 'Call out-of-area contact every 2 hours', 'Text when safe', 'Use radio channel 1 at top of each hour', etc.)"
                        rows={4}
                      />
                    </div>
                  </TabsContent>
                  
                  <TabsContent value="protocols" className="space-y-6 mt-4">
                    <div>
                      <div className="mb-4">
                        <h3 className="font-semibold text-lg mb-2">Communication Protocols</h3>
                        <p className="text-sm text-foreground/90">
                          Define specific procedures for different emergency scenarios and communication challenges.
                        </p>
                      </div>
                      
                      <div className="space-y-6">
                        <div>
                          <Label htmlFor="power-outage-protocol">During Power Outage</Label>
                          <Textarea
                            id="power-outage-protocol"
                            value={planData.communication_plan?.communication_protocols?.during_power_outage ?? ''}
                            onChange={(e) => {
                              setPlanData(prev => ({
                                ...prev,
                                communication_plan: {
                                  ...(prev.communication_plan ?? {}),
                                  communication_protocols: {
                                    ...(prev.communication_plan?.communication_protocols ?? {}),
                                    during_power_outage: e.target.value
                                  }
                                }
                              }));
                            }}
                            placeholder="How will family members communicate when power is out? (e.g., Use battery-powered radio, meet at designated location, use car charger for phones)"
                            rows={4}
                          />
                        </div>
                        
                        <div>
                          <Label htmlFor="internet-outage-protocol">During Internet Outage</Label>
                          <Textarea
                            id="internet-outage-protocol"
                            value={planData.communication_plan?.communication_protocols?.during_internet_outage ?? ''}
                            onChange={(e) => {
                              setPlanData(prev => ({
                                ...prev,
                                communication_plan: {
                                  ...(prev.communication_plan ?? {}),
                                  communication_protocols: {
                                    ...(prev.communication_plan?.communication_protocols ?? {}),
                                    during_internet_outage: e.target.value
                                  }
                                }
                              }));
                            }}
                            placeholder="How will family members communicate when internet is unavailable?"
                            rows={4}
                          />
                        </div>
                        
                        <div>
                          <Label htmlFor="phone-outage-protocol">During Phone Outage</Label>
                          <Textarea
                            id="phone-outage-protocol"
                            value={planData.communication_plan?.communication_protocols?.during_phone_outage ?? ''}
                            onChange={(e) => {
                              setPlanData(prev => ({
                                ...prev,
                                communication_plan: {
                                  ...(prev.communication_plan ?? {}),
                                  communication_protocols: {
                                    ...(prev.communication_plan?.communication_protocols ?? {}),
                                    during_phone_outage: e.target.value
                                  }
                                }
                              }));
                            }}
                            placeholder="How will family members communicate when phone service is unavailable?"
                            rows={4}
                          />
                        </div>
                        
                        <div>
                          <Label htmlFor="meeting-point-procedures">Meeting Point Procedures</Label>
                          <Textarea
                            id="meeting-point-procedures"
                            value={planData.communication_plan?.communication_protocols?.meeting_point_procedures ?? ''}
                            onChange={(e) => {
                              setPlanData(prev => ({
                                ...prev,
                                communication_plan: {
                                  ...(prev.communication_plan ?? {}),
                                  communication_protocols: {
                                    ...(prev.communication_plan?.communication_protocols ?? {}),
                                    meeting_point_procedures: e.target.value
                                  }
                                }
                              }));
                            }}
                            placeholder="Procedures for meeting at designated locations during emergencies"
                            rows={4}
                          />
                        </div>
                        
                        <div>
                          <Label htmlFor="school-work-procedures">School & Work Procedures</Label>
                          <Textarea
                            id="school-work-procedures"
                            value={planData.communication_plan?.communication_protocols?.school_work_procedures ?? ''}
                            onChange={(e) => {
                              setPlanData(prev => ({
                                ...prev,
                                communication_plan: {
                                  ...(prev.communication_plan ?? {}),
                                  communication_protocols: {
                                    ...(prev.communication_plan?.communication_protocols ?? {}),
                                    school_work_procedures: e.target.value
                                  }
                                }
                              }));
                            }}
                            placeholder="Procedures for communicating during school or work hours"
                            rows={4}
                          />
                        </div>
                      </div>
                    </div>
                  </TabsContent>
                  
                  <TabsContent value="protocols" className="space-y-6 mt-4">
                    <div>
                      <div className="mb-4">
                        <h3 className="font-semibold text-lg mb-2">Communication Protocols</h3>
                        <p className="text-sm text-foreground/90">
                          Define specific procedures for different emergency scenarios and communication challenges.
                        </p>
                      </div>
                      
                      <div className="space-y-6">
                        <div>
                          <Label htmlFor="power-outage-protocol">During Power Outage</Label>
                          <Textarea
                            id="power-outage-protocol"
                            value={planData.communication_plan?.communication_protocols?.during_power_outage ?? ''}
                            onChange={(e) => {
                              setPlanData(prev => ({
                                ...prev,
                                communication_plan: {
                                  ...(prev.communication_plan ?? {}),
                                  communication_protocols: {
                                    ...(prev.communication_plan?.communication_protocols ?? {}),
                                    during_power_outage: e.target.value
                                  }
                                }
                              }));
                            }}
                            placeholder="How will family members communicate when power is out? (e.g., Use battery-powered radio, meet at designated location, use car charger for phones)"
                            rows={4}
                          />
                        </div>
                        
                        <div>
                          <Label htmlFor="internet-outage-protocol">During Internet Outage</Label>
                          <Textarea
                            id="internet-outage-protocol"
                            value={planData.communication_plan?.communication_protocols?.during_internet_outage ?? ''}
                            onChange={(e) => {
                              setPlanData(prev => ({
                                ...prev,
                                communication_plan: {
                                  ...(prev.communication_plan ?? {}),
                                  communication_protocols: {
                                    ...(prev.communication_plan?.communication_protocols ?? {}),
                                    during_internet_outage: e.target.value
                                  }
                                }
                              }));
                            }}
                            placeholder="How will family members communicate when internet is unavailable?"
                            rows={4}
                          />
                        </div>
                        
                        <div>
                          <Label htmlFor="phone-outage-protocol">During Phone Outage</Label>
                          <Textarea
                            id="phone-outage-protocol"
                            value={planData.communication_plan?.communication_protocols?.during_phone_outage ?? ''}
                            onChange={(e) => {
                              setPlanData(prev => ({
                                ...prev,
                                communication_plan: {
                                  ...(prev.communication_plan ?? {}),
                                  communication_protocols: {
                                    ...(prev.communication_plan?.communication_protocols ?? {}),
                                    during_phone_outage: e.target.value
                                  }
                                }
                              }));
                            }}
                            placeholder="How will family members communicate when phone service is unavailable?"
                            rows={4}
                          />
                        </div>
                        
                        <div>
                          <Label htmlFor="meeting-point-procedures">Meeting Point Procedures</Label>
                          <Textarea
                            id="meeting-point-procedures"
                            value={planData.communication_plan?.communication_protocols?.meeting_point_procedures ?? ''}
                            onChange={(e) => {
                              setPlanData(prev => ({
                                ...prev,
                                communication_plan: {
                                  ...(prev.communication_plan ?? {}),
                                  communication_protocols: {
                                    ...(prev.communication_plan?.communication_protocols ?? {}),
                                    meeting_point_procedures: e.target.value
                                  }
                                }
                              }));
                            }}
                            placeholder="Procedures for meeting at designated locations during emergencies"
                            rows={4}
                          />
                        </div>
                        
                        <div>
                          <Label htmlFor="school-work-procedures">School & Work Procedures</Label>
                          <Textarea
                            id="school-work-procedures"
                            value={planData.communication_plan?.communication_protocols?.school_work_procedures ?? ''}
                            onChange={(e) => {
                              setPlanData(prev => ({
                                ...prev,
                                communication_plan: {
                                  ...(prev.communication_plan ?? {}),
                                  communication_protocols: {
                                    ...(prev.communication_plan?.communication_protocols ?? {}),
                                    school_work_procedures: e.target.value
                                  }
                                }
                              }));
                            }}
                            placeholder="Procedures for communicating during school or work hours"
                            rows={4}
                          />
                        </div>
                      </div>
                    </div>
                  </TabsContent>
                  
                  <TabsContent value="signaling" className="space-y-6 mt-4">
                    <div>
                      <div className="mb-4">
                        <h3 className="font-semibold text-lg mb-2">Signaling & Communication Methods Reference</h3>
                        <p className="text-sm text-foreground/90">
                          Reference guide for various signaling and communication methods that may be useful in emergency situations when standard communication is unavailable.
                        </p>
                      </div>
                      
                      <div className="space-y-4">
                        {/* Morse Code */}
                        <Card className="p-4">
                          <CardHeader className="pb-3">
                            <CardTitle className="text-lg">Morse Code</CardTitle>
                            <CardDescription>International standard for encoding text using dots (•) and dashes (-)</CardDescription>
                          </CardHeader>
                          <CardContent className="space-y-4">
                            <div className="bg-primary/10 border-l-4 border-primary p-3 rounded text-foreground/90">
                              <div className="font-semibold mb-2 text-text-primary">Learning Method:</div>
                              <div className="text-sm space-y-1">
                                <div>• Place your pencil at START and listen to Morse code</div>
                                <div>• Move down and RIGHT for each DIT (•)</div>
                                <div>• Move down and LEFT for each DAH (-)</div>
                                <div className="mt-2 italic">Practice 10 minutes a day to build recognition!</div>
                              </div>
                            </div>
                            
                            {/* Morse Code Tree Diagram */}
                            <div>
                              <Label className="font-semibold mb-3 block">Morse Code Tree (Follow the Paths):</Label>
                              <div className="bg-card border-2 border-border p-6 rounded-lg">
                                <MorseCodeTreeDiagram />
                              </div>
                              <div className="mt-3 p-3 bg-surface-muted rounded">
                                <p className="text-xs text-foreground/90 mb-2">
                                  <strong>How to use:</strong> Start at START. For each DAH (-), move LEFT (←). For each DIT (•), move RIGHT (→). 
                                  Follow the connecting lines to decode the letter or number.
                                </p>
                                <p className="text-xs text-foreground/90">
                                  <strong>Example:</strong> To decode "DAH DIT DIT" (dash dot dot): 
                                  START → DAH (left to <strong>T</strong>) → DIT (right to <strong>N</strong>) → DIT (right to <strong>D</strong>) = <strong>D</strong>
                                </p>
                                <p className="text-xs text-foreground/90 mt-2 italic">
                                  <strong>Tip:</strong> The key is hearing and comprehending the code. Practice by tracing this tree while listening to Morse code. 
                                  Start with common letters (E, T, A, I, N) which are the shortest codes.
                                </p>
                              </div>
                            </div>
                            
                            <div>
                              <Label className="font-semibold mb-2 block">Common Emergency Signals:</Label>
                              <div className="bg-surface-muted p-3 rounded text-sm font-mono space-y-1">
                                <div><strong>SOS:</strong> ••• --- ••• (three dots, three dashes, three dots)</div>
                                <div><strong>Help:</strong> •••• • •-•• •--•</div>
                                <div><strong>Need Water:</strong> •-- •- - • •- •-•</div>
                                <div><strong>Need Medical:</strong> -- • -•• •- -•- •- •-••</div>
                              </div>
                            </div>
                            
                            <div>
                              <Label className="font-semibold mb-2 block">Complete Alphabet:</Label>
                              <div className="bg-surface-muted p-3 rounded text-xs font-mono grid grid-cols-2 md:grid-cols-4 gap-2">
                                <div><strong>A:</strong> •-</div><div><strong>B:</strong> -•••</div><div><strong>C:</strong> -•-•</div><div><strong>D:</strong> -••</div>
                                <div><strong>E:</strong> •</div><div><strong>F:</strong> ••-•</div><div><strong>G:</strong> --•</div><div><strong>H:</strong> ••••</div>
                                <div><strong>I:</strong> ••</div><div><strong>J:</strong> •---</div><div><strong>K:</strong> -•-</div><div><strong>L:</strong> •-••</div>
                                <div><strong>M:</strong> --</div><div><strong>N:</strong> -•</div><div><strong>O:</strong> ---</div><div><strong>P:</strong> •--•</div>
                                <div><strong>Q:</strong> --•-</div><div><strong>R:</strong> •-•</div><div><strong>S:</strong> •••</div><div><strong>T:</strong> -</div>
                                <div><strong>U:</strong> ••-</div><div><strong>V:</strong> •••-</div><div><strong>W:</strong> •--</div><div><strong>X:</strong> -••-</div>
                                <div><strong>Y:</strong> -•--</div><div><strong>Z:</strong> --••</div>
                              </div>
                            </div>
                            
                            <div>
                              <Label className="font-semibold mb-2 block">Numbers:</Label>
                              <div className="bg-surface-muted p-3 rounded text-xs font-mono grid grid-cols-2 md:grid-cols-5 gap-2">
                                <div><strong>1:</strong> •----</div>
                                <div><strong>2:</strong> ••---</div>
                                <div><strong>3:</strong> •••--</div>
                                <div><strong>4:</strong> ••••-</div>
                                <div><strong>5:</strong> •••••</div>
                                <div><strong>6:</strong> -••••</div>
                                <div><strong>7:</strong> --•••</div>
                                <div><strong>8:</strong> ---••</div>
                                <div><strong>9:</strong> ----•</div>
                                <div><strong>0:</strong> -----</div>
                              </div>
                            </div>
                            
                            <div>
                              <Label className="font-semibold mb-2 block">Common Punctuation:</Label>
                              <div className="bg-surface-muted p-3 rounded text-xs font-mono space-y-1">
                                <div><strong>Period (.):</strong> •-•-•-</div>
                                <div><strong>Comma (,):</strong> --••--</div>
                                <div><strong>Question (?):</strong> ••--••</div>
                                <div><strong>Slash (/):</strong> -••-•</div>
                                <div><strong>At (@):</strong> •--•-•</div>
                              </div>
                            </div>
                            
                            </CardContent>
                        </Card>

                        {/* POW Tap Code */}
                        <Card className="p-4">
                          <CardHeader className="pb-3">
                            <CardTitle className="text-lg">POW Tap Code</CardTitle>
                            <CardDescription>Simple communication method using taps on surfaces</CardDescription>
                          </CardHeader>
                          <CardContent className="space-y-3">
                            <div>
                              <p className="text-sm mb-3">The tap code uses a 5x5 grid. First tap indicates row, second tap indicates column. Pause between letters.</p>
                              <div className="bg-surface-muted p-3 rounded">
                                <div className="text-xs font-mono grid grid-cols-5 gap-1 mb-2">
                                  <div className="font-bold">1</div><div className="font-bold">2</div><div className="font-bold">3</div><div className="font-bold">4</div><div className="font-bold">5</div>
                                  <div>1: A</div><div>2: B</div><div>3: C</div><div>4: D</div><div>5: E</div>
                                  <div>2: F</div><div>2: G</div><div>2: H</div><div>2: I</div><div>2: J</div>
                                  <div>3: L</div><div>3: M</div><div>3: N</div><div>3: O</div><div>3: P</div>
                                  <div>4: Q</div><div>4: R</div><div>4: S</div><div>4: T</div><div>4: U</div>
                                  <div>5: V</div><div>5: W</div><div>5: X</div><div>5: Y</div><div>5: Z</div>
                                </div>
                                <p className="text-xs text-foreground/90">Note: K is represented as C (3,3) or omitted. Example: "HELP" = (2,3) (5,3) (3,3) (4,3)</p>
                              </div>
                            </div>
                          </CardContent>
                        </Card>

                        {/* USAR / Xmark Markings */}
                        <Card className="p-4">
                          <CardHeader className="pb-3">
                            <CardTitle className="text-lg">USAR / Xmark Structure Search Markings</CardTitle>
                            <CardDescription>FEMA standard markings for Urban Search and Rescue operations</CardDescription>
                          </CardHeader>
                          <CardContent className="space-y-3">
                            <div className="space-y-4">
                              <div className="bg-primary/10 border-l-4 border-primary p-3">
                                <div className="font-semibold mb-2">Single Slash (/) - Search In Progress</div>
                                <div className="flex items-center gap-4 mb-2">
                                  <div className="bg-card border-2 border-border p-4 rounded flex items-center justify-center min-w-[120px]">
                                    <div className="text-6xl font-bold text-primary">/</div>
                                  </div>
                                  <div className="text-sm flex-1">
                                    <div className="mb-1"><strong>NY-TF1</strong> ← Team ID & entry time to left</div>
                                    <div>Place at entry point when search begins</div>
                                  </div>
                                </div>
                              </div>
                              
                              <div className="bg-primary/10 border-l-4 border-primary p-3">
                                <div className="font-semibold mb-2">Completed X (2 ft × 2 ft) - Search Complete</div>
                                <div className="flex items-start gap-4 mb-2">
                                  <div className="bg-card border-2 border-border p-4 rounded flex items-center justify-center min-w-[150px]">
                                    <div className="relative">
                                      <svg width="120" height="120" viewBox="0 0 120 120" className="border border-border">
                                        <line x1="10" y1="10" x2="110" y2="110" stroke="#ff6600" strokeWidth="8"/>
                                        <line x1="110" y1="10" x2="10" y2="110" stroke="#ff6600" strokeWidth="8"/>
                                        <text x="60" y="20" textAnchor="middle" fontSize="12" fontWeight="bold" fill="#f1f5f9" stroke="#0f1419" strokeWidth="0.8">01/15 14:30</text>
                                        <text x="20" y="60" textAnchor="middle" fontSize="12" fontWeight="bold" fill="#f1f5f9" stroke="#0f1419" strokeWidth="0.8">NY-TF1</text>
                                        <text x="100" y="60" textAnchor="middle" fontSize="12" fontWeight="bold" fill="#f1f5f9" stroke="#0f1419" strokeWidth="0.8">GAS</text>
                                        <text x="60" y="100" textAnchor="middle" fontSize="12" fontWeight="bold" fill="#f1f5f9" stroke="#0f1419" strokeWidth="0.8">2L/1D</text>
                                      </svg>
                                    </div>
                                  </div>
                                  <div className="text-sm flex-1 space-y-1">
                                    <div><strong>Top:</strong> Exit date/time</div>
                                    <div><strong>Left:</strong> Team identifier</div>
                                    <div><strong>Right:</strong> Hazards found</div>
                                    <div><strong>Bottom:</strong> Victims (L=Live, D=Deceased)</div>
                                  </div>
                                </div>
                              </div>
                              
                              <div className="bg-destructive/10 border-l-4 border-destructive p-3">
                                <div className="font-semibold mb-2">X with Circle - Incomplete Search</div>
                                <div className="flex items-center gap-4 mb-2">
                                  <div className="bg-card border-2 border-border p-4 rounded flex items-center justify-center min-w-[120px]">
                                    <svg width="100" height="100" viewBox="0 0 100 100">
                                      <circle cx="50" cy="50" r="45" fill="none" stroke="#ff0000" strokeWidth="3"/>
                                      <line x1="15" y1="15" x2="85" y2="85" stroke="#ff6600" strokeWidth="6"/>
                                      <line x1="85" y1="15" x2="15" y2="85" stroke="#ff6600" strokeWidth="6"/>
                                    </svg>
                                  </div>
                                  <div className="text-sm">Entry not recommended due to hazards</div>
                                </div>
                              </div>
                              
                              <div className="bg-primary/10 border-l-4 border-primary p-3">
                                <div className="font-semibold mb-2">V Symbol - Potential Victim</div>
                                <div className="flex items-center gap-4 mb-2">
                                  <div className="bg-card border-2 border-border p-4 rounded flex items-center justify-center min-w-[120px]">
                                    <div className="text-6xl font-bold text-primary">V</div>
                                  </div>
                                  <div className="text-sm">Potential victim location - needs verification</div>
                                </div>
                              </div>
                              
                              <div className="bg-primary/10 border-l-4 border-primary p-3">
                                <div className="font-semibold mb-2">V with Circle - Live Victim</div>
                                <div className="flex items-center gap-4 mb-2">
                                  <div className="bg-card border-2 border-border p-4 rounded flex items-center justify-center min-w-[120px]">
                                    <svg width="100" height="100" viewBox="0 0 100 100">
                                      <circle cx="50" cy="50" r="40" fill="none" stroke="#10b981" strokeWidth="4"/>
                                      <path d="M 30 70 L 50 30 L 70 70" fill="none" stroke="#10b981" strokeWidth="6" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                  </div>
                                  <div className="text-sm">Live victim confirmed at this location</div>
                                </div>
                              </div>
                              
                              <div className="bg-surface-muted border-l-4 border-border p-3">
                                <div className="font-semibold mb-2">V with Horizontal Line - Deceased Victim</div>
                                <div className="flex items-center gap-4 mb-2">
                                  <div className="bg-card border-2 border-border p-4 rounded flex items-center justify-center min-w-[120px]">
                                    <svg width="100" height="100" viewBox="0 0 100 100">
                                      <path d="M 30 70 L 50 30 L 70 70" fill="none" stroke="#6b7280" strokeWidth="6" strokeLinecap="round" strokeLinejoin="round"/>
                                      <line x1="25" y1="70" x2="75" y2="70" stroke="#6b7280" strokeWidth="4"/>
                                    </svg>
                                  </div>
                                  <div className="text-sm">Deceased victim at this location</div>
                                </div>
                              </div>
                              
                              <div className="bg-primary/10 border-l-4 border-primary p-3">
                                <div className="font-semibold mb-2">X over V - Victims Removed</div>
                                <div className="flex items-center gap-4 mb-2">
                                  <div className="bg-card border-2 border-border p-4 rounded flex items-center justify-center min-w-[120px]">
                                    <svg width="100" height="100" viewBox="0 0 100 100">
                                      <path d="M 30 70 L 50 30 L 70 70" fill="none" stroke="#9333ea" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" opacity="0.5"/>
                                      <line x1="20" y1="20" x2="80" y2="80" stroke="#9333ea" strokeWidth="6"/>
                                      <line x1="80" y1="20" x2="20" y2="80" stroke="#9333ea" strokeWidth="6"/>
                                    </svg>
                                  </div>
                                  <div className="text-sm">All victims removed from this location</div>
                                </div>
                              </div>
                              
                              <div className="bg-primary/10 border-l-4 border-primary p-3">
                                <div className="font-semibold mb-2">Square Box (Empty) - Safe Structure</div>
                                <div className="flex items-center gap-4 mb-2">
                                  <div className="bg-card border-2 border-border p-4 rounded flex items-center justify-center min-w-[120px]">
                                    <div className="w-20 h-20 border-4 border-primary"></div>
                                  </div>
                                  <div className="text-sm">Structure safe for search operations</div>
                                </div>
                              </div>
                              
                              <div className="bg-primary/10 border-l-4 border-primary p-3">
                                <div className="font-semibold mb-2">Square Box with / - Damaged Structure</div>
                                <div className="flex items-center gap-4 mb-2">
                                  <div className="bg-card border-2 border-border p-4 rounded flex items-center justify-center min-w-[120px]">
                                    <div className="relative w-20 h-20 border-4 border-yellow-600">
                                      <div className="absolute inset-0 flex items-center justify-center">
                                        <div className="text-4xl font-bold text-yellow-600">/</div>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="text-sm">Structure significantly damaged - Proceed with caution</div>
                                </div>
                              </div>
                              
                              <div className="bg-destructive/10 border-l-4 border-destructive p-3">
                                <div className="font-semibold mb-2">Square Box with X - Unsafe Structure</div>
                                <div className="flex items-center gap-4 mb-2">
                                  <div className="bg-card border-2 border-border p-4 rounded flex items-center justify-center min-w-[120px]">
                                    <div className="relative w-20 h-20 border-4 border-destructive">
                                      <svg width="80" height="80" viewBox="0 0 80 80" className="absolute inset-0">
                                        <line x1="10" y1="10" x2="70" y2="70" stroke="#dc2626" strokeWidth="4"/>
                                        <line x1="70" y1="10" x2="10" y2="70" stroke="#dc2626" strokeWidth="4"/>
                                      </svg>
                                    </div>
                                  </div>
                                  <div className="text-sm">Structure unsafe - Entry not permitted</div>
                                </div>
                              </div>
                            </div>
                            <p className="text-xs text-foreground/90 mt-3">
                              <strong>Note:</strong> Markings typically made with orange spray paint. Standard size: 2 feet by 2 feet. 
                              Always include date/time and team identifier. These markings are used by FEMA USAR teams and are recognized internationally.
                            </p>
                          </CardContent>
                        </Card>

                        {/* Utility Marking */}
                        <Card className="p-4">
                          <CardHeader className="pb-3">
                            <CardTitle className="text-lg">Utility Marking (APWA Standard)</CardTitle>
                            <CardDescription>Standard color codes used by utility companies for marking underground infrastructure</CardDescription>
                          </CardHeader>
                          <CardContent className="space-y-3">
                            <div className="space-y-2">
                              <div className="flex items-center gap-3 p-2 bg-surface-muted rounded">
                                <div className="w-8 h-8 bg-red-600 rounded shrink-0" title="Red"></div>
                                <div className="text-foreground/90">
                                  <div className="font-semibold text-text-primary">Red</div>
                                  <div className="text-sm">Electric power lines, cables, conduit, and lighting cables</div>
                                </div>
                              </div>
                              <div className="flex items-center gap-3 p-2 bg-surface-muted rounded">
                                <div className="w-8 h-8 bg-yellow-500 rounded shrink-0" title="Yellow"></div>
                                <div className="text-foreground/90">
                                  <div className="font-semibold text-text-primary">Yellow</div>
                                  <div className="text-sm">Gas, oil, steam, petroleum, or gaseous materials</div>
                                </div>
                              </div>
                              <div className="flex items-center gap-3 p-2 bg-surface-muted rounded">
                                <div className="w-8 h-8 bg-orange-500 rounded shrink-0" title="Orange"></div>
                                <div className="text-foreground/90">
                                  <div className="font-semibold text-text-primary">Orange</div>
                                  <div className="text-sm">Telecommunication, alarm or signal lines, cables, or conduit</div>
                                </div>
                              </div>
                              <div className="flex items-center gap-3 p-2 bg-surface-muted rounded">
                                <div className="w-8 h-8 bg-blue-600 rounded shrink-0" title="Blue"></div>
                                <div className="text-foreground/90">
                                  <div className="font-semibold text-text-primary">Blue</div>
                                  <div className="text-sm">Potable water</div>
                                </div>
                              </div>
                              <div className="flex items-center gap-3 p-2 bg-surface-muted rounded">
                                <div className="w-8 h-8 bg-green-600 rounded shrink-0" title="Green"></div>
                                <div className="text-foreground/90">
                                  <div className="font-semibold text-text-primary">Green</div>
                                  <div className="text-sm">Sewers and drain lines</div>
                                </div>
                              </div>
                              <div className="flex items-center gap-3 p-2 bg-surface-muted rounded">
                                <div className="w-8 h-8 bg-purple-600 rounded shrink-0" title="Purple"></div>
                                <div className="text-foreground/90">
                                  <div className="font-semibold text-text-primary">Purple</div>
                                  <div className="text-sm">Reclaimed water, irrigation, and slurry lines</div>
                                </div>
                              </div>
                              <div className="flex items-center gap-3 p-2 bg-surface-muted rounded">
                                <div className="w-8 h-8 bg-pink-500 rounded shrink-0" title="Pink"></div>
                                <div className="text-foreground/90">
                                  <div className="font-semibold text-text-primary">Pink</div>
                                  <div className="text-sm">Temporary survey markings, unknown/unidentified facilities</div>
                                </div>
                              </div>
                              <div className="flex items-center gap-3 p-2 bg-surface-muted border-2 border-border rounded">
                                <div className="w-8 h-8 border-2 border-border rounded shrink-0 bg-[#fff]" title="White"></div>
                                <div className="text-foreground/90">
                                  <div className="font-semibold text-text-primary">White</div>
                                  <div className="text-sm">Proposed excavation limits or route</div>
                                </div>
                              </div>
                            </div>
                          </CardContent>
                        </Card>

                        {/* Hobo Code */}
                        <Card className="p-4">
                          <CardHeader className="pb-3">
                            <CardTitle className="text-lg">Hobo Code / Traveler's Symbols</CardTitle>
                            <CardDescription>Traditional symbols used by travelers to communicate information about locations</CardDescription>
                          </CardHeader>
                          <CardContent className="space-y-3">
                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                              <div className="bg-primary/10 border-l-4 border-primary p-4 rounded">
                                <div className="mb-3 flex items-center justify-center">
                                  <svg width="128" height="128" viewBox="0 0 60 60" className="border-2 border-border flex-shrink-0">
                                    {/* Square missing top line - only 3 sides */}
                                    <line x1="10" y1="20" x2="10" y2="50" stroke="#000" strokeWidth="2"/>
                                    <line x1="50" y1="20" x2="50" y2="50" stroke="#000" strokeWidth="2"/>
                                    <line x1="10" y1="50" x2="50" y2="50" stroke="#000" strokeWidth="2"/>
                                  </svg>
                                </div>
                                <div className="font-semibold text-sm">Safe place to camp</div>
                                <div className="text-xs text-foreground/90">Square missing top line</div>
                              </div>
                              
                              <div className="bg-primary/10 border-l-4 border-primary p-3 rounded">
                                <div className="mb-3 flex items-center justify-center">
                                  <svg width="128" height="128" viewBox="0 0 60 60" className="border-2 border-border flex-shrink-0">
                                    {/* Wavy line above X */}
                                    <path d="M 10 15 Q 15 10, 20 15 Q 25 10, 30 15 Q 35 10, 40 15 Q 45 10, 50 15" fill="none" stroke="#000" strokeWidth="2"/>
                                    {/* X below wavy line */}
                                    <line x1="20" y1="35" x2="40" y2="45" stroke="#000" strokeWidth="2"/>
                                    <line x1="40" y1="35" x2="20" y2="45" stroke="#000" strokeWidth="2"/>
                                  </svg>
                                </div>
                                <div className="font-semibold text-sm">Fresh water and safe camp</div>
                                <div className="text-xs text-foreground/90">Wavy line above X</div>
                              </div>
                              
                              <div className="bg-destructive/10 border-l-4 border-destructive p-3 rounded">
                                <div className="mb-3 flex items-center justify-center">
                                  <svg width="128" height="128" viewBox="0 0 60 60" className="border-2 border-border flex-shrink-0">
                                    {/* Three diagonal lines */}
                                    <line x1="10" y1="50" x2="30" y2="10" stroke="#000" strokeWidth="3"/>
                                    <line x1="20" y1="50" x2="40" y2="10" stroke="#000" strokeWidth="3"/>
                                    <line x1="30" y1="50" x2="50" y2="10" stroke="#000" strokeWidth="3"/>
                                  </svg>
                                </div>
                                <div className="font-semibold text-sm">Not safe, dangerous area</div>
                                <div className="text-xs text-foreground/90">Three diagonal lines</div>
                              </div>
                              
                              <div className="bg-primary/10 border-l-4 border-primary p-3 rounded">
                                <div className="mb-3 flex items-center justify-center">
                                  <svg width="128" height="128" viewBox="0 0 60 60" className="border-2 border-border flex-shrink-0">
                                    {/* Triangle with hands - hands should extend outward from triangle sides */}
                                    <path d="M 30 10 L 50 40 L 10 40 Z" fill="none" stroke="#000" strokeWidth="2"/>
                                    {/* Left hand extending from triangle */}
                                    <path d="M 10 40 L 5 35 L 5 40 L 5 45 L 10 40" fill="none" stroke="#000" strokeWidth="2"/>
                                    {/* Right hand extending from triangle */}
                                    <path d="M 50 40 L 55 35 L 55 40 L 55 45 L 50 40" fill="none" stroke="#000" strokeWidth="2"/>
                                    {/* Gun barrel in center */}
                                    <line x1="30" y1="20" x2="30" y2="35" stroke="#000" strokeWidth="2"/>
                                    <line x1="30" y1="20" x2="35" y2="20" stroke="#000" strokeWidth="2"/>
                                  </svg>
                                </div>
                                <div className="font-semibold text-sm">Man with gun lives here</div>
                                <div className="text-xs text-foreground/90">Triangle with hands</div>
                              </div>
                              
                              <div className="bg-primary/10 border-l-4 border-primary p-3 rounded">
                                <div className="mb-3 flex items-center justify-center">
                                  <svg width="128" height="128" viewBox="0 0 60 60" className="border-2 border-border flex-shrink-0">
                                    {/* Horizontal zigzag for barking/vicious dog */}
                                    <path d="M 10 30 L 15 25 L 20 30 L 25 25 L 30 30 L 35 25 L 40 30 L 45 25 L 50 30" fill="none" stroke="#000" strokeWidth="3" strokeLinecap="round"/>
                                  </svg>
                                </div>
                                <div className="font-semibold text-sm">Vicious dog lives here</div>
                                <div className="text-xs text-foreground/90">Sharp teeth / zigzag</div>
                              </div>
                              
                              <div className="bg-primary/10 border-l-4 border-primary p-3 rounded">
                                <div className="mb-3 flex items-center justify-center">
                                  <svg width="128" height="128" viewBox="0 0 60 60" className="border-2 border-border flex-shrink-0">
                                    {/* Arrow pointing right */}
                                    <line x1="10" y1="30" x2="40" y2="30" stroke="#000" strokeWidth="3"/>
                                    <path d="M 40 30 L 30 20 M 40 30 L 30 40" stroke="#000" strokeWidth="3" fill="none"/>
                                  </svg>
                                </div>
                                <div className="font-semibold text-sm">Good road to follow</div>
                                <div className="text-xs text-foreground/90">Arrow pointing direction</div>
                              </div>
                              
                              <div className="bg-primary/10 border-l-4 border-primary p-3 rounded">
                                <div className="mb-3 flex items-center justify-center">
                                  <svg width="128" height="128" viewBox="0 0 60 60" className="border-2 border-border flex-shrink-0">
                                    {/* Cat symbol - simplified cat face */}
                                    <circle cx="30" cy="25" r="8" fill="none" stroke="#000" strokeWidth="2"/>
                                    {/* Ears */}
                                    <path d="M 22 20 L 20 10 L 25 18" fill="none" stroke="#000" strokeWidth="2"/>
                                    <path d="M 38 20 L 40 10 L 35 18" fill="none" stroke="#000" strokeWidth="2"/>
                                    {/* Eyes */}
                                    <circle cx="27" cy="25" r="1.5" fill="#000"/>
                                    <circle cx="33" cy="25" r="1.5" fill="#000"/>
                                    {/* Nose and mouth */}
                                    <path d="M 30 27 L 28 30 M 30 27 L 32 30" stroke="#000" strokeWidth="2"/>
                                    {/* Whiskers */}
                                    <line x1="20" y1="25" x2="15" y2="25" stroke="#000" strokeWidth="1.5"/>
                                    <line x1="20" y1="28" x2="15" y2="30" stroke="#000" strokeWidth="1.5"/>
                                    <line x1="40" y1="25" x2="45" y2="25" stroke="#000" strokeWidth="1.5"/>
                                    <line x1="40" y1="28" x2="45" y2="30" stroke="#000" strokeWidth="1.5"/>
                                  </svg>
                                </div>
                                <div className="font-semibold text-sm">Kind lady lives here</div>
                                <div className="text-xs text-foreground/90">Cat symbol</div>
                              </div>
                              
                              <div className="bg-primary/10 border-l-4 border-primary p-4 rounded">
                                <div className="mb-3 flex items-center justify-center">
                                  <svg width="128" height="128" viewBox="0 0 60 60" className="border-2 border-border flex-shrink-0">
                                    {/* Caduceus - staff with two snakes and wings */}
                                    <line x1="30" y1="8" x2="30" y2="52" stroke="#000" strokeWidth="2.5"/>
                                    <path d="M 30 8 L 22 12 L 22 15 M 30 8 L 38 12 L 38 15" stroke="#000" strokeWidth="2" fill="none"/>
                                    <path d="M 30 12 Q 22 18, 30 24 Q 22 30, 30 36 Q 22 42, 30 48" stroke="#000" strokeWidth="2.5" fill="none"/>
                                    <path d="M 30 12 Q 38 18, 30 24 Q 38 30, 30 36 Q 38 42, 30 48" stroke="#000" strokeWidth="2.5" fill="none"/>
                                  </svg>
                                </div>
                                <div className="font-semibold text-sm">Doctor lives here</div>
                                <div className="text-xs text-foreground/90">Caduceus symbol</div>
                              </div>
                              
                              <div className="bg-primary/10 border-l-4 border-primary p-3 rounded">
                                <div className="mb-3 flex items-center justify-center">
                                  <svg width="128" height="128" viewBox="0 0 60 60" className="border-2 border-border flex-shrink-0">
                                    {/* Two shovels */}
                                    {/* Left shovel */}
                                    <line x1="15" y1="15" x2="15" y2="45" stroke="#000" strokeWidth="3"/>
                                    <path d="M 15 45 L 10 50 L 20 50 Z" fill="#000"/>
                                    {/* Right shovel */}
                                    <line x1="45" y1="15" x2="45" y2="45" stroke="#000" strokeWidth="3"/>
                                    <path d="M 45 45 L 40 50 L 50 50 Z" fill="#000"/>
                                  </svg>
                                </div>
                                <div className="font-semibold text-sm">Work available</div>
                                <div className="text-xs text-foreground/90">Two shovels</div>
                              </div>
                              
                              <div className="bg-destructive/10 border-l-4 border-destructive p-3 rounded">
                                <div className="mb-3 flex items-center justify-center">
                                  <svg width="128" height="128" viewBox="0 0 60 60" className="border-2 border-border flex-shrink-0">
                                    {/* Circle with parallel arrows pointing out */}
                                    <circle cx="30" cy="30" r="18" fill="none" stroke="#000" strokeWidth="2"/>
                                    {/* Left arrow pointing out */}
                                    <line x1="12" y1="30" x2="5" y2="30" stroke="#000" strokeWidth="2"/>
                                    <path d="M 5 30 L 10 25 M 5 30 L 10 35" stroke="#000" strokeWidth="2" fill="none"/>
                                    {/* Right arrow pointing out */}
                                    <line x1="48" y1="30" x2="55" y2="30" stroke="#000" strokeWidth="2"/>
                                    <path d="M 55 30 L 50 25 M 55 30 L 50 35" stroke="#000" strokeWidth="2" fill="none"/>
                                  </svg>
                                </div>
                                <div className="font-semibold text-sm">Get out fast, not welcome</div>
                                <div className="text-xs text-foreground/90">Circle with parallel arrows</div>
                              </div>
                              
                              <div className="bg-surface-muted border-l-4 border-border p-3 rounded">
                                <div className="mb-3 flex items-center justify-center">
                                  <svg width="128" height="128" viewBox="0 0 60 60" className="border-2 border-border flex-shrink-0">
                                    <circle cx="20" cy="20" r="12" fill="none" stroke="#000" strokeWidth="2"/>
                                    <circle cx="40" cy="20" r="12" fill="none" stroke="#000" strokeWidth="2"/>
                                    <path d="M 20 32 Q 30 38, 40 32" fill="none" stroke="#000" strokeWidth="2"/>
                                  </svg>
                                </div>
                                <div className="font-semibold text-sm">Police active, risk of arrest</div>
                                <div className="text-xs text-foreground/90">Two interlocked circles (handcuffs)</div>
                              </div>
                              
                              <div className="bg-primary/10 border-l-4 border-primary p-3 rounded">
                                <div className="mb-3 flex items-center justify-center">
                                  <svg width="128" height="128" viewBox="0 0 60 60" className="border-2 border-border flex-shrink-0">
                                    {/* House shape with roof */}
                                    <path d="M 15 40 L 15 30 L 30 18 L 45 30 L 45 40 Z" fill="none" stroke="#000" strokeWidth="2"/>
                                    {/* X through the house */}
                                    <line x1="15" y1="30" x2="45" y2="40" stroke="#000" strokeWidth="2.5"/>
                                    <line x1="45" y1="30" x2="15" y2="40" stroke="#000" strokeWidth="2.5"/>
                                  </svg>
                                </div>
                                <div className="font-semibold text-sm">House "burned" - not trustworthy</div>
                                <div className="text-xs text-foreground/90">House with X through it</div>
                              </div>
                            </div>
                            <p className="text-xs text-foreground/90 mt-3">
                              <strong>Note:</strong> These symbols were traditionally drawn with chalk or coal on fences, walls, or railroad trestles. 
                              They were designed to be easily understood by travelers while appearing innocuous to outsiders.
                            </p>
                          </CardContent>
                        </Card>

                        {/* Dead Drops and Caches */}
                        <Card className="p-4">
                          <CardHeader className="pb-3">
                            <CardTitle className="text-lg">Dead Drops and Caches</CardTitle>
                            <CardDescription>Secure methods for leaving and retrieving information or supplies without direct contact</CardDescription>
                          </CardHeader>
                          <CardContent className="space-y-4">
                            <div>
                              <div className="font-semibold mb-2 text-base">What is a Dead Drop?</div>
                              <div className="text-sm text-foreground/90 mb-3">
                                A dead drop is a pre-arranged location where information or small items can be left and retrieved without the parties meeting directly. This method provides security, deniability, and allows for asynchronous communication.
                              </div>
                            </div>

                            <div className="bg-primary/10 border-l-4 border-primary p-4 rounded">
                              <div className="font-semibold mb-2">Dead Drop Principles</div>
                              <div className="text-sm space-y-2">
                                <div>• <strong>Location Selection:</strong> Choose inconspicuous, accessible locations that blend into the environment</div>
                                <div>• <strong>Security:</strong> Use locations that provide natural cover and allow for observation before approach</div>
                                <div>• <strong>Deniability:</strong> Items should appear accidental or naturally occurring if discovered</div>
                                <div>• <strong>Timing:</strong> Establish clear schedules for placement and retrieval to avoid conflicts</div>
                                <div>• <strong>Signaling:</strong> Use subtle markers to indicate when a drop is active or has been retrieved</div>
                              </div>
                            </div>

                            <div>
                              <div className="font-semibold mb-2">Common Dead Drop Locations</div>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
                                <div className="bg-surface-muted p-3 rounded text-sm">
                                  <div className="font-semibold mb-1">Urban Settings</div>
                                  <div>• Under park benches</div>
                                  <div>• Inside newspaper boxes</div>
                                  <div>• Behind loose bricks or stones</div>
                                  <div>• Under trash cans (clean areas)</div>
                                  <div>• Inside library books (specific volumes)</div>
                                </div>
                                <div className="bg-surface-muted p-3 rounded text-sm">
                                  <div className="font-semibold mb-1">Rural/Natural Settings</div>
                                  <div>• Hollow trees or logs</div>
                                  <div>• Under specific rocks or landmarks</div>
                                  <div>• Inside fence posts (hollow)</div>
                                  <div>• Buried containers (GPS marked)</div>
                                  <div>• Inside birdhouses or feeders</div>
                                </div>
                              </div>
                            </div>

                            <div>
                              <div className="font-semibold mb-2">Dead Drop Containers</div>
                              <div className="text-sm text-foreground/90 mb-2">
                                Containers must be weatherproof, inconspicuous, and appropriate for the location:
                              </div>
                              <div className="bg-surface-muted p-3 rounded text-sm space-y-1">
                                <div>• <strong>Waterproof:</strong> Use PVC pipes, ammo cans, or sealed plastic containers</div>
                                <div>• <strong>Size:</strong> Small enough to be hidden but large enough for intended contents</div>
                                <div>• <strong>Camouflage:</strong> Paint or wrap to match surroundings (bark, stone, metal)</div>
                                <div>• <strong>Durability:</strong> Must withstand weather and time without degradation</div>
                                <div>• <strong>Access:</strong> Easy to open/close quickly without drawing attention</div>
                              </div>
                            </div>

                            <div>
                              <div className="font-semibold mb-2">Signaling Methods</div>
                              <div className="text-sm text-foreground/90 mb-2">
                                Use subtle signals to indicate drop status without compromising security:
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
                                <div className="bg-primary/10 p-3 rounded text-sm text-foreground/90">
                                  <div className="font-semibold mb-1">Visual Signals</div>
                                  <div>• Chalk marks on walls/posts</div>
                                  <div>• Small stones arranged in patterns</div>
                                  <div>• Tape on specific objects</div>
                                  <div>• Position of objects (trash can lid, etc.)</div>
                                </div>
                                <div className="bg-primary/10 p-3 rounded text-foreground/90 text-sm">
                                  <div className="font-semibold mb-1">Natural Markers</div>
                                  <div>• Specific branch position</div>
                                  <div>• Rock placement or orientation</div>
                                  <div>• Leaf/stick arrangements</div>
                                  <div>• Grass tied in specific knots</div>
                                </div>
                              </div>
                            </div>

                            <div>
                              <div className="font-semibold mb-2">Cache Types</div>
                              <div className="space-y-3 mt-2">
                                <div className="bg-primary/10 border-l-4 border-primary p-3 rounded">
                                  <div className="font-semibold mb-1">Supply Cache</div>
                                  <div className="text-sm">Pre-positioned emergency supplies (food, water, medical, tools) for retrieval during emergencies. Should be rotated regularly and checked for spoilage.</div>
                                </div>
                                <div className="bg-primary/10 border-l-4 border-primary p-3 rounded">
                                  <div className="font-semibold mb-1 text-text-primary">Information Cache</div>
                                  <div className="text-sm text-foreground/90">Documents, maps, contact information, or encrypted data stored securely. Use waterproof containers and consider multiple copies in different locations.</div>
                                </div>
                                <div className="bg-primary/10 border-l-4 border-primary p-3 rounded">
                                  <div className="font-semibold mb-1">Communication Cache</div>
                                  <div className="text-sm">Messages, updates, or status reports left for others to retrieve. Include date/time stamps and use code names or encryption for sensitive information.</div>
                                </div>
                              </div>
                            </div>

                            <div className="bg-destructive/10 border-l-4 border-destructive p-4 rounded">
                              <div className="font-semibold mb-2">Security Considerations</div>
                              <div className="text-sm space-y-2">
                                <div>• <strong>Never approach a drop if you suspect surveillance</strong> - abort and return later</div>
                                <div>• <strong>Vary your approach routes</strong> - don't establish patterns</div>
                                <div>• <strong>Observe the area first</strong> - watch for 10-15 minutes before approach</div>
                                <div>• <strong>Use dead drops only with trusted parties</strong> - verify identities through other channels</div>
                                <div>• <strong>Don't linger</strong> - place or retrieve quickly and leave immediately</div>
                                <div>• <strong>Legal compliance:</strong> Ensure all activities comply with local laws and regulations</div>
                                <div>• <strong>Document locations:</strong> Keep GPS coordinates and descriptions in secure, separate location</div>
                              </div>
                            </div>

                            <div>
                              <div className="font-semibold mb-2">Best Practices</div>
                              <div className="bg-surface-muted p-3 rounded text-sm space-y-2">
                                <div>1. <strong>Establish protocols:</strong> Agree on locations, timing, signals, and emergency procedures in advance</div>
                                <div>2. <strong>Test locations:</strong> Verify accessibility and security before actual use</div>
                                <div>3. <strong>Maintain operational security:</strong> Don't discuss drop locations or contents openly</div>
                                <div>4. <strong>Regular maintenance:</strong> Check caches periodically for condition and security</div>
                                <div>5. <strong>Have backup locations:</strong> Maintain multiple drop sites in case one is compromised</div>
                                <div>6. <strong>Use encryption:</strong> Encrypt sensitive information even in dead drops</div>
                                <div>7. <strong>Document everything:</strong> Keep records of what was placed, when, and by whom</div>
                              </div>
                            </div>

                            <p className="text-xs text-foreground/90 mt-4 italic">
                              <strong>Note:</strong> Dead drops and caches are useful for emergency preparedness, family communication during disasters, and secure information exchange. Always ensure activities are legal and ethical. In true emergency situations, these methods can help maintain communication when normal channels are unavailable.
                            </p>
                          </CardContent>
                        </Card>
                      </div>
                    </div>
                  </TabsContent>
                  
                  <TabsContent value="protocols" className="space-y-6 mt-4">
                    <div>
                      <div className="mb-4">
                        <h3 className="font-semibold text-lg mb-2">Communication Protocols</h3>
                        <p className="text-sm text-foreground/90">
                          Define specific procedures for different emergency scenarios and communication challenges.
                        </p>
                      </div>
                      
                      <div className="space-y-6">
                        <div>
                          <Label htmlFor="power-outage-protocol">During Power Outage</Label>
                          <Textarea
                            id="power-outage-protocol"
                            value={planData.communication_plan?.communication_protocols?.during_power_outage ?? ''}
                            onChange={(e) => {
                              setPlanData(prev => ({
                                ...prev,
                                communication_plan: {
                                  ...(prev.communication_plan ?? {}),
                                  communication_protocols: {
                                    ...(prev.communication_plan?.communication_protocols ?? {}),
                                    during_power_outage: e.target.value
                                  }
                                }
                              }));
                            }}
                            placeholder="How will family members communicate when power is out? (e.g., Use battery-powered radio, meet at designated location, use car charger for phones)"
                            rows={4}
                          />
                        </div>
                        
                        <div>
                          <Label htmlFor="internet-outage-protocol">During Internet Outage</Label>
                          <Textarea
                            id="internet-outage-protocol"
                            value={planData.communication_plan?.communication_protocols?.during_internet_outage ?? ''}
                            onChange={(e) => {
                              setPlanData(prev => ({
                                ...prev,
                                communication_plan: {
                                  ...(prev.communication_plan ?? {}),
                                  communication_protocols: {
                                    ...(prev.communication_plan?.communication_protocols ?? {}),
                                    during_internet_outage: e.target.value
                                  }
                                }
                              }));
                            }}
                            placeholder="How will family members communicate when internet is down? (e.g., Use phone calls, text messages, or in-person check-ins)"
                            rows={4}
                          />
                        </div>
                        
                        <div>
                          <Label htmlFor="phone-outage-protocol">During Phone Outage</Label>
                          <Textarea
                            id="phone-outage-protocol"
                            value={planData.communication_plan?.communication_protocols?.during_phone_outage ?? ''}
                            onChange={(e) => {
                              setPlanData(prev => ({
                                ...prev,
                                communication_plan: {
                                  ...(prev.communication_plan ?? {}),
                                  communication_protocols: {
                                    ...(prev.communication_plan?.communication_protocols ?? {}),
                                    during_phone_outage: e.target.value
                                  }
                                }
                              }));
                            }}
                            placeholder="How will family members communicate when phone service is unavailable? (e.g., Use walkie-talkies, meet at designated location, use social media if internet available)"
                            rows={4}
                          />
                        </div>
                        
                        <div>
                          <Label htmlFor="meeting-point-procedures">Meeting Point Procedures</Label>
                          <Textarea
                            id="meeting-point-procedures"
                            value={planData.communication_plan?.communication_protocols?.meeting_point_procedures ?? ''}
                            onChange={(e) => {
                              setPlanData(prev => ({
                                ...prev,
                                communication_plan: {
                                  ...(prev.communication_plan ?? {}),
                                  communication_protocols: {
                                    ...(prev.communication_plan?.communication_protocols ?? {}),
                                    meeting_point_procedures: e.target.value
                                  }
                                }
                              }));
                            }}
                            placeholder="Procedures for meeting at designated locations (e.g., Wait time, what to do if someone doesn't arrive, how to leave messages)"
                            rows={4}
                          />
                        </div>
                        
                        <div>
                          <Label htmlFor="school-work-procedures">School/Work Communication Procedures</Label>
                          <Textarea
                            id="school-work-procedures"
                            value={planData.communication_plan?.communication_protocols?.school_work_procedures ?? ''}
                            onChange={(e) => {
                              setPlanData(prev => ({
                                ...prev,
                                communication_plan: {
                                  ...(prev.communication_plan ?? {}),
                                  communication_protocols: {
                                    ...(prev.communication_plan?.communication_protocols ?? {}),
                                    school_work_procedures: e.target.value
                                  }
                                }
                              }));
                            }}
                            placeholder="How to communicate during school or work hours? (e.g., Contact school/work directly, emergency contact procedures, pickup authorization)"
                            rows={4}
                          />
                        </div>
                      </div>

                      {/* Status Codes */}
                      <div className="mt-6">
                        <div className="flex justify-between items-center mb-4">
                          <div>
                            <h3 className="font-semibold text-lg mb-2">Status Codes</h3>
                            <p className="text-sm text-foreground/90">
                              Define your own status codes for rapid communication. Use these codes in radio communications, text messages, or other quick check-ins.
                            </p>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const newCode = {
                                id: `status_${Date.now()}`,
                                code: '',
                                meaning: '',
                                color: '' // Optional - user can leave empty if not color-based
                              };
                              setPlanData(prev => ({
                                ...prev,
                                communication_plan: {
                                  ...(prev.communication_plan ?? {}),
                                  status_codes: [...(prev.communication_plan?.status_codes ?? []), newCode]
                                }
                              }));
                            }}
                          >
                            <Plus className="h-4 w-4 mr-1" />
                            Add Status Code
                          </Button>
                        </div>
                        {(planData.communication_plan?.status_codes?.length ?? 0) === 0 ? (
                          <p className="text-sm text-text-muted italic">No status codes defined yet. Add your first status code to get started.</p>
                        ) : (
                          <div className="space-y-3">
                            {(planData.communication_plan?.status_codes ?? []).map((status) => (
                              <Card key={status.id || status.code} className="p-4">
                                <div className="flex items-center justify-between mb-3">
                                  <span className="text-sm text-foreground/90">Status Code</span>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => {
                                      const updated = (planData.communication_plan?.status_codes ?? []).filter(s => s.id !== status.id && s.code !== status.code);
                                      setPlanData(prev => ({
                                        ...prev,
                                        communication_plan: {
                                          ...(prev.communication_plan ?? {}),
                                          status_codes: updated
                                        }
                                      }));
                                    }}
                                    className="text-destructive"
                                  >
                                    <X className="h-4 w-4" />
                                  </Button>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                  <div>
                                    <Label>Code Name</Label>
                                    <Input
                                      value={status.code || ''}
                                      onChange={(e) => {
                                        const updated = (planData.communication_plan?.status_codes ?? []).map(s =>
                                          (s.id === status.id || s.code === status.code) ? { ...s, code: e.target.value } : s
                                        );
                                        setPlanData(prev => ({
                                          ...prev,
                                          communication_plan: {
                                            ...(prev.communication_plan ?? {}),
                                            status_codes: updated
                                          }
                                        }));
                                      }}
                                      placeholder="e.g., GREEN, RED, 1, A, SAFE, HELP"
                                    />
                                  </div>
                                  <div>
                                    <Label>Color (Optional)</Label>
                                    <Select
                                      value={status.color || ''}
                                      onValueChange={(value) => {
                                        const updated = (planData.communication_plan?.status_codes ?? []).map(s =>
                                          (s.id === status.id || s.code === status.code) ? { ...s, color: value === 'none' ? '' : value } : s
                                        );
                                        setPlanData(prev => ({
                                          ...prev,
                                          communication_plan: {
                                            ...(prev.communication_plan ?? {}),
                                            status_codes: updated
                                          }
                                        }));
                                      }}
                                    >
                                      <SelectTrigger>
                                        <SelectValue placeholder="No color" />
                                      </SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="none">No Color</SelectItem>
                                        <SelectItem value="green">Green</SelectItem>
                                        <SelectItem value="yellow">Yellow</SelectItem>
                                        <SelectItem value="red">Red</SelectItem>
                                        <SelectItem value="blue">Blue</SelectItem>
                                        <SelectItem value="purple">Purple</SelectItem>
                                        <SelectItem value="orange">Orange</SelectItem>
                                        <SelectItem value="black">Black</SelectItem>
                                        <SelectItem value="gray">Gray</SelectItem>
                                      </SelectContent>
                                    </Select>
                                  </div>
                                  <div className="md:col-span-2">
                                    <Label>Meaning</Label>
                                    <Input
                                      value={status.meaning || ''}
                                      onChange={(e) => {
                                        const updated = (planData.communication_plan?.status_codes ?? []).map(s =>
                                          (s.id === status.id || s.code === status.code) ? { ...s, meaning: e.target.value } : s
                                        );
                                        setPlanData(prev => ({
                                          ...prev,
                                          communication_plan: {
                                            ...(prev.communication_plan ?? {}),
                                            status_codes: updated
                                          }
                                        }));
                                      }}
                                      placeholder="What this status code means (e.g., 'All clear, safe and secure')"
                                    />
                                  </div>
                                </div>
                              </Card>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Rally Points / Meeting Places (L1/L2/L3) */}
                      <div className="mt-6">
                        <div className="flex justify-between items-center mb-3">
                          <div>
                            <h3 className="font-semibold text-lg">Meeting Places (Rally Points)</h3>
                            <p className="text-sm text-foreground/90">
                              Designated meeting locations for family members. In your binder these print as L1 (Home), L2 (Nearby), L3 (Out of town).
                            </p>
                          </div>
                        </div>
                        <div className="space-y-4">
                          {(planData.communication_plan?.rally_points ?? [
                            { id: 'home', name: 'Home', location: '', coordinates: '', notes: '' },
                            { id: 'neighborhood', name: 'Neighborhood Safe Spot', location: '', coordinates: '', notes: '' },
                            { id: 'out_of_area', name: 'Out-of-Area Meeting Point', location: '', coordinates: '', notes: '' }
                          ]).map((point) => {
                            const levelTag = point.id === 'home' ? 'L1' : point.id === 'neighborhood' ? 'L2' : 'L3';
                            return (
                            <Card key={point.id} className="p-4">
                              <div className="space-y-3">
                                <div className="flex items-center gap-2">
                                  <span className="text-xs font-medium px-2 py-0.5 rounded bg-primary/15 text-primary">{levelTag}</span>
                                  <Label>{point.name}</Label>
                                </div>
                                <div>
                                  <Input
                                    value={point.location || ''}
                                    onChange={(e) => {
                                      const updated = (planData.communication_plan?.rally_points ?? []).map(p =>
                                        p.id === point.id ? { ...p, location: e.target.value } : p
                                      );
                                      setPlanData(prev => ({
                                        ...prev,
                                        communication_plan: {
                                          ...(prev.communication_plan ?? {}),
                                          rally_points: updated
                                        }
                                      }));
                                    }}
                                    placeholder="Address or description"
                                  />
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                  <div>
                                    <Label>Coordinates (optional)</Label>
                                    <Input
                                      value={point.coordinates || ''}
                                      onChange={(e) => {
                                        const updated = (planData.communication_plan?.rally_points ?? []).map(p =>
                                          p.id === point.id ? { ...p, coordinates: e.target.value } : p
                                        );
                                        setPlanData(prev => ({
                                          ...prev,
                                          communication_plan: {
                                            ...(prev.communication_plan ?? {}),
                                            rally_points: updated
                                          }
                                        }));
                                      }}
                                      placeholder="Lat, Long or GPS coordinates"
                                    />
                                  </div>
                                  <div>
                                    <Label>Notes</Label>
                                    <Input
                                      value={point.notes || ''}
                                      onChange={(e) => {
                                        const updated = (planData.communication_plan?.rally_points ?? []).map(p =>
                                          p.id === point.id ? { ...p, notes: e.target.value } : p
                                        );
                                        setPlanData(prev => ({
                                          ...prev,
                                          communication_plan: {
                                            ...(prev.communication_plan ?? {}),
                                            rally_points: updated
                                          }
                                        }));
                                      }}
                                      placeholder="Landmarks, directions, etc."
                                    />
                                  </div>
                                </div>
                              </div>
                            </Card>
                            );
                          })}
                        </div>
                      </div>

                      {/* Check-In Schedules */}
                      <div className="mt-6">
                        <h3 className="font-semibold text-lg mb-3">Check-In Schedules</h3>
                        <p className="text-sm text-foreground/90 mb-4">
                          Define how often family members should check in during different situations
                        </p>
                        <div className="space-y-4">
                          <Card className="p-4">
                            <div className="mb-3">
                              <Label className="font-semibold">Normal Conditions</Label>
                              <p className="text-sm text-foreground/90">Regular check-in schedule</p>
                            </div>
                            <div className="flex gap-2">
                              <Input
                                type="number"
                                value={planData.communication_plan?.check_in_schedule?.normal?.interval ?? '24'}
                                onChange={(e) => {
                                  setPlanData(prev => ({
                                    ...prev,
                                    communication_plan: {
                                      ...(prev.communication_plan ?? {}),
                                      check_in_schedule: {
                                        ...(prev.communication_plan?.check_in_schedule ?? {}),
                                        normal: {
                                          ...(prev.communication_plan?.check_in_schedule?.normal ?? {}),
                                          interval: e.target.value
                                        }
                                      }
                                    }
                                  }));
                                }}
                                className="w-20"
                              />
                              <Select
                                value={planData.communication_plan?.check_in_schedule?.normal?.unit ?? 'hours'}
                                onValueChange={(value) => {
                                  setPlanData(prev => ({
                                    ...prev,
                                    communication_plan: {
                                      ...(prev.communication_plan ?? {}),
                                      check_in_schedule: {
                                        ...(prev.communication_plan?.check_in_schedule ?? {}),
                                        normal: {
                                          ...(prev.communication_plan?.check_in_schedule?.normal ?? {}),
                                          unit: value
                                        }
                                      }
                                    }
                                  }));
                                }}
                              >
                                <SelectTrigger className="w-32">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="minutes">Minutes</SelectItem>
                                  <SelectItem value="hours">Hours</SelectItem>
                                  <SelectItem value="days">Days</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </Card>

                          <Card className="p-4">
                            <div className="mb-3">
                              <Label className="font-semibold">Emergency Conditions</Label>
                              <p className="text-sm text-foreground/90">During active emergency</p>
                            </div>
                            <div className="flex gap-2">
                              <Input
                                type="number"
                                value={planData.communication_plan?.check_in_schedule?.emergency?.interval ?? '2'}
                                onChange={(e) => {
                                  setPlanData(prev => ({
                                    ...prev,
                                    communication_plan: {
                                      ...(prev.communication_plan ?? {}),
                                      check_in_schedule: {
                                        ...(prev.communication_plan?.check_in_schedule ?? {}),
                                        emergency: {
                                          ...(prev.communication_plan?.check_in_schedule?.emergency ?? {}),
                                          interval: e.target.value
                                        }
                                      }
                                    }
                                  }));
                                }}
                                className="w-20"
                              />
                              <Select
                                value={planData.communication_plan?.check_in_schedule?.emergency?.unit ?? 'hours'}
                                onValueChange={(value) => {
                                  setPlanData(prev => ({
                                    ...prev,
                                    communication_plan: {
                                      ...(prev.communication_plan ?? {}),
                                      check_in_schedule: {
                                        ...(prev.communication_plan?.check_in_schedule ?? {}),
                                        emergency: {
                                          ...(prev.communication_plan?.check_in_schedule?.emergency ?? {}),
                                          unit: value
                                        }
                                      }
                                    }
                                  }));
                                }}
                              >
                                <SelectTrigger className="w-32">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="minutes">Minutes</SelectItem>
                                  <SelectItem value="hours">Hours</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </Card>

                          <Card className="p-4">
                            <div className="mb-3">
                              <Label className="font-semibold">Extreme Emergency</Label>
                              <p className="text-sm text-foreground/90">Life-threatening situations</p>
                            </div>
                            <div className="flex gap-2">
                              <Input
                                type="number"
                                value={planData.communication_plan?.check_in_schedule?.extreme?.interval ?? '30'}
                                onChange={(e) => {
                                  setPlanData(prev => ({
                                    ...prev,
                                    communication_plan: {
                                      ...(prev.communication_plan ?? {}),
                                      check_in_schedule: {
                                        ...(prev.communication_plan?.check_in_schedule ?? {}),
                                        extreme: {
                                          ...(prev.communication_plan?.check_in_schedule?.extreme ?? {}),
                                          interval: e.target.value
                                        }
                                      }
                                    }
                                  }));
                                }}
                                className="w-20"
                              />
                              <Select
                                value={planData.communication_plan?.check_in_schedule?.extreme?.unit ?? 'minutes'}
                                onValueChange={(value) => {
                                  setPlanData(prev => ({
                                    ...prev,
                                    communication_plan: {
                                      ...(prev.communication_plan ?? {}),
                                      check_in_schedule: {
                                        ...(prev.communication_plan?.check_in_schedule ?? {}),
                                        extreme: {
                                          ...(prev.communication_plan?.check_in_schedule?.extreme ?? {}),
                                          unit: value
                                        }
                                      }
                                    }
                                  }));
                                }}
                              >
                                <SelectTrigger className="w-32">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="minutes">Minutes</SelectItem>
                                  <SelectItem value="hours">Hours</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </Card>
                        </div>
                      </div>
                    </div>
                  </TabsContent>
                  
                  <TabsContent value="network" className="space-y-6 mt-4">
                    <div>
                      <div className="mb-4">
                        <h3 className="font-semibold text-lg mb-2">Neighbor Network</h3>
                        <p className="text-sm text-foreground/90">
                          Build a network of trusted neighbors who can help during emergencies. This creates a local support system for mutual aid and information sharing.
                        </p>
                      </div>
                      
                      {(planData.communication_plan?.neighbor_network?.length ?? 0) === 0 ? (
                        <p className="text-sm text-text-muted italic mb-4">No neighbors added yet</p>
                      ) : (
                        <div className="space-y-4 mb-4">
                          {(planData.communication_plan?.neighbor_network ?? []).map((neighbor, index) => (
                            <Card key={neighbor.id || index} className="p-4">
                              <div className="flex items-center justify-between mb-3">
                                <span className="font-semibold">Neighbor {index + 1}</span>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    const updated = (planData.communication_plan?.neighbor_network ?? []).filter(n => n.id !== neighbor.id);
                                    setPlanData(prev => ({
                                      ...prev,
                                      communication_plan: {
                                        ...(prev.communication_plan ?? {}),
                                        neighbor_network: updated
                                      }
                                    }));
                                  }}
                                  className="text-destructive"
                                >
                                  <X className="h-4 w-4" />
                                </Button>
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                  <Label>Name</Label>
                                  <Input
                                    value={neighbor.name || ''}
                                    onChange={(e) => {
                                      const updated = (planData.communication_plan?.neighbor_network ?? []).map(n =>
                                        n.id === neighbor.id ? { ...n, name: e.target.value } : n
                                      );
                                      setPlanData(prev => ({
                                        ...prev,
                                        communication_plan: {
                                          ...(prev.communication_plan ?? {}),
                                          neighbor_network: updated
                                        }
                                      }));
                                    }}
                                    placeholder="Neighbor's name"
                                  />
                                </div>
                                <div>
                                  <Label>Address</Label>
                                  <Input
                                    value={neighbor.address || ''}
                                    onChange={(e) => {
                                      const updated = (planData.communication_plan?.neighbor_network ?? []).map(n =>
                                        n.id === neighbor.id ? { ...n, address: e.target.value } : n
                                      );
                                      setPlanData(prev => ({
                                        ...prev,
                                        communication_plan: {
                                          ...(prev.communication_plan ?? {}),
                                          neighbor_network: updated
                                        }
                                      }));
                                    }}
                                    placeholder="Street address"
                                  />
                                </div>
                                <div>
                                  <Label>Phone Number</Label>
                                  <Input
                                    value={neighbor.phone || ''}
                                    onChange={(e) => {
                                      const updated = (planData.communication_plan?.neighbor_network ?? []).map(n =>
                                        n.id === neighbor.id ? { ...n, phone: e.target.value } : n
                                      );
                                      setPlanData(prev => ({
                                        ...prev,
                                        communication_plan: {
                                          ...(prev.communication_plan ?? {}),
                                          neighbor_network: updated
                                        }
                                      }));
                                    }}
                                    placeholder="(555) 123-4567"
                                  />
                                </div>
                                <div>
                                  <Label>Email</Label>
                                  <Input
                                    type="email"
                                    value={neighbor.email || ''}
                                    onChange={(e) => {
                                      const updated = (planData.communication_plan?.neighbor_network ?? []).map(n =>
                                        n.id === neighbor.id ? { ...n, email: e.target.value } : n
                                      );
                                      setPlanData(prev => ({
                                        ...prev,
                                        communication_plan: {
                                          ...(prev.communication_plan ?? {}),
                                          neighbor_network: updated
                                        }
                                      }));
                                    }}
                                    placeholder="email@example.com"
                                  />
                                </div>
                                <div className="md:col-span-2">
                                  <Label>Skills/Resources</Label>
                                  <Input
                                    value={neighbor.skills || ''}
                                    onChange={(e) => {
                                      const updated = (planData.communication_plan?.neighbor_network ?? []).map(n =>
                                        n.id === neighbor.id ? { ...n, skills: e.target.value } : n
                                      );
                                      setPlanData(prev => ({
                                        ...prev,
                                        communication_plan: {
                                          ...(prev.communication_plan ?? {}),
                                          neighbor_network: updated
                                        }
                                      }));
                                    }}
                                    placeholder="e.g., Medical training, generator, tools, vehicle"
                                  />
                                </div>
                                <div className="md:col-span-2">
                                  <Label>Notes</Label>
                                  <Textarea
                                    value={neighbor.notes || ''}
                                    onChange={(e) => {
                                      const updated = (planData.communication_plan?.neighbor_network ?? []).map(n =>
                                        n.id === neighbor.id ? { ...n, notes: e.target.value } : n
                                      );
                                      setPlanData(prev => ({
                                        ...prev,
                                        communication_plan: {
                                          ...(prev.communication_plan ?? {}),
                                          neighbor_network: updated
                                        }
                                      }));
                                    }}
                                    placeholder="Additional notes about this neighbor"
                                    rows={2}
                                  />
                                </div>
                              </div>
                            </Card>
                          ))}
                        </div>
                      )}
                      
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const newNeighbor = {
                            id: `neighbor_${Date.now()}`,
                            name: '',
                            address: '',
                            phone: '',
                            email: '',
                            skills: '',
                            notes: ''
                          };
                          setPlanData(prev => ({
                            ...prev,
                            communication_plan: {
                              ...(prev.communication_plan ?? {}),
                              neighbor_network: [...(prev.communication_plan?.neighbor_network ?? []), newNeighbor]
                            }
                          }));
                        }}
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        Add Neighbor
                      </Button>
                    </div>
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Medical Information moved to Family Members section */}

          {/* Medical Information moved to Family Members section */}

          <TabsContent value="home">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Home className="h-5 w-5 text-primary" />
                  Home Information
                </CardTitle>
                <CardDescription>
                  Important information about your home and utilities
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <h3 className="font-semibold mb-4 text-lg">Home Address</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <Label htmlFor="home-street">Street Address</Label>
                      <Input
                        id="home-street"
                        value={planData.home_info.address.street}
                        onChange={(e) => {
                          setPlanData(prev => ({
                            ...prev,
                            home_info: {
                              ...prev.home_info,
                              address: {
                                ...prev.home_info.address,
                                street: e.target.value
                              }
                            }
                          }));
                        }}
                        placeholder="123 Main Street"
                      />
                    </div>
                    <div>
                      <Label htmlFor="home-city">City</Label>
                      <Input
                        id="home-city"
                        value={planData.home_info.address.city}
                        onChange={(e) => {
                          setPlanData(prev => ({
                            ...prev,
                            home_info: {
                              ...prev.home_info,
                              address: {
                                ...prev.home_info.address,
                                city: e.target.value
                              }
                            }
                          }));
                        }}
                        placeholder="City"
                      />
                    </div>
                    <div>
                      <Label htmlFor="home-state">State</Label>
                      <Input
                        id="home-state"
                        value={planData.home_info.address.state}
                        onChange={(e) => {
                          setPlanData(prev => ({
                            ...prev,
                            home_info: {
                              ...prev.home_info,
                              address: {
                                ...prev.home_info.address,
                                state: e.target.value.toUpperCase()
                              }
                            }
                          }));
                        }}
                        placeholder="ST"
                        maxLength={2}
                      />
                    </div>
                    <div>
                      <Label htmlFor="home-zip">ZIP Code</Label>
                      <Input
                        id="home-zip"
                        value={planData.home_info.address.zip}
                        onChange={(e) => {
                          setPlanData(prev => ({
                            ...prev,
                            home_info: {
                              ...prev.home_info,
                              address: {
                                ...prev.home_info.address,
                                zip: e.target.value
                              }
                            }
                          }));
                        }}
                        placeholder="12345"
                      />
                    </div>
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold mb-4 text-lg">Utility Shutoff Locations</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Label htmlFor="shutoff-gas">Gas Shutoff</Label>
                      <Input
                        id="shutoff-gas"
                        value={planData.home_info.utility_shutoffs.gas}
                        onChange={(e) => {
                          setPlanData(prev => ({
                            ...prev,
                            home_info: {
                              ...prev.home_info,
                              utility_shutoffs: {
                                ...prev.home_info.utility_shutoffs,
                                gas: e.target.value
                              }
                            }
                          }));
                        }}
                        placeholder="Location description"
                      />
                    </div>
                    <div>
                      <Label htmlFor="shutoff-water">Water Shutoff</Label>
                      <Input
                        id="shutoff-water"
                        value={planData.home_info.utility_shutoffs.water}
                        onChange={(e) => {
                          setPlanData(prev => ({
                            ...prev,
                            home_info: {
                              ...prev.home_info,
                              utility_shutoffs: {
                                ...prev.home_info.utility_shutoffs,
                                water: e.target.value
                              }
                            }
                          }));
                        }}
                        placeholder="Location description"
                      />
                    </div>
                    <div>
                      <Label htmlFor="shutoff-electricity">Electricity Shutoff</Label>
                      <Input
                        id="shutoff-electricity"
                        value={planData.home_info.utility_shutoffs.electricity}
                        onChange={(e) => {
                          setPlanData(prev => ({
                            ...prev,
                            home_info: {
                              ...prev.home_info,
                              utility_shutoffs: {
                                ...prev.home_info.utility_shutoffs,
                                electricity: e.target.value
                              }
                            }
                          }));
                        }}
                        placeholder="Location description"
                      />
                    </div>
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold mb-4 text-lg">Utility shut-off photos (for binder)</h3>
                  <p className="text-sm text-muted-foreground mb-4">Optional: add a photo of each shut-off (valve, meter, breaker) so anyone can find it quickly. These appear in your binder export.</p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {['gas', 'water', 'electricity'].map((key) => {
                      const label = key.charAt(0).toUpperCase() + key.slice(1);
                      const photo = planData.home_info?.utility_photos?.[key] ?? { imageData: null, caption: '' };
                      return (
                        <Card key={key} className="p-4">
                          <div className="space-y-3">
                            <Label className="font-medium">{label}</Label>
                            {photo.imageData && (
                              <div className="relative">
                                <img src={photo.imageData} alt={`${label} shut-off`} className="w-full h-32 object-cover rounded border" />
                                <Button
                                  type="button"
                                  variant="secondary"
                                  size="sm"
                                  className="absolute top-1 right-1"
                                  onClick={() => setPlanData(prev => ({
                                    ...prev,
                                    home_info: {
                                      ...prev.home_info,
                                      utility_photos: {
                                        ...(prev.home_info?.utility_photos || { gas: { imageData: null, caption: '' }, water: { imageData: null, caption: '' }, electricity: { imageData: null, caption: '' } }),
                                        [key]: { imageData: null, caption: prev.home_info?.utility_photos?.[key]?.caption || '' }
                                      }
                                    }
                                  }))}
                                >
                                  <Trash2 className="h-3 w-3" />
                                </Button>
                              </div>
                            )}
                            <Input
                              type="file"
                              accept="image/*"
                              className="text-sm"
                              onChange={async (e) => {
                                const file = e.target.files?.[0];
                                if (!file || !file.type.startsWith('image/')) return;
                                try {
                                  const dataUrl = await new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file); });
                                  setPlanData(prev => ({
                                    ...prev,
                                    home_info: {
                                      ...prev.home_info,
                                      utility_photos: {
                                        ...(prev.home_info?.utility_photos || { gas: { imageData: null, caption: '' }, water: { imageData: null, caption: '' }, electricity: { imageData: null, caption: '' } }),
                                        [key]: { imageData: dataUrl, caption: prev.home_info?.utility_photos?.[key]?.caption || '' }
                                      }
                                    }
                                  }));
                                } catch (_) {}
                                e.target.value = '';
                              }}
                            />
                            <Input
                              placeholder="Caption (e.g. Turn left at furnace)"
                              value={photo.caption || ''}
                              onChange={(e) => setPlanData(prev => ({
                                ...prev,
                                home_info: {
                                  ...prev.home_info,
                                  utility_photos: {
                                    ...(prev.home_info?.utility_photos || { gas: { imageData: null, caption: '' }, water: { imageData: null, caption: '' }, electricity: { imageData: null, caption: '' } }),
                                    [key]: { imageData: photo.imageData, caption: e.target.value }
                                  }
                                }
                              }))}
                            />
                          </div>
                        </Card>
                      );
                    })}
                  </div>
                </div>
                <div>
                  <Label htmlFor="home-layout">Home Layout Notes</Label>
                  <Textarea
                    id="home-layout"
                    value={planData.home_info.layout_notes}
                    onChange={(e) => {
                      setPlanData(prev => ({
                        ...prev,
                        home_info: {
                          ...prev.home_info,
                          layout_notes: e.target.value
                        }
                      }));
                    }}
                    placeholder="Important notes about your home layout, exits, etc."
                    rows={4}
                  />
                </div>
                <div>
                  <h3 className="font-semibold mb-4 text-lg">Home Warranty</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Label htmlFor="hw-company">Company</Label>
                      <Input
                        id="hw-company"
                        value={planData.home_info.home_warranty?.company ?? ''}
                        onChange={(e) => {
                          setPlanData(prev => ({
                            ...prev,
                            home_info: {
                              ...prev.home_info,
                              home_warranty: { ...(prev.home_info.home_warranty || {}), company: e.target.value }
                            }
                          }));
                        }}
                        placeholder="Warranty company name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="hw-contract">Contract / Policy number</Label>
                      <Input
                        id="hw-contract"
                        value={planData.home_info.home_warranty?.contract_number ?? ''}
                        onChange={(e) => {
                          setPlanData(prev => ({
                            ...prev,
                            home_info: {
                              ...prev.home_info,
                              home_warranty: { ...(prev.home_info.home_warranty || {}), contract_number: e.target.value }
                            }
                          }));
                        }}
                        placeholder="Optional"
                      />
                    </div>
                    <div>
                      <Label htmlFor="hw-expiry">Expiry</Label>
                      <Input
                        id="hw-expiry"
                        value={planData.home_info.home_warranty?.expiry ?? ''}
                        onChange={(e) => {
                          setPlanData(prev => ({
                            ...prev,
                            home_info: {
                              ...prev.home_info,
                              home_warranty: { ...(prev.home_info.home_warranty || {}), expiry: e.target.value }
                            }
                          }));
                        }}
                        placeholder="e.g. 2026"
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Household medical (advance directive, POA, etc.) - in Family tab below members */}

          <TabsContent value="pets">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <PawPrint className="h-5 w-5 text-primary" />
                  Pet Plans
                </CardTitle>
                <CardDescription>
                  Emergency plans for your pets and animals
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <Label>Pets</Label>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const newPet = {
                          id: `pet_${Date.now()}`,
                          name: '',
                          type: '',
                          breed: '',
                          age: '',
                          special_needs: '',
                          microchip_id: '',
                          imageData: null,
                          vetName: '',
                          vetPhone: ''
                        };
                        setPlanData(prev => ({
                          ...prev,
                          pet_plans: {
                            ...prev.pet_plans,
                            pets: [...prev.pet_plans.pets, newPet]
                          }
                        }));
                      }}
                      className="flex items-center gap-1"
                    >
                      <Plus className="h-3 w-3" />
                      Add Pet
                    </Button>
                  </div>
                  {planData.pet_plans.pets.length === 0 ? (
                    <p className="text-sm text-text-muted italic">No pets added yet</p>
                  ) : (
                    <div className="space-y-3">
                      {planData.pet_plans.pets.map((pet) => (
                        <Card key={pet.id} className="p-4">
                          <div className="flex justify-end mb-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setPlanData(prev => ({
                                  ...prev,
                                  pet_plans: {
                                    ...prev.pet_plans,
                                    pets: prev.pet_plans.pets.filter(p => p.id !== pet.id)
                                  }
                                }));
                              }}
                              className="h-6 w-6 p-0 text-destructive"
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <Input
                              value={pet.name}
                              onChange={(e) => {
                                const updated = planData.pet_plans.pets.map(p =>
                                  p.id === pet.id ? { ...p, name: e.target.value } : p
                                );
                                setPlanData(prev => ({
                                  ...prev,
                                  pet_plans: { ...prev.pet_plans, pets: updated }
                                }));
                              }}
                              placeholder="Pet name"
                            />
                            <Input
                              value={pet.type}
                              onChange={(e) => {
                                const updated = planData.pet_plans.pets.map(p =>
                                  p.id === pet.id ? { ...p, type: e.target.value } : p
                                );
                                setPlanData(prev => ({
                                  ...prev,
                                  pet_plans: { ...prev.pet_plans, pets: updated }
                                }));
                              }}
                              placeholder="Type (Dog, Cat, etc.)"
                            />
                            <Input
                              value={pet.breed}
                              onChange={(e) => {
                                const updated = planData.pet_plans.pets.map(p =>
                                  p.id === pet.id ? { ...p, breed: e.target.value } : p
                                );
                                setPlanData(prev => ({
                                  ...prev,
                                  pet_plans: { ...prev.pet_plans, pets: updated }
                                }));
                              }}
                              placeholder="Breed"
                            />
                            <Input
                              value={pet.age}
                              onChange={(e) => {
                                const updated = planData.pet_plans.pets.map(p =>
                                  p.id === pet.id ? { ...p, age: e.target.value } : p
                                );
                                setPlanData(prev => ({
                                  ...prev,
                                  pet_plans: { ...prev.pet_plans, pets: updated }
                                }));
                              }}
                              placeholder="Age"
                            />
                            <Input
                              value={pet.microchip_id}
                              onChange={(e) => {
                                const updated = planData.pet_plans.pets.map(p =>
                                  p.id === pet.id ? { ...p, microchip_id: e.target.value } : p
                                );
                                setPlanData(prev => ({
                                  ...prev,
                                  pet_plans: { ...prev.pet_plans, pets: updated }
                                }));
                              }}
                              placeholder="Microchip ID"
                            />
                            <Input
                              value={pet.special_needs}
                              onChange={(e) => {
                                const updated = planData.pet_plans.pets.map(p =>
                                  p.id === pet.id ? { ...p, special_needs: e.target.value } : p
                                );
                                setPlanData(prev => ({
                                  ...prev,
                                  pet_plans: { ...prev.pet_plans, pets: updated }
                                }));
                              }}
                              placeholder="Special needs"
                            />
                          </div>
                          <div className="col-span-1 md:col-span-2 space-y-3 mt-2">
                            <div>
                              <Label className="text-xs text-muted-foreground mb-1 block">Photo (optional)</Label>
                              <div className="flex items-center gap-4">
                                {pet.imageData && (
                                  <img
                                    src={pet.imageData}
                                    alt={pet.name || 'Pet'}
                                    className="w-20 h-20 object-cover rounded-md border"
                                  />
                                )}
                                <Input
                                  type="file"
                                  accept="image/*"
                                  className="max-w-xs"
                                  onChange={async (e) => {
                                    const file = e.target.files?.[0];
                                    if (!file) return;
                                    if (!file.type.startsWith('image/')) {
                                      toast.error('Please choose an image file (e.g. JPEG, PNG).');
                                      return;
                                    }
                                    try {
                                      const reader = new FileReader();
                                      reader.onload = () => {
                                        const updated = planData.pet_plans.pets.map(p =>
                                          p.id === pet.id ? { ...p, imageData: reader.result } : p
                                        );
                                        setPlanData(prev => ({
                                          ...prev,
                                          pet_plans: { ...prev.pet_plans, pets: updated }
                                        }));
                                      };
                                      reader.readAsDataURL(file);
                                    } catch (_) {
                                      toast.error('Could not read the image.');
                                    }
                                    e.target.value = '';
                                  }}
                                />
                              </div>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                              <Input
                                value={pet.vetName || ''}
                                onChange={(e) => {
                                  const updated = planData.pet_plans.pets.map(p =>
                                    p.id === pet.id ? { ...p, vetName: e.target.value } : p
                                  );
                                  setPlanData(prev => ({
                                    ...prev,
                                    pet_plans: { ...prev.pet_plans, pets: updated }
                                  }));
                                }}
                                placeholder="Vet name (e.g. Dr. Smith)"
                              />
                              <Input
                                value={pet.vetPhone || ''}
                                onChange={(e) => {
                                  const updated = planData.pet_plans.pets.map(p =>
                                    p.id === pet.id ? { ...p, vetPhone: e.target.value } : p
                                  );
                                  setPlanData(prev => ({
                                    ...prev,
                                    pet_plans: { ...prev.pet_plans, pets: updated }
                                  }));
                                }}
                                placeholder="Vet phone (e.g. 555-0100)"
                              />
                            </div>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handlePrintPetIdCard(pet)}
                              className="mt-2"
                            >
                              <Printer className="h-4 w-4 mr-1" />
                              Print ID Card
                            </Button>
                          </div>
                        </Card>
                      ))}
                    </div>
                  )}
                </div>
                <div>
                  <Label htmlFor="pet-evacuation">Pet Evacuation Procedures</Label>
                  <Textarea
                    id="pet-evacuation"
                    value={planData.pet_plans.evacuation_procedures}
                    onChange={(e) => {
                      setPlanData(prev => ({
                        ...prev,
                        pet_plans: {
                          ...prev.pet_plans,
                          evacuation_procedures: e.target.value
                        }
                      }));
                    }}
                    placeholder="Describe how to evacuate with pets, where to take them, etc."
                    rows={4}
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="grab-and-go">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-primary" />
                  Departure Countdown &amp; Grab List
                </CardTitle>
                <CardDescription>
                  Plan what to do in the 30, 15, and 5 minutes before you leave. Add items by time tier so your binder has a clear first-look checklist.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <div className="flex flex-wrap justify-between items-center gap-2 mb-4">
                    <Label className="text-base font-semibold">Items by time (30 / 15 / 5 min) or general priority</Label>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const countdownItems = [
                            { name: 'Shut off main breaker (if safe)', tier: '30' },
                            { name: 'Turn off water at main (if leaving)', tier: '30' },
                            { name: 'Lock windows and doors', tier: '15' },
                            { name: 'Grab pet carrier and leash', tier: '15' },
                            { name: 'Grab go-bag and documents', tier: '5' },
                            { name: 'Final head count and leave', tier: '5' }
                          ];
                          const newItems = countdownItems.filter(c => 
                            !(planData.grab_and_go ?? []).some(i => i.name === c.name)
                          ).map((c, index) => ({
                            id: `grab_${Date.now()}_${index}`,
                            name: c.name,
                            priority: 'high',
                            tier: c.tier,
                            checked: false
                          }));
                          if (newItems.length === 0) {
                            toast.info('Countdown items already added');
                            return;
                          }
                          setPlanData(prev => ({
                            ...prev,
                            grab_and_go: [...(prev.grab_and_go ?? []), ...newItems]
                          }));
                          toast.success(`Added ${newItems.length} countdown items`);
                        }}
                        className="flex items-center gap-1"
                      >
                        <Bell className="h-3 w-3" />
                        Add Countdown Items
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const defaultItems = [
                            'Wallet/Purse', 'Keys', 'Phone & Charger', 'Medications (7-day supply)',
                            'Important Documents Folder', 'Cash', 'Credit Cards', 'Flashlight',
                            'First Aid Kit', 'Water Bottle', 'Non-perishable Snacks', 'Change of Clothes'
                          ];
                          const newItems = defaultItems.filter(item =>
                            !(planData.grab_and_go ?? []).some(i => i.name === item)
                          ).map((item, index) => ({
                            id: `grab_${Date.now()}_${index}`,
                            name: item,
                            priority: 'high',
                            checked: false
                          }));
                          setPlanData(prev => ({
                            ...prev,
                            grab_and_go: [...(prev.grab_and_go ?? []), ...newItems]
                          }));
                          toast.success(`Added ${newItems.length} common grab-and-go items`);
                        }}
                        className="flex items-center gap-1"
                      >
                        <Plus className="h-3 w-3" />
                        Add Common Items
                      </Button>
                    </div>
                  </div>
                  {(planData.grab_and_go?.length ?? 0) === 0 ? (
                    <p className="text-sm text-text-muted italic">No items yet. Use &quot;Add Countdown Items&quot; for a 30/15/5 min checklist, or &quot;Add Common Items&quot; for a general list.</p>
                  ) : (
                    <div className="space-y-2">
                      {[...(planData.grab_and_go ?? [])]
                        .sort((a, b) => {
                          const tierOrder = { '30': 1, '15': 2, '5': 3 };
                          const aT = tierOrder[a.tier] ?? 99;
                          const bT = tierOrder[b.tier] ?? 99;
                          if (aT !== bT) return aT - bT;
                          const priorityOrder = { high: 1, medium: 2, low: 3 };
                          return (priorityOrder[a.priority] || 99) - (priorityOrder[b.priority] || 99);
                        })
                        .map((item) => (
                          <div key={item.id} className="flex items-center gap-3 p-3 border rounded-lg hover:bg-surface-muted">
                            <Checkbox
                              checked={item.checked || false}
                              onCheckedChange={(checked) => {
                                const updated = (planData.grab_and_go ?? []).map(i =>
                                  i.id === item.id ? { ...i, checked: checked === true } : i
                                );
                                setPlanData(prev => ({
                                  ...prev,
                                  grab_and_go: updated
                                }));
                              }}
                            />
                            <div className="flex-1 flex flex-wrap items-center gap-2 sm:gap-3">
                              <Input
                                value={item.name || ''}
                                onChange={(e) => {
                                  const updated = (planData.grab_and_go ?? []).map(i =>
                                    i.id === item.id ? { ...i, name: e.target.value } : i
                                  );
                                  setPlanData(prev => ({
                                    ...prev,
                                    grab_and_go: updated
                                  }));
                                }}
                                placeholder="Item or task"
                                className={`flex-1 min-w-[140px] ${item.checked ? 'line-through text-text-muted' : ''}`}
                              />
                              <Select
                                value={item.tier || 'other'}
                                onValueChange={(value) => {
                                  const tier = value === 'other' ? undefined : value;
                                  const updated = (planData.grab_and_go ?? []).map(i =>
                                    i.id === item.id ? { ...i, tier } : i
                                  );
                                  setPlanData(prev => ({
                                    ...prev,
                                    grab_and_go: updated
                                  }));
                                }}
                              >
                                <SelectTrigger className="w-[160px]">
                                  <SelectValue placeholder="When" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="30">30 min before</SelectItem>
                                  <SelectItem value="15">15 min before</SelectItem>
                                  <SelectItem value="5">5 min before</SelectItem>
                                  <SelectItem value="other">Other (priority)</SelectItem>
                                </SelectContent>
                              </Select>
                              {(!item.tier || item.tier === 'other') && (
                                <Select
                                  value={item.priority || 'high'}
                                  onValueChange={(value) => {
                                    const updated = (planData.grab_and_go ?? []).map(i =>
                                      i.id === item.id ? { ...i, priority: value } : i
                                    );
                                    setPlanData(prev => ({
                                      ...prev,
                                      grab_and_go: updated
                                    }));
                                  }}
                                >
                                  <SelectTrigger className="w-28">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="high">High</SelectItem>
                                    <SelectItem value="medium">Medium</SelectItem>
                                    <SelectItem value="low">Low</SelectItem>
                                  </SelectContent>
                                </Select>
                              )}
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setPlanData(prev => ({
                                    ...prev,
                                    grab_and_go: (prev.grab_and_go ?? []).filter(i => i.id !== item.id)
                                  }));
                                }}
                                className="text-destructive shrink-0"
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        ))}
                    </div>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const newItem = {
                        id: `grab_${Date.now()}`,
                        name: '',
                        priority: 'high',
                        tier: '30',
                        checked: false
                      };
                      setPlanData(prev => ({
                        ...prev,
                        grab_and_go: [...(prev.grab_and_go ?? []), newItem]
                      }));
                    }}
                    className="mt-4"
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    Add Custom Item
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="vehicle">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Car className="h-5 w-5 text-primary" />
                  Vehicle Loading Plan
                </CardTitle>
                <CardDescription>
                  Add each vehicle or trailer, then define zones (e.g. trunk, back seat) and what goes in each. Your binder will show a loading checklist per vehicle.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const id = `vehicle_${Date.now()}`;
                      setPlanData(prev => ({
                        ...prev,
                        vehicle_loading_plan: {
                          vehicles: [...(prev.vehicle_loading_plan?.vehicles ?? []), { id, name: '', type: 'vehicle', zones: [] }]
                        }
                      }));
                      toast.success('Added vehicle');
                    }}
                    className="flex items-center gap-1"
                  >
                    <Plus className="h-3 w-3" />
                    Add vehicle
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const id = `vehicle_${Date.now()}`;
                      setPlanData(prev => ({
                        ...prev,
                        vehicle_loading_plan: {
                          vehicles: [...(prev.vehicle_loading_plan?.vehicles ?? []), { id, name: '', type: 'trailer', zones: [] }]
                        }
                      }));
                      toast.success('Added trailer');
                    }}
                    className="flex items-center gap-1"
                  >
                    <Plus className="h-3 w-3" />
                    Add trailer
                  </Button>
                </div>
                {(planData.vehicle_loading_plan?.vehicles?.length ?? 0) === 0 ? (
                  <p className="text-sm text-text-muted italic">No vehicles or trailers yet. Add a vehicle or trailer to define zones and loading lists.</p>
                ) : (
                  <div className="space-y-6">
                    {(planData.vehicle_loading_plan?.vehicles ?? []).map((vehicle) => (
                      <Card key={vehicle.id} className="p-4 border-primary/20">
                        <div className="flex flex-wrap items-center gap-2 mb-4">
                          <Input
                            value={vehicle.name || ''}
                            onChange={(e) => {
                              const updated = (planData.vehicle_loading_plan?.vehicles ?? []).map(v =>
                                v.id === vehicle.id ? { ...v, name: e.target.value } : v
                              );
                              setPlanData(prev => ({
                                ...prev,
                                vehicle_loading_plan: { ...prev.vehicle_loading_plan, vehicles: updated }
                              }));
                            }}
                            placeholder={vehicle.type === 'trailer' ? 'Trailer name' : 'Vehicle name'}
                            className="font-medium max-w-[200px]"
                          />
                          <Select
                            value={vehicle.type || 'vehicle'}
                            onValueChange={(value) => {
                              const updated = (planData.vehicle_loading_plan?.vehicles ?? []).map(v =>
                                v.id === vehicle.id ? { ...v, type: value } : v
                              );
                              setPlanData(prev => ({
                                ...prev,
                                vehicle_loading_plan: { ...prev.vehicle_loading_plan, vehicles: updated }
                              }));
                            }}
                          >
                            <SelectTrigger className="w-[120px]">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="vehicle">Vehicle</SelectItem>
                              <SelectItem value="trailer">Trailer</SelectItem>
                            </SelectContent>
                          </Select>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setPlanData(prev => ({
                                ...prev,
                                vehicle_loading_plan: {
                                  vehicles: (prev.vehicle_loading_plan?.vehicles ?? []).filter(v => v.id !== vehicle.id)
                                }
                              }));
                            }}
                            className="text-destructive shrink-0"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                        <div className="flex flex-wrap gap-2 mb-3">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const sampleZones = [
                                { id: `zone_${Date.now()}_0`, name: 'Trunk', items: [] },
                                { id: `zone_${Date.now()}_1`, name: 'Back seat', items: [] },
                                { id: `zone_${Date.now()}_2`, name: 'Front seat / cab', items: [] }
                              ];
                              const updated = (planData.vehicle_loading_plan?.vehicles ?? []).map(v =>
                                v.id === vehicle.id ? { ...v, zones: [...(v.zones ?? []), ...sampleZones] } : v
                              );
                              setPlanData(prev => ({
                                ...prev,
                                vehicle_loading_plan: { ...prev.vehicle_loading_plan, vehicles: updated }
                              }));
                              toast.success('Added sample zones');
                            }}
                          >
                            <Plus className="h-3 w-3 mr-1" />
                            Add sample zones
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const zoneId = `zone_${Date.now()}`;
                              const updated = (planData.vehicle_loading_plan?.vehicles ?? []).map(v =>
                                v.id === vehicle.id ? { ...v, zones: [...(v.zones ?? []), { id: zoneId, name: '', items: [] }] } : v
                              );
                              setPlanData(prev => ({
                                ...prev,
                                vehicle_loading_plan: { ...prev.vehicle_loading_plan, vehicles: updated }
                              }));
                            }}
                          >
                            <Plus className="h-3 w-3 mr-1" />
                            Add zone
                          </Button>
                        </div>
                        <div className="space-y-3 pl-2 border-l-2 border-border">
                          {(vehicle.zones ?? []).length === 0 ? (
                            <p className="text-sm text-text-muted italic">No zones for this {vehicle.type === 'trailer' ? 'trailer' : 'vehicle'}. Add sample zones or add a custom zone.</p>
                          ) : (
                            (vehicle.zones ?? []).map((zone) => (
                              <Card key={zone.id} className="p-3">
                                <div className="flex items-center gap-2 mb-2">
                                  <Input
                                    value={zone.name || ''}
                                    onChange={(e) => {
                                      const updated = (planData.vehicle_loading_plan?.vehicles ?? []).map(v => {
                                        if (v.id !== vehicle.id) return v;
                                        return {
                                          ...v,
                                          zones: (v.zones ?? []).map(z =>
                                            z.id === zone.id ? { ...z, name: e.target.value } : z
                                          )
                                        };
                                      });
                                      setPlanData(prev => ({
                                        ...prev,
                                        vehicle_loading_plan: { ...prev.vehicle_loading_plan, vehicles: updated }
                                      }));
                                    }}
                                    placeholder="Zone (e.g. Trunk, Back seat)"
                                    className="font-medium max-w-xs h-8 text-sm"
                                  />
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => {
                                      const updated = (planData.vehicle_loading_plan?.vehicles ?? []).map(v =>
                                        v.id === vehicle.id ? { ...v, zones: (v.zones ?? []).filter(z => z.id !== zone.id) } : v
                                      );
                                      setPlanData(prev => ({
                                        ...prev,
                                        vehicle_loading_plan: { ...prev.vehicle_loading_plan, vehicles: updated }
                                      }));
                                    }}
                                    className="text-destructive shrink-0 h-8 w-8 p-0"
                                  >
                                    <X className="h-3 w-3" />
                                  </Button>
                                </div>
                                <div className="space-y-2">
                                  {(zone.items ?? []).map((item) => (
                                    <div key={item.id} className="flex items-center gap-2">
                              <Input
                                value={item.name || ''}
                                onChange={(e) => {
                                  const updated = (planData.vehicle_loading_plan?.vehicles ?? []).map(v => {
                                    if (v.id !== vehicle.id) return v;
                                    return {
                                      ...v,
                                      zones: (v.zones ?? []).map(z =>
                                        z.id !== zone.id ? z : { ...z, items: (z.items ?? []).map(i => i.id === item.id ? { ...i, name: e.target.value } : i) }
                                      )
                                    };
                                  });
                                  setPlanData(prev => ({ ...prev, vehicle_loading_plan: { ...prev.vehicle_loading_plan, vehicles: updated } }));
                                }}
                                placeholder="Item"
                                className="flex-1 min-w-0 h-8 text-sm"
                              />
                              <Input
                                value={item.notes || ''}
                                onChange={(e) => {
                                  const updated = (planData.vehicle_loading_plan?.vehicles ?? []).map(v => {
                                    if (v.id !== vehicle.id) return v;
                                    return {
                                      ...v,
                                      zones: (v.zones ?? []).map(z =>
                                        z.id !== zone.id ? z : { ...z, items: (z.items ?? []).map(i => i.id === item.id ? { ...i, notes: e.target.value } : i) }
                                      )
                                    };
                                  });
                                  setPlanData(prev => ({ ...prev, vehicle_loading_plan: { ...prev.vehicle_loading_plan, vehicles: updated } }));
                                }}
                                placeholder="Notes"
                                className="flex-1 min-w-0 max-w-[140px] h-8 text-sm"
                              />
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  const updated = (planData.vehicle_loading_plan?.vehicles ?? []).map(v => {
                                    if (v.id !== vehicle.id) return v;
                                    return {
                                      ...v,
                                      zones: (v.zones ?? []).map(z =>
                                        z.id !== zone.id ? z : { ...z, items: (z.items ?? []).filter(i => i.id !== item.id) }
                                      )
                                    };
                                  });
                                  setPlanData(prev => ({ ...prev, vehicle_loading_plan: { ...prev.vehicle_loading_plan, vehicles: updated } }));
                                }}
                                className="text-destructive shrink-0 h-8 w-8 p-0"
                              >
                                <X className="h-3 w-3" />
                              </Button>
                                    </div>
                                  ))}
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="h-7 text-xs"
                                    onClick={() => {
                                      const newItem = { id: `item_${Date.now()}`, name: '', notes: '' };
                                      const updated = (planData.vehicle_loading_plan?.vehicles ?? []).map(v =>
                                        v.id === vehicle.id
                                          ? { ...v, zones: (v.zones ?? []).map(z => z.id === zone.id ? { ...z, items: [...(z.items ?? []), newItem] } : z) }
                                          : v
                                      );
                                      setPlanData(prev => ({ ...prev, vehicle_loading_plan: { ...prev.vehicle_loading_plan, vehicles: updated } }));
                                    }}
                                  >
                                    <Plus className="h-3 w-3 mr-1" />
                                    Add item
                                  </Button>
                                </div>
                              </Card>
                            ))
                          )}
                        </div>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="go-no-go">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ListChecks className="h-5 w-5 text-primary" />
                  Go / No-Go Criteria
                </CardTitle>
                <CardDescription>
                  Each rule is an <strong>if–then</strong>: <em>If</em> [condition occurs], <em>then</em> [we go or we stay]. Add conditions and the decision for each; set when to re-evaluate (condition list or period).
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <Label>Instructions (optional)</Label>
                  <Textarea
                    value={planData.go_no_go_criteria?.intro ?? ''}
                    onChange={(e) =>
                      setPlanData(prev => ({
                        ...prev,
                        go_no_go_criteria: { ...prev.go_no_go_criteria, intro: e.target.value }
                      }))
                    }
                    placeholder="e.g. When in doubt, follow official orders. Each rule below is read as: If [condition], then [go or stay]."
                    className="mt-1"
                    rows={2}
                  />
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const examples = [
                        { id: `criterion_${Date.now()}_0`, trigger: 'Mandatory evacuation ordered', when_met: 'go', notes: '', reevaluate_period: '30_min', also_reassess: 'Road conditions, family location' },
                        { id: `criterion_${Date.now()}_1`, trigger: 'Roads flooded or impassable', when_met: 'no_go', notes: '', reevaluate_period: '1_hr', also_reassess: 'Evacuation order, alternate routes' },
                        { id: `criterion_${Date.now()}_2`, trigger: 'Power out more than 24 hours in winter', when_met: 'go', notes: 'Consider going to family or shelter', reevaluate_period: '6_hr', also_reassess: 'Road conditions, shelter availability' }
                      ];
                      setPlanData(prev => ({
                        ...prev,
                        go_no_go_criteria: {
                          ...prev.go_no_go_criteria,
                          criteria: [...(prev.go_no_go_criteria?.criteria ?? []), ...examples]
                        }
                      }));
                      toast.success('Added example if–then rules');
                    }}
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    Add example if–then rules
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const id = `criterion_${Date.now()}`;
                      setPlanData(prev => ({
                        ...prev,
                        go_no_go_criteria: {
                          ...prev.go_no_go_criteria,
                          criteria: [...(prev.go_no_go_criteria?.criteria ?? []), { id, trigger: '', when_met: 'go', notes: '', reevaluate_period: '', also_reassess: '' }]
                        }
                      }));
                    }}
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    Add if–then rule
                  </Button>
                </div>
                {(planData.go_no_go_criteria?.criteria?.length ?? 0) === 0 ? (
                  <p className="text-sm text-text-muted italic">No if–then rules yet. Add rules in the form: If [condition], then [go or stay].</p>
                ) : (
                  <div className="space-y-3">
                    {(planData.go_no_go_criteria?.criteria ?? []).map((criterion) => (
                      <Card key={criterion.id} className="p-4">
                        <p className="text-xs font-medium text-text-muted mb-2">If–then rule</p>
                        <div className="flex flex-wrap items-start gap-2">
                          <span className="text-sm font-medium pt-2 shrink-0">If</span>
                          <Input
                            value={criterion.trigger || ''}
                            onChange={(e) => {
                              const updated = (planData.go_no_go_criteria?.criteria ?? []).map(c =>
                                c.id === criterion.id ? { ...c, trigger: e.target.value } : c
                              );
                              setPlanData(prev => ({
                                ...prev,
                                go_no_go_criteria: { ...prev.go_no_go_criteria, criteria: updated }
                              }));
                            }}
                            placeholder="condition occurs (e.g. Evacuation order is issued)"
                            className="flex-1 min-w-[200px]"
                          />
                          <span className="text-sm font-medium pt-2 shrink-0">then</span>
                          <Select
                            value={criterion.when_met || 'go'}
                            onValueChange={(value) => {
                              const updated = (planData.go_no_go_criteria?.criteria ?? []).map(c =>
                                c.id === criterion.id ? { ...c, when_met: value } : c
                              );
                              setPlanData(prev => ({
                                ...prev,
                                go_no_go_criteria: { ...prev.go_no_go_criteria, criteria: updated }
                              }));
                            }}
                          >
                            <SelectTrigger className="w-[140px]">
                              <SelectValue placeholder="decision" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="go">we go (leave)</SelectItem>
                              <SelectItem value="no_go">we stay (no-go)</SelectItem>
                            </SelectContent>
                          </Select>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setPlanData(prev => ({
                                ...prev,
                                go_no_go_criteria: {
                                  ...prev.go_no_go_criteria,
                                  criteria: (prev.go_no_go_criteria?.criteria ?? []).filter(c => c.id !== criterion.id)
                                }
                              }));
                            }}
                            className="text-destructive shrink-0"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                        <div className="mt-3 p-2 rounded-md bg-muted/50 border border-border">
                          <Label className="text-xs font-medium text-foreground">Re-evaluate if (condition list) or every (period)</Label>
                          <p className="text-xs text-text-muted mt-0.5 mb-2">Re-check this decision when any of these conditions occur, or after the time interval.</p>
                          <div className="flex flex-wrap items-center gap-2">
                            <Input
                              value={criterion.also_reassess || ''}
                              onChange={(e) => {
                                const updated = (planData.go_no_go_criteria?.criteria ?? []).map(c =>
                                  c.id === criterion.id ? { ...c, also_reassess: e.target.value } : c
                                );
                                setPlanData(prev => ({
                                  ...prev,
                                  go_no_go_criteria: { ...prev.go_no_go_criteria, criteria: updated }
                                }));
                              }}
                              placeholder="e.g. Roads clear, evacuation lifted, power restored"
                              className="flex-1 min-w-[180px] text-sm"
                            />
                            <span className="text-xs text-text-muted shrink-0">or every</span>
                            <Select
                              value={criterion.reevaluate_period || 'none'}
                              onValueChange={(value) => {
                                const updated = (planData.go_no_go_criteria?.criteria ?? []).map(c =>
                                  c.id === criterion.id ? { ...c, reevaluate_period: value === 'none' ? '' : value } : c
                                );
                                setPlanData(prev => ({
                                  ...prev,
                                  go_no_go_criteria: { ...prev.go_no_go_criteria, criteria: updated }
                                }));
                              }}
                            >
                              <SelectTrigger className="w-[130px]">
                                <SelectValue placeholder="period" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">No interval</SelectItem>
                                <SelectItem value="15_min">15 min</SelectItem>
                                <SelectItem value="30_min">30 min</SelectItem>
                                <SelectItem value="1_hr">1 hr</SelectItem>
                                <SelectItem value="2_hr">2 hr</SelectItem>
                                <SelectItem value="6_hr">6 hr</SelectItem>
                                <SelectItem value="12_hr">12 hr</SelectItem>
                                <SelectItem value="24_hr">24 hr</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <div className="mt-2">
                          <Input
                            value={criterion.notes || ''}
                            onChange={(e) => {
                              const updated = (planData.go_no_go_criteria?.criteria ?? []).map(c =>
                                c.id === criterion.id ? { ...c, notes: e.target.value } : c
                              );
                              setPlanData(prev => ({
                                ...prev,
                                go_no_go_criteria: { ...prev.go_no_go_criteria, criteria: updated }
                              }));
                            }}
                            placeholder="Other notes (optional)"
                            className="text-sm"
                          />
                        </div>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="first-hour">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="h-5 w-5 text-primary" />
                  First Hour Protocol
                </CardTitle>
                <CardDescription>
                  What to do in the first 60 minutes of an emergency. Add steps in order; use the template to start or write your own. Exports as a one-pager for your binder.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const templateSteps = [
                        'Ensure everyone is safe and accounted for.',
                        'Check for immediate hazards (gas, water, electrical) and shut off if needed.',
                        'If evacuating: grab go-bags and follow evacuation plan. If sheltering: move to safe area.',
                        'Contact your out-of-area contact if possible.',
                        'Follow your Go/No-Go and communication plan as you go.'
                      ];
                      const existing = planData.first_hour_protocol?.steps ?? [];
                      const toAdd = templateSteps.filter(t => !existing.some(s => (s.text || '').trim() === t.trim())).map((text, i) => ({ id: `step_${Date.now()}_${i}`, text }));
                      if (toAdd.length === 0) {
                        toast.info('Template steps already present');
                        return;
                      }
                      setPlanData(prev => ({
                        ...prev,
                        first_hour_protocol: { steps: [...(prev.first_hour_protocol?.steps ?? []), ...toAdd] }
                      }));
                      toast.success(`Added ${toAdd.length} template steps`);
                    }}
                    className="flex items-center gap-1"
                  >
                    <Wand2 className="h-3 w-3" />
                    Add from template
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const id = `step_${Date.now()}`;
                      setPlanData(prev => ({
                        ...prev,
                        first_hour_protocol: { steps: [...(prev.first_hour_protocol?.steps ?? []), { id, text: '' }] }
                      }));
                    }}
                    className="flex items-center gap-1"
                  >
                    <Plus className="h-3 w-3" />
                    Add step
                  </Button>
                </div>
                {(planData.first_hour_protocol?.steps?.length ?? 0) === 0 ? (
                  <p className="text-sm text-text-muted italic">No steps yet. Use &quot;Add from template&quot; for a starter list or &quot;Add step&quot; to write your own.</p>
                ) : (
                  <div className="space-y-2">
                    {(planData.first_hour_protocol?.steps ?? []).map((step, index) => (
                      <div key={step.id} className="flex items-start gap-2 p-3 border rounded-lg bg-card">
                        <span className="text-sm font-medium text-muted-foreground shrink-0 pt-2 w-6">{index + 1}.</span>
                        <Input
                          value={step.text || ''}
                          onChange={(e) => {
                            const steps = (planData.first_hour_protocol?.steps ?? []).map(s =>
                              s.id === step.id ? { ...s, text: e.target.value } : s
                            );
                            setPlanData(prev => ({ ...prev, first_hour_protocol: { steps } }));
                          }}
                          placeholder="e.g. Ensure everyone is safe and accounted for"
                          className="flex-1"
                        />
                        <div className="flex gap-1 shrink-0">
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={index === 0}
                            onClick={() => {
                              const steps = [...(planData.first_hour_protocol?.steps ?? [])];
                              if (index <= 0) return;
                              [steps[index - 1], steps[index]] = [steps[index], steps[index - 1]];
                              setPlanData(prev => ({ ...prev, first_hour_protocol: { steps } }));
                            }}
                            className="h-8 w-8 p-0"
                          >
                            <ChevronUp className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={index === (planData.first_hour_protocol?.steps?.length ?? 0) - 1}
                            onClick={() => {
                              const steps = [...(planData.first_hour_protocol?.steps ?? [])];
                              if (index >= steps.length - 1) return;
                              [steps[index], steps[index + 1]] = [steps[index + 1], steps[index]];
                              setPlanData(prev => ({ ...prev, first_hour_protocol: { steps } }));
                            }}
                            className="h-8 w-8 p-0"
                          >
                            <ChevronDown className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              const steps = (planData.first_hour_protocol?.steps ?? []).filter(s => s.id !== step.id);
                              setPlanData(prev => ({ ...prev, first_hour_protocol: { steps } }));
                            }}
                            className="text-destructive h-8 w-8 p-0"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="school-work">
            <div className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <GraduationCap className="h-5 w-5 text-primary" />
                    School Emergency Plans
                  </CardTitle>
                  <CardDescription>
                    Emergency procedures and pickup information for school locations
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <Label>School Locations</Label>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const newSchool = {
                            id: `school_${Date.now()}`,
                            name: '',
                            address: '',
                            phone: '',
                            pickup_procedures: '',
                            emergency_contacts: []
                          };
                          setPlanData(prev => ({
                            ...prev,
                            school_work_plans: {
                              ...prev.school_work_plans,
                              school_pickup: {
                                ...prev.school_work_plans.school_pickup,
                                [newSchool.id]: newSchool
                              }
                            }
                          }));
                        }}
                        className="flex items-center gap-1"
                      >
                        <Plus className="h-3 w-3" />
                        Add School
                      </Button>
                    </div>
                    {Object.keys(planData.school_work_plans.school_pickup).length === 0 ? (
                      <p className="text-sm text-text-muted italic">No schools added yet</p>
                    ) : (
                      <div className="space-y-3">
                        {Object.values(planData.school_work_plans.school_pickup).map((school) => (
                          <Card key={school.id} className="p-4">
                            <div className="flex justify-end mb-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  const updated = { ...planData.school_work_plans.school_pickup };
                                  delete updated[school.id];
                                  setPlanData(prev => ({
                                    ...prev,
                                    school_work_plans: {
                                      ...prev.school_work_plans,
                                      school_pickup: updated
                                    }
                                  }));
                                }}
                                className="h-6 w-6 p-0 text-destructive"
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                            <div className="space-y-3">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <Input
                                  value={school.name}
                                  onChange={(e) => {
                                    const updated = {
                                      ...planData.school_work_plans.school_pickup,
                                      [school.id]: { ...school, name: e.target.value }
                                    };
                                    setPlanData(prev => ({
                                      ...prev,
                                      school_work_plans: {
                                        ...prev.school_work_plans,
                                        school_pickup: updated
                                      }
                                    }));
                                  }}
                                  placeholder="School name"
                                />
                                <Input
                                  value={school.phone}
                                  onChange={(e) => {
                                    const updated = {
                                      ...planData.school_work_plans.school_pickup,
                                      [school.id]: { ...school, phone: e.target.value }
                                    };
                                    setPlanData(prev => ({
                                      ...prev,
                                      school_work_plans: {
                                        ...prev.school_work_plans,
                                        school_pickup: updated
                                      }
                                    }));
                                  }}
                                  placeholder="School phone"
                                />
                              </div>
                              <Input
                                value={school.address}
                                onChange={(e) => {
                                  const updated = {
                                    ...planData.school_work_plans.school_pickup,
                                    [school.id]: { ...school, address: e.target.value }
                                  };
                                  setPlanData(prev => ({
                                    ...prev,
                                    school_work_plans: {
                                      ...prev.school_work_plans,
                                      school_pickup: updated
                                    }
                                  }));
                                }}
                                placeholder="School address"
                              />
                              <Textarea
                                value={school.pickup_procedures}
                                onChange={(e) => {
                                  const updated = {
                                    ...planData.school_work_plans.school_pickup,
                                    [school.id]: { ...school, pickup_procedures: e.target.value }
                                  };
                                  setPlanData(prev => ({
                                    ...prev,
                                    school_work_plans: {
                                      ...prev.school_work_plans,
                                      school_pickup: updated
                                    }
                                  }));
                                }}
                                placeholder="Emergency pickup procedures and authorized pickup persons"
                                rows={3}
                              />
                            </div>
                          </Card>
                        ))}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <DollarSign className="h-5 w-5 text-primary" />
                    Work Emergency Plans
                  </CardTitle>
                  <CardDescription>
                    Emergency contacts and procedures for work locations
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <Label>Work Locations</Label>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const newWork = {
                            id: `work_${Date.now()}`,
                            company: '',
                            address: '',
                            phone: '',
                            supervisor: '',
                            emergency_procedures: ''
                          };
                          setPlanData(prev => ({
                            ...prev,
                            school_work_plans: {
                              ...prev.school_work_plans,
                              work_contacts: [...prev.school_work_plans.work_contacts, newWork]
                            }
                          }));
                        }}
                        className="flex items-center gap-1"
                      >
                        <Plus className="h-3 w-3" />
                        Add Work Location
                      </Button>
                    </div>
                    {planData.school_work_plans.work_contacts.length === 0 ? (
                      <p className="text-sm text-text-muted italic">No work locations added yet</p>
                    ) : (
                      <div className="space-y-3">
                        {planData.school_work_plans.work_contacts.map((work) => (
                          <Card key={work.id} className="p-4">
                            <div className="flex justify-end mb-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setPlanData(prev => ({
                                    ...prev,
                                    school_work_plans: {
                                      ...prev.school_work_plans,
                                      work_contacts: prev.school_work_plans.work_contacts.filter(w => w.id !== work.id)
                                    }
                                  }));
                                }}
                                className="h-6 w-6 p-0 text-destructive"
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                            <div className="space-y-3">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <Input
                                  value={work.company}
                                  onChange={(e) => {
                                    const updated = planData.school_work_plans.work_contacts.map(w =>
                                      w.id === work.id ? { ...w, company: e.target.value } : w
                                    );
                                    setPlanData(prev => ({
                                      ...prev,
                                      school_work_plans: {
                                        ...prev.school_work_plans,
                                        work_contacts: updated
                                      }
                                    }));
                                  }}
                                  placeholder="Company name"
                                />
                                <Input
                                  value={work.phone}
                                  onChange={(e) => {
                                    const updated = planData.school_work_plans.work_contacts.map(w =>
                                      w.id === work.id ? { ...w, phone: e.target.value } : w
                                    );
                                    setPlanData(prev => ({
                                      ...prev,
                                      school_work_plans: {
                                        ...prev.school_work_plans,
                                        work_contacts: updated
                                      }
                                    }));
                                  }}
                                  placeholder="Work phone"
                                />
                              </div>
                              <Input
                                value={work.address}
                                onChange={(e) => {
                                  const updated = planData.school_work_plans.work_contacts.map(w =>
                                    w.id === work.id ? { ...w, address: e.target.value } : w
                                  );
                                  setPlanData(prev => ({
                                    ...prev,
                                    school_work_plans: {
                                      ...prev.school_work_plans,
                                      work_contacts: updated
                                    }
                                  }));
                                }}
                                placeholder="Work address"
                              />
                              <Input
                                value={work.supervisor}
                                onChange={(e) => {
                                  const updated = planData.school_work_plans.work_contacts.map(w =>
                                    w.id === work.id ? { ...w, supervisor: e.target.value } : w
                                  );
                                  setPlanData(prev => ({
                                    ...prev,
                                    school_work_plans: {
                                      ...prev.school_work_plans,
                                      work_contacts: updated
                                    }
                                  }));
                                }}
                                placeholder="Supervisor name and contact"
                              />
                              <Textarea
                                value={work.emergency_procedures}
                                onChange={(e) => {
                                  const updated = planData.school_work_plans.work_contacts.map(w =>
                                    w.id === work.id ? { ...w, emergency_procedures: e.target.value } : w
                                  );
                                  setPlanData(prev => ({
                                    ...prev,
                                    school_work_plans: {
                                      ...prev.school_work_plans,
                                      work_contacts: updated
                                    }
                                  }));
                                }}
                                placeholder="Emergency procedures and evacuation plans"
                                rows={3}
                              />
                            </div>
                          </Card>
                        ))}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>

      {/* Import Modal */}
      <Dialog open={showImportModal} onOpenChange={setShowImportModal}>
        <DialogContent className="max-w-2xl bg-white" style={{ backgroundColor: 'white' }}>
          <DialogHeader className="bg-white">
            <DialogTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5 text-primary" />
              Import from Other Tools
            </DialogTitle>
            <DialogDescription>
              Import your data from other preparedness tools to auto-populate sections
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4 bg-white">
            {/* Emergency Contact App Import */}
            <Card className="bg-white">
              <CardHeader className="bg-white">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Phone className="h-5 w-5 text-primary" />
                  Emergency Contact App
                </CardTitle>
                <CardDescription>
                  Import your emergency contacts to populate the Emergency Contacts section
                </CardDescription>
              </CardHeader>
              <CardContent className="bg-white">
                <label className="cursor-pointer">
                  <Button
                    variant="outline"
                    type="button"
                    className="w-full flex items-center gap-2"
                    onClick={() => document.getElementById('import-contacts-modal').click()}
                  >
                    <Upload className="h-4 w-4" />
                    {planData.emergency_contacts.imported ? 'Re-import Contacts' : 'Import Contacts'}
                  </Button>
                  <input
                    id="import-contacts-modal"
                    type="file"
                    accept=".json"
                    onChange={(e) => {
                      handleImportEmergencyContacts(e);
                      setShowImportModal(false);
                    }}
                    className="hidden"
                  />
                </label>
                {planData.emergency_contacts.imported && (
                  <p className="text-sm text-primary mt-2 flex items-center gap-1">
                    <CheckCircle className="h-4 w-4" />
                    {planData.emergency_contacts.contacts.length} contacts imported
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Inventory Tool Import */}
            <Card className="bg-white">
              <CardHeader className="bg-white">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <FileText className="h-5 w-5 text-primary" />
                  Inventory Tool
                </CardTitle>
                <CardDescription>
                  Import your inventory kits to populate the Emergency Kits section
                </CardDescription>
              </CardHeader>
              <CardContent className="bg-white">
                <label className="cursor-pointer">
                  <Button
                    variant="outline"
                    type="button"
                    className="w-full flex items-center gap-2"
                    onClick={() => document.getElementById('import-inventory-modal').click()}
                  >
                    <Upload className="h-4 w-4" />
                    {planData.kits.imported ? 'Re-import Kits' : 'Import Kits'}
                  </Button>
                  <input
                    id="import-inventory-modal"
                    type="file"
                    accept=".json"
                    onChange={(e) => {
                      handleImportInventory(e);
                      setShowImportModal(false);
                    }}
                    className="hidden"
                  />
                </label>
                {planData.kits.imported && (
                  <p className="text-sm text-primary mt-2 flex items-center gap-1">
                    <CheckCircle className="h-4 w-4" />
                    {planData.kits.kits.length} kit(s) imported
                  </p>
                )}
              </CardContent>
            </Card>

            <div className="text-sm text-foreground/90 bg-blue-50 p-3 rounded" style={{ backgroundColor: '#eff6ff' }}>
              <strong>Import Tips:</strong>
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>Export your data from the other tools first</li>
                <li>Import files should be in JSON format</li>
                <li>You can re-import to update your data</li>
                <li>Imported items are marked with an "Imported" badge</li>
              </ul>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Map Editor Dialog */}
      <Dialog open={showMapEditor} onOpenChange={setShowMapEditor}>
        <DialogContent className="max-w-6xl h-[90vh] bg-white flex flex-col" style={{ backgroundColor: 'white', zIndex: 100 }}>
          <DialogHeader className="bg-white flex-shrink-0 relative z-[60]" style={{ zIndex: 60 }}>
            <DialogTitle>Evacuation Map Editor</DialogTitle>
            <DialogDescription>
              Create or edit your evacuation map. Upload a map image, draw routes, add markers, and label important locations.
            </DialogDescription>
          </DialogHeader>
          {showMapEditor && (
            <ErrorBoundary
              fallback={
                <div className="flex flex-col items-center justify-center h-full p-8">
                  <AlertTriangle className="h-12 w-12 text-destructive mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Error Loading Map Editor</h3>
                  <p className="text-gray-600 mb-4">The map editor encountered an error. Please try again.</p>
                  <Button onClick={() => { setShowMapEditor(false); setEditingPlanId(null); }} variant="outline">
                    Close
                  </Button>
                </div>
              }
            >
              <MapEditorComponent
                key={`map-editor-${editingPlanId || 'new'}-${showMapEditor}`}
                planId={editingPlanId}
                initialImage={editingPlanId ? (planData.evacuation_plans?.plans || []).find(p => p.id === editingPlanId)?.map_image : null}
                onSave={(imageData) => {
                  if (editingPlanId) {
                    const updated = (planData.evacuation_plans?.plans || []).map(p =>
                      p.id === editingPlanId ? { ...p, map_image: imageData } : p
                    );
                    setPlanData(prev => ({
                      ...prev,
                      evacuation_plans: { ...prev.evacuation_plans, plans: updated }
                    }));
                    toast.success('Map saved successfully');
                    setShowMapEditor(false);
                  }
                }}
                onClose={() => {
                  setShowMapEditor(false);
                  setEditingPlanId(null);
                }}
              />
            </ErrorBoundary>
          )}
        </DialogContent>
      </Dialog>
        </div></></ToolLayout>
    </ErrorBoundary>
  );
}

// Helper function to generate babysitter HTML document
function generateBabysitterHTMLDocument(content) {
  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Babysitter Quick Reference</title>
  <style>
    @page {
      size: letter; /* 8.5in x 11in */
      margin: 0.5in;
      width: 8.5in;
      height: 11in;
    }
    @media print {
      @page {
        size: letter;
        margin: 0.5in;
      }
      html, body {
        width: 8.5in;
        height: 11in;
        margin: 0;
        padding: 0;
      }
    }
    body {
      font-family: Arial, sans-serif;
      font-size: 11pt;
      line-height: 1.4;
      color: #000;
    }
    .reference-card {
      border: 2px solid #000;
      padding: 20px;
      max-width: 8.5in;
      margin: 0 auto;
    }
    .header {
      text-align: center;
      border-bottom: 3px solid #000;
      padding-bottom: 15px;
      margin-bottom: 20px;
    }
    .header h1 {
      font-size: 24pt;
      font-weight: bold;
      margin: 0;
      color: #dc2626;
    }
    .section {
      margin-bottom: 20px;
      page-break-inside: avoid;
    }
    .section-title {
      font-size: 14pt;
      font-weight: bold;
      background: #f3f4f6;
      padding: 8px;
      margin-bottom: 10px;
      border-left: 4px solid #8b5cf6;
    }
    .info-row {
      display: flex;
      margin-bottom: 8px;
      padding: 4px 0;
    }
    .info-label {
      font-weight: bold;
      min-width: 120px;
      margin-right: 10px;
    }
    .info-value {
      flex: 1;
    }
    .emergency-numbers {
      background: #fee2e2;
      border: 2px solid #dc2626;
      padding: 15px;
      margin: 15px 0;
      border-radius: 5px;
    }
    .emergency-numbers .section-title {
      background: #dc2626;
      color: white;
      margin: -15px -15px 15px -15px;
      padding: 10px 15px;
      border-left: none;
    }
    .family-member {
      border-bottom: 1px solid #e5e7eb;
      padding-bottom: 10px;
      margin-bottom: 10px;
    }
    .allergy-warning {
      background: #fef3c7;
      border-left: 4px solid #f59e0b;
      padding: 8px;
      margin: 5px 0;
      font-size: 10pt;
    }
    .medication-info {
      background: #e0e7ff;
      border-left: 4px solid #6366f1;
      padding: 8px;
      margin: 5px 0;
      font-size: 10pt;
    }
    @media print {
      .no-print {
        display: none;
      }
    }
  </style>
</head>
<body>
  ${content}
</body>
</html>`;
}

// Map Editor Component - Simplified version to prevent crashes
function MapEditorComponent({ planId, initialImage, onSave, onClose }) {
  console.log('MapEditorComponent rendering', { planId, hasInitialImage: !!initialImage });
  
  const [tool, setTool] = useState('marker'); // 'marker', 'route', or 'text'
  const [color, setColor] = useState('#FF0000'); // Red for routes
  const [markerColor, setMarkerColor] = useState('#3388ff'); // Blue for markers (default Leaflet blue)
  const [textColor, setTextColor] = useState('#000000'); // Black for text
  const [textContent, setTextContent] = useState(''); // Text to add
  const [showTextInput, setShowTextInput] = useState(false);
  const [pendingTextPosition, setPendingTextPosition] = useState(null);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  
  // Map-specific state
  const [mapCenter, setMapCenter] = useState([39.8283, -98.5795]); // Center of USA
  const [mapZoom, setMapZoom] = useState(10);
  const [mapMarkers, setMapMarkers] = useState([]);
  const [mapPolylines, setMapPolylines] = useState([]);
  const [mapTexts, setMapTexts] = useState([]);
  const mapRef = React.useRef(null);
  const mapInitializedRef = React.useRef(false);
  
  // Undo history - track actions for undo
  const [undoHistory, setUndoHistory] = useState([]);

  // Load saved map data when initialImage prop changes
  useEffect(() => {
    console.log('initialImage changed', initialImage);
    if (initialImage && !mapInitializedRef.current) {
      // Check if it's map data (JSON)
      if (initialImage.startsWith('data:application/json')) {
        try {
          const base64Data = initialImage.split(',')[1];
          const jsonString = atob(base64Data);
          const mapData = JSON.parse(jsonString);
          
          if (mapData.type === 'map') {
            setMapCenter(mapData.center || [39.8283, -98.5795]);
            setMapZoom(mapData.zoom || 10);
            setMapMarkers(mapData.markers || []);
            setMapPolylines(mapData.polylines || []);
            setMapTexts(mapData.texts || []);
            if (mapData.color) setColor(mapData.color);
            if (mapData.markerColor) setMarkerColor(mapData.markerColor);
            if (mapData.textColor) setTextColor(mapData.textColor);
            mapInitializedRef.current = true;
          }
        } catch (err) {
          console.error('Error parsing map data:', err);
        }
      }
    }
  }, [initialImage]);
  
  // Reset initialization flag when component unmounts or editor closes
  useEffect(() => {
    return () => {
      mapInitializedRef.current = false;
      // Don't call remove() - it can cause "container reused" errors
      // Just clear the ref and let React/Leaflet handle DOM cleanup
      if (mapRef.current) {
        mapRef.current = null;
      }
    };
  }, []);


  const handleSave = () => {
    // Save the map data as JSON
    const mapData = {
      type: 'map',
      center: mapCenter,
      zoom: mapZoom,
      markers: mapMarkers,
      polylines: mapPolylines,
      texts: mapTexts,
      color: color,
      markerColor: markerColor,
      textColor: textColor
    };
    // Convert to JSON string and then to data URL for consistency
    const jsonString = JSON.stringify(mapData);
    const dataUrl = 'data:application/json;base64,' + btoa(jsonString);
    onSave(dataUrl);
  };

  const handleClear = () => {
    if (window.confirm('Clear all markers and routes? This cannot be undone.')) {
      // Save to undo history before clearing
      setUndoHistory(prev => [...prev, {
        type: 'clear',
        markers: [...mapMarkers],
        polylines: [...mapPolylines],
        texts: [...mapTexts]
      }]);
      setMapMarkers([]);
      setMapPolylines([]);
      setMapTexts([]);
    }
  };

  const handleUndo = () => {
    if (undoHistory.length === 0) {
      toast.info('Nothing to undo');
      return;
    }

    const lastAction = undoHistory[undoHistory.length - 1];
    setUndoHistory(prev => prev.slice(0, -1));

    if (lastAction.type === 'marker') {
      // Remove the last marker
      setMapMarkers(prev => prev.filter(m => m.id !== lastAction.markerId));
      toast.success('Marker removed');
    } else if (lastAction.type === 'polyline') {
      // Remove the last polyline
      setMapPolylines(prev => prev.filter(p => p.id !== lastAction.polylineId));
      toast.success('Route removed');
    } else if (lastAction.type === 'text') {
      // Remove the last text
      setMapTexts(prev => prev.filter(t => t.id !== lastAction.textId));
      toast.success('Text removed');
    } else if (lastAction.type === 'clear') {
      // Restore all markers, polylines, and texts
      setMapMarkers(lastAction.markers);
      setMapPolylines(lastAction.polylines);
      setMapTexts(lastAction.texts || []);
      toast.success('Cleared items restored');
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;

    setIsSearching(true);
    setError(null);

    try {
      // Use Nominatim (OpenStreetMap) geocoding service
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchQuery)}&limit=1`,
        {
          headers: {
            'User-Agent': 'EmergencyPlanTool/1.0' // Required by Nominatim
          }
        }
      );

      if (!response.ok) {
        throw new Error('Search failed');
      }

      const data = await response.json();
      
      if (data && data.length > 0) {
        const result = data[0];
        const lat = parseFloat(result.lat);
        const lon = parseFloat(result.lon);
        
        // Update map center and zoom
        setMapCenter([lat, lon]);
        setMapZoom(15); // Zoom in to the location
        
        // If we have a map instance, pan to the location
        if (mapRef.current) {
          mapRef.current.setView([lat, lon], 15);
        }
        
        toast.success(`Found: ${result.display_name}`);
        setSearchQuery(''); // Clear search input
      } else {
        toast.error('Location not found. Please try a different search term.');
      }
    } catch (err) {
      console.error('Search error:', err);
      setError('Failed to search location. Please try again.');
      toast.error('Failed to search location');
    } finally {
      setIsSearching(false);
    }
  };

  if (error) {
    console.error('MapEditorComponent error state:', error);
    return (
      <div className="flex flex-col items-center justify-center h-full p-8 bg-white" style={{ backgroundColor: 'white' }}>
        <AlertTriangle className="h-12 w-12 text-destructive mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Error Loading Map Editor</h3>
        <p className="text-gray-600 mb-4">{error}</p>
        <p className="text-xs text-text-muted mb-4">Check browser console for details</p>
        <Button onClick={() => { setError(null); }} variant="outline" className="mr-2">
          Try Again
        </Button>
        <Button onClick={() => { onClose(); }} variant="outline">
          Close
        </Button>
      </div>
    );
  }
  
  return (
    <div className="flex flex-col h-full bg-white" style={{ backgroundColor: 'white' }}>
      {/* Toolbar */}
      <div className="flex items-center gap-2 p-4 border-b bg-white flex-shrink-0 relative z-50" style={{ backgroundColor: 'white', zIndex: 50 }}>
        {/* Search Bar */}
        <form onSubmit={handleSearch} className="flex items-center gap-2 flex-1 max-w-md">
          <div className="relative flex-1">
            <Input
              type="text"
              placeholder="Search for a location..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pr-10"
            />
            <Button
              type="submit"
              size="sm"
              variant="ghost"
              className="absolute right-0 top-0 h-full px-3"
              disabled={isSearching || !searchQuery.trim()}
            >
              {isSearching ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
              ) : (
                <MapPin className="h-4 w-4 text-text-muted" />
              )}
            </Button>
          </div>
        </form>
        
        <div className="h-6 w-px bg-gray-300 mx-2" />
        
        <Button
          variant={tool === 'marker' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setTool('marker')}
          className={tool === 'marker' ? 'bg-primary hover:opacity-90 text-primary-foreground' : ''}
        >
          <MapPin className="h-4 w-4 mr-2" />
          Add Marker
        </Button>
        <Button
          variant={tool === 'route' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setTool('route')}
          className={tool === 'route' ? 'bg-primary hover:opacity-90 text-primary-foreground' : ''}
        >
          <PenTool className="h-4 w-4 mr-2" />
          Draw Route
        </Button>
        <Button
          variant={tool === 'text' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setTool('text')}
          className={tool === 'text' ? 'bg-primary hover:opacity-90 text-primary-foreground' : ''}
        >
          <Type className="h-4 w-4 mr-2" />
          Add Text
        </Button>
        {tool === 'route' && (
          <div className="flex items-center gap-2 ml-4">
            <Label>Route Color:</Label>
            <Input
              type="color"
              value={color}
              onChange={(e) => setColor(e.target.value)}
              className="w-12 h-8"
            />
          </div>
        )}
        {tool === 'marker' && (
          <div className="flex items-center gap-2 ml-4">
            <Label>Marker Color:</Label>
            <Input
              type="color"
              value={markerColor}
              onChange={(e) => setMarkerColor(e.target.value)}
              className="w-12 h-8"
            />
          </div>
        )}
        {tool === 'text' && (
          <div className="flex items-center gap-2 ml-4">
            <Label>Text Color:</Label>
            <Input
              type="color"
              value={textColor}
              onChange={(e) => setTextColor(e.target.value)}
              className="w-12 h-8"
            />
          </div>
        )}
        <div className="flex-1" />
        <Button 
          variant="outline" 
          size="sm" 
          onClick={handleUndo}
          disabled={undoHistory.length === 0}
          title={undoHistory.length === 0 ? 'Nothing to undo' : 'Undo last action'}
        >
          <Undo2 className="h-4 w-4 mr-2" />
          Undo
        </Button>
        <Button variant="outline" size="sm" onClick={handleClear}>
          <Trash2 className="h-4 w-4 mr-2" />
          Clear All
        </Button>
      </div>

      {/* Map Area */}
      <div 
        id={`map-area-${planId || 'new'}`}
        className="flex-1 overflow-hidden bg-gray-100 min-h-0 relative" 
        style={{ 
          zIndex: 1, 
          isolation: 'isolate', 
          position: 'relative', 
          contain: 'layout style paint', 
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <MapViewComponent
          key={`map-editor-${planId || 'new'}`}
          center={mapCenter}
          zoom={mapZoom}
          markers={mapMarkers}
          polylines={mapPolylines}
          texts={mapTexts}
          tool={tool}
          color={color}
          textColor={textColor}
          mapRef={mapRef}
          onMarkerAdd={(position) => {
            const newMarker = { id: Date.now(), position, color: markerColor };
            setMapMarkers(prev => {
              const updated = [...prev, newMarker];
              // Add to undo history
              setUndoHistory(prevHistory => [...prevHistory, {
                type: 'marker',
                markerId: newMarker.id
              }]);
              return updated;
            });
          }}
          onPolylineAdd={(positions) => {
            const newPolyline = { id: Date.now(), positions, color };
            setMapPolylines(prev => {
              const updated = [...prev, newPolyline];
              // Add to undo history
              setUndoHistory(prevHistory => [...prevHistory, {
                type: 'polyline',
                polylineId: newPolyline.id
              }]);
              return updated;
            });
          }}
          onTextAdd={(position) => {
            // Show the text input dialog
            setPendingTextPosition(position);
            setShowTextInput(true);
          }}
          onMapMove={(center, zoom) => {
            setMapCenter(center);
            setMapZoom(zoom);
          }}
        />
      </div>

      {/* Text Input Dialog */}
      {showTextInput && pendingTextPosition && (
        <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[2000]">
          <div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Add Text</h3>
            <Input
              type="text"
              placeholder="Enter text to add to map"
              value={textContent}
              onChange={(e) => setTextContent(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && textContent.trim()) {
                  const newText = { id: Date.now(), position: pendingTextPosition, text: textContent.trim(), color: textColor };
                  setMapTexts(prev => {
                    const updated = [...prev, newText];
                    setUndoHistory(prevHistory => [...prevHistory, {
                      type: 'text',
                      textId: newText.id
                    }]);
                    return updated;
                  });
                  setTextContent('');
                  setShowTextInput(false);
                  setPendingTextPosition(null);
                } else if (e.key === 'Escape') {
                  setTextContent('');
                  setShowTextInput(false);
                  setPendingTextPosition(null);
                }
              }}
              autoFocus
              className="mb-4"
            />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => {
                setTextContent('');
                setShowTextInput(false);
                setPendingTextPosition(null);
              }}>
                Cancel
              </Button>
              <Button onClick={() => {
                if (textContent.trim()) {
                  const newText = { id: Date.now(), position: pendingTextPosition, text: textContent.trim(), color: textColor };
                  setMapTexts(prev => {
                    const updated = [...prev, newText];
                    setUndoHistory(prevHistory => [...prevHistory, {
                      type: 'text',
                      textId: newText.id
                    }]);
                    return updated;
                  });
                  setTextContent('');
                  setShowTextInput(false);
                  setPendingTextPosition(null);
                }
              }}>
                Add
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="flex justify-end gap-2 p-4 border-t bg-white flex-shrink-0" style={{ backgroundColor: 'white' }}>
        <Button variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button className="bg-primary hover:opacity-90 text-primary-foreground" onClick={handleSave}>
          <Save className="h-4 w-4 mr-2" />
          Save Map
        </Button>
      </div>
    </div>
  );
}

// Component to update map view when center/zoom changes
function MapUpdater({ center, zoom, mapRef }) {
  const map = useMap();
  
  React.useEffect(() => {
    if (map && center && zoom) {
      map.setView(center, zoom);
      if (mapRef) {
        mapRef.current = map;
      }
    }
  }, [center, zoom, map, mapRef]);
  
  return null;
}

// Helper function to create a colored marker icon
function createColoredMarkerIcon(color) {
  // Create an SVG marker icon with the specified color
  const svgIcon = `
    <svg width="25" height="41" viewBox="0 0 25 41" xmlns="http://www.w3.org/2000/svg">
      <path fill="${color}" stroke="#fff" stroke-width="1.5" d="M12.5 0C5.6 0 0 5.6 0 12.5c0 8.7 12.5 28.5 12.5 28.5S25 21.2 25 12.5C25 5.6 19.4 0 12.5 0z"/>
      <circle fill="#fff" cx="12.5" cy="12.5" r="4"/>
    </svg>
  `;
  
  return L.divIcon({
    html: svgIcon,
    className: 'custom-marker-icon',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34]
  });
}

// Helper function to create a text icon
function createTextIcon(text, color) {
  // Escape HTML to prevent XSS
  const escapeHtml = (str) => {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  };
  
  const escapedText = escapeHtml(text);
  
  return L.divIcon({
    html: `<div style="
      background-color: rgba(255, 255, 255, 0.9);
      border: 2px solid ${color};
      border-radius: 4px;
      padding: 4px 8px;
      color: ${color};
      font-weight: bold;
      font-size: 14px;
      white-space: nowrap;
      box-shadow: 0 2px 4px rgba(0,0,0,0.3);
      pointer-events: none;
      user-select: none;
    ">${escapedText}</div>`,
    className: 'custom-text-icon',
    iconSize: [null, null],
    iconAnchor: [0, 0]
  });
}

// Map View Component using Leaflet
function MapViewComponent({ center, zoom, markers, polylines, texts, tool, color, textColor, mapRef: externalMapRef, onMarkerAdd, onPolylineAdd, onTextAdd, onMapMove }) {
  const [currentPolyline, setCurrentPolyline] = useState(null);
  const internalMapRef = React.useRef(null);
  const mapInstanceRef = React.useRef(null);
  const containerRef = React.useRef(null);
  
  // Use external ref if provided, otherwise use internal ref
  const mapRef = externalMapRef || internalMapRef;

  // Cleanup map instance on unmount
  // Instead of calling remove(), we just clear the ref and let React/Leaflet handle DOM cleanup
  React.useEffect(() => {
    return () => {
      // Don't call remove() - it can cause "container reused" errors
      // Just clear the ref and let the DOM removal handle cleanup
      if (mapInstanceRef.current) {
        mapInstanceRef.current = null;
      }
    };
  }, []);
  
  // Remove duplicate maps - simplified approach
  React.useEffect(() => {
    const checkDuplicates = () => {
      // Check within our container
      if (containerRef.current) {
        const containerMaps = containerRef.current.querySelectorAll('.leaflet-container');
        if (containerMaps.length > 1) {
          // Keep only the last one
          const mapsArray = Array.from(containerMaps);
          for (let i = 0; i < mapsArray.length - 1; i++) {
            const mapEl = mapsArray[i];
            try {
              // Try to safely remove Leaflet instance
              if (mapEl._leaflet_id && window.L && window.L.Map) {
                try {
                  const mapInstance = mapEl._leaflet;
                  if (mapInstance && typeof mapInstance.remove === 'function') {
                    const container = mapInstance.getContainer?.();
                    // Only remove if container is still associated with this instance
                    if (container === mapEl && container._leaflet_id === mapEl._leaflet_id) {
                      try {
                        mapInstance.remove();
                      } catch (e) {
                        // If remove fails, container was reused - just remove DOM
                        if (mapEl.parentNode) {
                          mapEl.parentNode.removeChild(mapEl);
                        }
                      }
                    } else {
                      // Container was reused, just remove DOM
                      if (mapEl.parentNode) {
                        mapEl.parentNode.removeChild(mapEl);
                      }
                    }
                  }
                } catch (e) {
                  // Ignore - just remove DOM
                  if (mapEl.parentNode) {
                    mapEl.parentNode.removeChild(mapEl);
                  }
                }
              }
              // Remove DOM element
              if (mapEl.parentNode) {
                mapEl.parentNode.removeChild(mapEl);
              } else {
                mapEl.remove();
              }
            } catch (e) {
              try {
                mapEl.remove();
              } catch (e2) {}
            }
          }
        }
      }
      
      // Also check dialog for duplicates
      const dialogContent = document.querySelector('[class*="DialogContent"]');
      if (dialogContent) {
        const allMapsInDialog = dialogContent.querySelectorAll('.leaflet-container');
        if (allMapsInDialog.length > 1) {
          const mapsArray = Array.from(allMapsInDialog);
          for (let i = 0; i < mapsArray.length - 1; i++) {
            const mapEl = mapsArray[i];
            try {
              if (mapEl._leaflet_id && window.L && window.L.Map) {
                try {
                  const mapInstance = mapEl._leaflet;
                  if (mapInstance && typeof mapInstance.remove === 'function') {
                    const container = mapInstance.getContainer?.();
                    // Only remove if container is still associated with this instance
                    if (container === mapEl && container._leaflet_id === mapEl._leaflet_id) {
                      mapInstance.remove();
                    } else {
                      // Container was reused, just remove DOM
                      if (mapEl.parentNode) {
                        mapEl.parentNode.removeChild(mapEl);
                      }
                    }
                  } else {
                    // No valid instance, just remove DOM
                    if (mapEl.parentNode) {
                      mapEl.parentNode.removeChild(mapEl);
                    }
                  }
                } catch (e) {
                  // Ignore - just remove DOM
                  if (mapEl.parentNode) {
                    mapEl.parentNode.removeChild(mapEl);
                  }
                }
              } else {
                // No Leaflet instance, just remove DOM
                if (mapEl.parentNode) {
                  mapEl.parentNode.removeChild(mapEl);
                } else {
                  mapEl.remove();
                }
              }
            } catch (e) {
              // Force remove DOM only
              try {
                if (mapEl.parentNode) {
                  mapEl.parentNode.removeChild(mapEl);
                } else {
                  mapEl.remove();
                }
              } catch (e2) {}
            }
          }
        }
      }
    };
    
    // Check for duplicates after render
    const timer1 = setTimeout(checkDuplicates, 100);
    const timer2 = setTimeout(checkDuplicates, 500);
    
    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
    };
  }, [center, zoom, markers.length, polylines.length]);

  // Handle map clicks based on tool
  function MapClickHandler() {
    useMapEvents({
      click: (e) => {
        if (tool === 'marker') {
          onMarkerAdd([e.latlng.lat, e.latlng.lng]);
        } else if (tool === 'route') {
          if (!currentPolyline) {
            setCurrentPolyline({ positions: [[e.latlng.lat, e.latlng.lng]] });
          } else {
            const newPositions = [...currentPolyline.positions, [e.latlng.lat, e.latlng.lng]];
            setCurrentPolyline({ positions: newPositions });
          }
        } else if (tool === 'text') {
          // For text, trigger the callback which will show the dialog in parent
          if (onTextAdd) {
            onTextAdd([e.latlng.lat, e.latlng.lng]);
          }
        }
      },
      moveend: (e) => {
        const map = e.target;
        onMapMove([map.getCenter().lat, map.getCenter().lng], map.getZoom());
      }
    });
    return null;
  }

  const handleFinishRoute = () => {
    if (currentPolyline && currentPolyline.positions.length > 1) {
      onPolylineAdd(currentPolyline.positions);
      setCurrentPolyline(null);
    }
  };

  const handleCancelRoute = () => {
    setCurrentPolyline(null);
  };

  // Add CSS to hide duplicate maps as a fallback
  React.useEffect(() => {
    const styleId = 'map-editor-duplicate-hide';
    let styleEl = document.getElementById(styleId);
    if (!styleEl) {
      styleEl = document.createElement('style');
      styleEl.id = styleId;
      document.head.appendChild(styleEl);
    }
    
    styleEl.textContent = `
      .map-container-wrapper .leaflet-container:not(:last-of-type),
      [class*="DialogContent"] .map-container-wrapper .leaflet-container:not(:last-of-type) {
        display: none !important;
        visibility: hidden !important;
        opacity: 0 !important;
        pointer-events: none !important;
      }
    `;
    
    return () => {
      // Keep style for reuse
    };
  }, []);

  // Add CSS to hide duplicate maps as a fallback - this ensures only the last map is visible
  React.useEffect(() => {
    const styleId = 'map-editor-duplicate-hide';
    let styleEl = document.getElementById(styleId);
    if (!styleEl) {
      styleEl = document.createElement('style');
      styleEl.id = styleId;
      document.head.appendChild(styleEl);
    }
    
    // Update style content
    styleEl.textContent = `
      /* Hide all but the last leaflet container in map editor */
      .map-container-wrapper .leaflet-container:not(:last-of-type),
      .map-container-wrapper > div > .leaflet-container:not(:last-of-type) {
        display: none !important;
        visibility: hidden !important;
        opacity: 0 !important;
        pointer-events: none !important;
        height: 0 !important;
        width: 0 !important;
        position: absolute !important;
        left: -9999px !important;
      }
      /* Also hide duplicates in dialog content */
      [class*="DialogContent"] .map-container-wrapper .leaflet-container:not(:last-of-type),
      [class*="DialogContent"] .map-editor-container:not(:last-of-type) {
        display: none !important;
        visibility: hidden !important;
        opacity: 0 !important;
        pointer-events: none !important;
      }
    `;
    
    return () => {
      // Don't remove the style on unmount - it might be needed for other instances
      // The style will be reused if the component remounts
    };
  }, []);

  // Add CSS to hide duplicate maps as a fallback - this ensures only the last map is visible
  React.useEffect(() => {
    const styleId = 'map-editor-duplicate-hide';
    let styleEl = document.getElementById(styleId);
    if (!styleEl) {
      styleEl = document.createElement('style');
      styleEl.id = styleId;
      document.head.appendChild(styleEl);
    }
    
    // Update style content
    styleEl.textContent = `
      /* Hide all but the last leaflet container in map editor */
      .map-container-wrapper .leaflet-container:not(:last-of-type),
      .map-container-wrapper > div > .leaflet-container:not(:last-of-type) {
        display: none !important;
        visibility: hidden !important;
        opacity: 0 !important;
        pointer-events: none !important;
        height: 0 !important;
        width: 0 !important;
        position: absolute !important;
        left: -9999px !important;
      }
      /* Also hide duplicates in dialog content */
      [class*="DialogContent"] .map-container-wrapper .leaflet-container:not(:last-of-type),
      [class*="DialogContent"] .map-editor-container:not(:last-of-type) {
        display: none !important;
        visibility: hidden !important;
        opacity: 0 !important;
        pointer-events: none !important;
      }
    `;
    
    return () => {
      // Don't remove the style on unmount - it might be needed for other instances
      // The style will be reused if the component remounts
    };
  }, []);

  return (
    <div ref={containerRef} className="relative w-full h-full map-container-wrapper" style={{ zIndex: 1, position: 'relative', isolation: 'isolate', overflow: 'hidden' }}>
      <MapContainer
        key="map-editor-main-single"
        center={center}
        zoom={zoom}
        className="map-editor-container"
        style={{ height: '100%', width: '100%', zIndex: 1, position: 'relative', display: 'block' }}
        whenCreated={(mapInstance) => {
          const currentContainer = mapInstance.getContainer();
          
          // Immediately remove any other map instances in the container
          if (containerRef.current) {
            const existingMaps = containerRef.current.querySelectorAll('.leaflet-container');
            existingMaps.forEach((mapEl) => {
              if (mapEl !== currentContainer) {
                try {
                  // Try to safely remove the Leaflet instance
                  if (mapEl._leaflet_id) {
                    const mapInstanceToRemove = mapEl._leaflet;
                    if (mapInstanceToRemove && typeof mapInstanceToRemove.remove === 'function') {
                      const container = mapInstanceToRemove.getContainer?.();
                      // Only remove if container is still associated with this instance
                      if (container === mapEl && container._leaflet_id === mapEl._leaflet_id) {
                        try {
                          mapInstanceToRemove.remove();
                        } catch (e) {
                          // If remove fails, just remove DOM
                          if (mapEl.parentNode) {
                            mapEl.parentNode.removeChild(mapEl);
                          }
                        }
                      } else {
                        // Container was reused, just remove DOM
                        if (mapEl.parentNode) {
                          mapEl.parentNode.removeChild(mapEl);
                        }
                      }
                    } else {
                      // No valid instance, just remove DOM
                      if (mapEl.parentNode) {
                        mapEl.parentNode.removeChild(mapEl);
                      }
                    }
                  } else {
                    // No Leaflet instance, just remove DOM
                    if (mapEl.parentNode) {
                      mapEl.parentNode.removeChild(mapEl);
                    }
                  }
                } catch (e) {
                  // Force remove DOM only
                  try {
                    if (mapEl.parentNode) {
                      mapEl.parentNode.removeChild(mapEl);
                    } else {
                      mapEl.remove();
                    }
                  } catch (e2) {
                    // Ignore
                  }
                }
              }
            });
          }
          
          // Clean up any existing map instance in ref
          if (mapInstanceRef.current && mapInstanceRef.current !== mapInstance) {
            try {
              // Check if the old instance is still valid before removing
              const oldContainer = mapInstanceRef.current.getContainer?.();
              if (oldContainer && oldContainer.parentNode) {
                // Verify the container is still associated with this instance
                if (oldContainer._leaflet_id === mapInstanceRef.current._leaflet_id) {
                  try {
                    mapInstanceRef.current.remove();
                  } catch (e) {
                    // If remove fails, container was likely reused
                    // Just clear the ref
                    mapInstanceRef.current = null;
                  }
                } else {
                  // Container was reused, just clear the ref
                  mapInstanceRef.current = null;
                }
              } else {
                // Container no longer exists, just clear the ref
                mapInstanceRef.current = null;
              }
            } catch (e) {
              // Map already removed or container reused - just clear ref
              mapInstanceRef.current = null;
            }
          }
          
          mapInstanceRef.current = mapInstance;
          
          if (mapRef) {
            mapRef.current = mapInstance;
          }
          
          // Constrain all Leaflet panes to have z-index within reasonable range (1-20)
          // This prevents them from overlaying modal controls (which are z-50+)
          const mapContainer = mapInstance.getContainer();
          if (mapContainer) {
            // Set container z-index
            mapContainer.style.zIndex = '1';
            mapContainer.style.position = 'relative';
            
            // Constrain all panes
            const panes = mapContainer.querySelectorAll('.leaflet-pane');
            panes.forEach((pane) => {
              const currentZ = parseInt(window.getComputedStyle(pane).zIndex) || 0;
              if (currentZ > 20) {
                pane.style.zIndex = '20';
              }
            });
            
            // Constrain controls
            const controls = mapContainer.querySelectorAll('.leaflet-control-container');
            controls.forEach((control) => {
              control.style.zIndex = '10';
            });
          }
        }}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <MapUpdater center={center} zoom={zoom} mapRef={mapRef} />
        <MapClickHandler />
        {markers.map((marker) => (
          <Marker
            key={marker.id}
            position={marker.position}
            icon={createColoredMarkerIcon(marker.color || '#3388ff')}
          >
            <Popup>Marker {marker.id}</Popup>
          </Marker>
        ))}
        {polylines.map((polyline) => (
          <Polyline
            key={polyline.id}
            positions={polyline.positions}
            color={polyline.color || color}
            weight={5}
            opacity={0.7}
          />
        ))}
        {texts && texts.map((textItem) => (
          <Marker
            key={textItem.id}
            position={textItem.position}
            icon={createTextIcon(textItem.text, textItem.color || textColor || '#000000')}
          />
        ))}
        {currentPolyline && currentPolyline.positions.length > 0 && (
          <Polyline
            positions={currentPolyline.positions}
            color={color}
            weight={5}
            opacity={0.7}
            dashArray="10, 5"
          />
        )}
      </MapContainer>
      {tool === 'route' && currentPolyline && (
        <div className="absolute top-4 left-1/2 transform -translate-x-1/2 z-[1000] bg-white p-3 rounded-lg shadow-lg border">
          <p className="text-sm mb-2">Drawing route... Click to add points</p>
          <div className="flex gap-2">
            <Button size="sm" onClick={handleFinishRoute} className="bg-green-600 hover:bg-green-700 text-white">
              Finish Route
            </Button>
            <Button size="sm" variant="outline" onClick={handleCancelRoute}>
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// Map Preview Component - Renders saved map data as a preview
function MapPreview({ mapData }) {
  const [mapPreviewData, setMapPreviewData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!mapData) return;

    try {
      // Check if it's map data (JSON) or an image
      if (mapData.startsWith('data:application/json')) {
        const base64Data = mapData.split(',')[1];
        const jsonString = atob(base64Data);
        const parsedData = JSON.parse(jsonString);
        
        if (parsedData.type === 'map') {
          setMapPreviewData(parsedData);
        } else {
          setError('Invalid map data format');
        }
      } else if (mapData.startsWith('data:image')) {
        // It's an image (old format) - show as image
        return;
      } else {
        setError('Unknown map data format');
      }
    } catch (err) {
      console.error('Error parsing map preview data:', err);
      setError('Failed to load map preview');
    }
  }, [mapData]);

  // If it's an image (old format), render as image
  if (mapData && mapData.startsWith('data:image')) {
    return (
      <div className="w-full border rounded-lg overflow-hidden bg-gray-50">
        <img 
          src={mapData} 
          alt="Evacuation map"
          className="w-full max-h-64 object-contain"
        />
      </div>
    );
  }

  // If there's an error or no data, show error state
  if (error || !mapPreviewData) {
    return (
      <div className="w-full border rounded-lg p-4 bg-gray-50 flex items-center justify-center min-h-[200px]">
        <div className="text-center text-text-muted">
          <MapPin className="h-8 w-8 mx-auto mb-2 text-text-muted" />
          <p className="text-sm">{error || 'Map preview unavailable'}</p>
        </div>
      </div>
    );
  }

  // Render map preview using Leaflet
  // Match the height of the edit map (approximately 75vh to account for header/footer)
  return (
    <div className="w-full border rounded-lg overflow-hidden bg-gray-50" style={{ height: '75vh', minHeight: '500px', zIndex: 0, position: 'relative' }}>
      <MapContainer
        center={mapPreviewData.center || [39.8283, -98.5795]}
        zoom={mapPreviewData.zoom || 10}
        style={{ height: '100%', width: '100%', zIndex: 0, position: 'relative' }}
        zoomControl={true}
        dragging={true}
        scrollWheelZoom={false}
        doubleClickZoom={false}
        whenCreated={(mapInstance) => {
          // Constrain all Leaflet panes to have very low z-index for preview
          const mapContainer = mapInstance.getContainer();
          if (mapContainer) {
            // Set container z-index to 0
            mapContainer.style.zIndex = '0';
            mapContainer.style.position = 'relative';
            
            // Constrain all panes to very low z-index
            const panes = mapContainer.querySelectorAll('.leaflet-pane');
            panes.forEach((pane) => {
              pane.style.zIndex = '1';
            });
            
            // Constrain controls to low z-index
            const controls = mapContainer.querySelectorAll('.leaflet-control-container');
            controls.forEach((control) => {
              control.style.zIndex = '2';
            });
          }
        }}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {mapPreviewData.markers && mapPreviewData.markers.map((marker) => (
          <Marker
            key={marker.id}
            position={marker.position}
            icon={createColoredMarkerIcon(marker.color || '#3388ff')}
          >
            <Popup>Marker</Popup>
          </Marker>
        ))}
        {mapPreviewData.polylines && mapPreviewData.polylines.map((polyline) => (
          <Polyline
            key={polyline.id}
            positions={polyline.positions}
            color={polyline.color || mapPreviewData.color || '#FF0000'}
            weight={5}
            opacity={0.7}
          />
        ))}
        {mapPreviewData.texts && mapPreviewData.texts.map((textItem) => (
          <Marker
            key={textItem.id}
            position={textItem.position}
            icon={createTextIcon(textItem.text, textItem.color || mapPreviewData.textColor || '#000000')}
          />
        ))}
      </MapContainer>
    </div>
  );
}

export default EmergencyPlanTool;
